import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { createPortal } from "react-dom";
import Modal from "react-bootstrap/Modal";
import Offcanvas from "react-bootstrap/Offcanvas";
import {
  applyBookingThemeToRoot,
  normalizeBookingTheme,
  readBookingThemeCssTokens,
} from "./bookingThemes";
import {
  isBookingHeroBackgroundEnabled,
} from "./bookingHeroBackground";
import "./bookingThemes.css";
import RoomCategoryDetails from "./RoomCategoryDetails.jsx";
import { normalizeBookingCardPolicy } from "./bookingCardPolicy";
import { commitBookingDraftGroup } from "./bookingDraftCommit";
import {
  getCurrencyMinorUnits,
  normalizeCurrencyCode,
} from "../currency/currencyPrecision";

import { loadStripe } from "@stripe/stripe-js/pure";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

// ✅ IMPORTANT: no global stripePromise. It must be created with the correct connected account per booking.

const BOOKING_ENTRY_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function unwrapBookingApiPayload(payload) {
  if (!payload || typeof payload !== "object") return {};
  return payload.data && typeof payload.data === "object" ? payload.data : payload;
}

function normalizeBookingContactValues(values, fields) {
  return (Array.isArray(values) ? values : [])
    .map((value) => {
      if (typeof value === "string") return value;
      return fields.map((field) => value?.[field]).find(Boolean) || "";
    })
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

function normalizeBookingCartItem(item, index = 0) {
  const source = item && typeof item === "object" ? item : {};
  const sourceHasExtras =
    (Array.isArray(source.extras) && source.extras.length > 0) ||
    Number(source.extras_total ?? source.extra_total ?? 0) !== 0;
  const draftId = Number(
    source.reservation_draft_id ?? source.draft_id ?? source.reservation_id ?? source.id
  );
  const category = source.selected_category || source.room_category || source.category || {
    id: source.room_category_id ?? source.category_id ?? null,
    name: source.room_category_name ?? source.category_name ?? "",
  };
  const rate = source.selected_rate || source.room_rate || source.rate || {
    id: source.room_rate_id ?? source.rate_id ?? null,
    name: source.room_rate_name ?? source.rate_name ?? "",
  };
  const minTotal =
    rate?.min_total ??
    rate?.total ??
    source.room_total ??
    source.rooms_total ??
    source.accommodation_total ??
    source.min_total ??
    (!sourceHasExtras ? (source.total ?? source.total_amount) : null);
  const minAverage =
    rate?.min_avg_night ??
    rate?.avg_night ??
    source.min_avg_night ??
    source.avg_night ??
    (
      Number(source.nights) > 0 && Number.isFinite(Number(minTotal))
        ? Number(minTotal) / Number(source.nights)
        : null
    );
  const fallbackId = `cart-item-${source.selection_key || index}`;

  return {
    ...source,
    reservation_draft_id: Number.isFinite(draftId) && draftId > 0 ? draftId : fallbackId,
    selection_key: String(source.selection_key || rate?.selection_key || "").trim(),
    check_in_date: source.check_in_date ?? source.checkIn ?? source.arrival_date ?? null,
    check_out_date: source.check_out_date ?? source.checkOut ?? source.departure_date ?? null,
    adults: Number(source.adults ?? source.number_of_adults ?? 0),
    children: Number(source.children ?? source.number_of_children ?? 0),
    babies: Number(source.babies ?? source.number_of_babies ?? 0),
    selected_category: category,
    selected_rate: {
      ...rate,
      selection_key: String(source.selection_key || rate?.selection_key || "").trim(),
      min_total: minTotal,
      min_avg_night: minAverage,
      currency: rate?.currency || source.currency || null,
    },
    extras: (Array.isArray(source.extras) ? source.extras : [])
      .map((extra) => normalizeBookingCartExtra(extra, source.currency || null))
      .filter(Boolean),
  };
}

function normalizeBookingCartExtra(extra, fallbackCurrency = null) {
  if (!extra || typeof extra !== "object") return null;
  const extraId = Number(extra.extra_id ?? extra.id);
  if (!Number.isFinite(extraId) || extraId <= 0) return null;
  const total = Number(
    extra.total ??
    extra.calculated_amount ??
    extra.amount ??
    extra.display_total ??
    0
  );
  const roomIds = Array.from(new Set(
    (
      (Array.isArray(extra.reservation_draft_ids) && extra.reservation_draft_ids) ||
      (Array.isArray(extra.room_ids) && extra.room_ids) ||
      (Array.isArray(extra.eligible_room_ids) && extra.eligible_room_ids) ||
      []
    )
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0)
  ));

  return {
    ...extra,
    extra_id: extraId,
    name: String(extra.name || extra.extra_name || "").trim(),
    total: Number.isFinite(total) ? total : 0,
    currency: extra.currency || extra.display_currency || fallbackCurrency,
    reservation_draft_ids: roomIds,
    eligible_room_count: Math.max(
      roomIds.length,
      Math.trunc(Number(extra.eligible_room_count ?? extra.room_count ?? 0))
    ),
  };
}

export function normalizeBookingCartPayload(payload, fallbackCurrency = "EUR") {
  const root = unwrapBookingApiPayload(payload);
  const cart = root.cart && typeof root.cart === "object" ? root.cart : root;
  const rawItems =
    (Array.isArray(cart.items) && cart.items) ||
    (Array.isArray(cart.selections) && cart.selections) ||
    (Array.isArray(cart.rooms) && cart.rooms) ||
    (Array.isArray(root.items) && root.items) ||
    [];
  const items = rawItems.map(normalizeBookingCartItem);
  const parsedCount = Number(cart.count ?? cart.item_count ?? cart.room_count ?? items.length);
  const currency = cart.currency || root.currency || fallbackCurrency;
  const rootExtras = (
    (Array.isArray(cart.extras) && cart.extras) ||
    (Array.isArray(cart.selected_extras) && cart.selected_extras) ||
    (Array.isArray(root.extras) && root.extras) ||
    []
  ).map((extra) => normalizeBookingCartExtra(extra, currency)).filter(Boolean);
  const extras = rootExtras.length > 0
    ? rootExtras
    : Array.from(items.reduce((byExtraId, item) => {
        (Array.isArray(item?.extras) ? item.extras : []).forEach((extra) => {
          const extraId = Number(extra?.extra_id);
          if (!Number.isFinite(extraId) || extraId <= 0) return;
          const current = byExtraId.get(extraId) || {
            ...extra,
            total: 0,
            base_total: 0,
            eligible_room_count: 0,
            reservation_draft_ids: [],
          };
          const draftId = Number(item?.reservation_draft_id);
          current.total += Number.isFinite(Number(extra?.total)) ? Number(extra.total) : 0;
          current.base_total += Number.isFinite(Number(extra?.base_total)) ? Number(extra.base_total) : 0;
          if (Number.isFinite(draftId) && draftId > 0 && !current.reservation_draft_ids.includes(draftId)) {
            current.reservation_draft_ids.push(draftId);
            current.eligible_room_count = current.reservation_draft_ids.length;
          }
          byExtraId.set(extraId, current);
        });
        return byExtraId;
      }, new Map()).values());
  const roomFallbackTotal = items.reduce((sum, item) => {
    const value = Number(item?.selected_rate?.min_total);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const extrasFallbackTotal = extras.reduce((sum, extra) => {
    const value = Number(extra?.total);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const parsedRoomsTotal = Number(
    cart.rooms_total ?? cart.room_total ?? cart.accommodation_total ?? roomFallbackTotal
  );
  const parsedExtrasTotal = Number(cart.extras_total ?? cart.extra_total ?? extrasFallbackTotal);
  const parsedGrandTotal = Number(
    cart.grand_total ?? cart.total ?? cart.total_amount ??
    ((Number.isFinite(parsedRoomsTotal) ? parsedRoomsTotal : roomFallbackTotal) +
      (Number.isFinite(parsedExtrasTotal) ? parsedExtrasTotal : extrasFallbackTotal))
  );

  return {
    ...cart,
    items,
    extras,
    count: Number.isFinite(parsedCount) ? Math.max(0, Math.trunc(parsedCount)) : items.length,
    rooms_total: Number.isFinite(parsedRoomsTotal) ? parsedRoomsTotal : roomFallbackTotal,
    extras_total: Number.isFinite(parsedExtrasTotal) ? parsedExtrasTotal : extrasFallbackTotal,
    grand_total: Number.isFinite(parsedGrandTotal)
      ? parsedGrandTotal
      : roomFallbackTotal + extrasFallbackTotal,
    total: Number.isFinite(parsedGrandTotal)
      ? parsedGrandTotal
      : roomFallbackTotal + extrasFallbackTotal,
    currency,
    cart_version: cart.cart_version ?? root.cart_version ?? null,
  };
}

function normalizeBookingExtraId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
}

function normalizeBookingExtraDraftId(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
}

export function bookingExtraSelectionKey(reservationDraftId, extraId) {
  const normalizedExtraId = normalizeBookingExtraId(extraId);
  if (!normalizedExtraId) return "";
  const normalizedDraftId = normalizeBookingExtraDraftId(reservationDraftId);
  return `${normalizedDraftId == null ? "reservation" : normalizedDraftId}:${normalizedExtraId}`;
}

function normalizeBookingExtraSelectionEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const extraId = normalizeBookingExtraId(entry.extra_id ?? entry.id);
  if (!extraId) return null;
  const reservationDraftId = normalizeBookingExtraDraftId(entry.reservation_draft_id);
  if (entry.reservation_draft_id != null && reservationDraftId == null) return null;
  return {
    reservation_draft_id: reservationDraftId,
    extra_id: extraId,
  };
}

function normalizeBookingExtraItem(rawItem, {
  currency,
  reservationDraftId = null,
  selectedKeys = new Set(),
  legacySelectedIds = new Set(),
  selectionEntriesAreAuthoritative = false,
} = {}) {
  const item = rawItem && typeof rawItem === "object" ? rawItem : {};
  const extraId = normalizeBookingExtraId(item.extra_id ?? item.id);
  if (!extraId) return null;
  const normalizedDraftId = normalizeBookingExtraDraftId(reservationDraftId);
  const eligibleRoomIds = Array.from(new Set(
    (Array.isArray(item.eligible_room_ids) ? item.eligible_room_ids : [])
      .map(normalizeBookingExtraDraftId)
      .filter((id) => id != null)
  ));
  const unitAmount = Number(item.unit_amount ?? item.display_unit_amount ?? item.amount ?? 0);
  const total = Number(item.total ?? item.display_total ?? item.calculated_amount ?? 0);
  const selectionReason = String(item.selection_reason || "").trim().toLowerCase();
  const isAutomatic =
    item.is_automatic === true ||
    item.apply_automatically === true ||
    selectionReason === "automatic";
  const isRequired =
    item.is_mandatory === true ||
    item.mandatory === true ||
    selectionReason === "mandatory" ||
    selectionReason === "required" ||
    (item.is_required === true && !isAutomatic);
  const selectionKey = bookingExtraSelectionKey(normalizedDraftId, extraId);
  const selectedByContract = selectedKeys.has(selectionKey);
  const selectedByLegacyAlias = legacySelectedIds.has(extraId);
  const isSelected =
    isRequired ||
    isAutomatic ||
    selectedByContract ||
    (!selectionEntriesAreAuthoritative && (
      item.is_selected === true || selectedByLegacyAlias
    ));

  return {
    ...item,
    extra_id: extraId,
    reservation_draft_id: normalizedDraftId,
    selection_key: selectionKey,
    name: String(item.name || item.extra_name || "").trim(),
    description: String(item.description || "").trim(),
    eligible_room_ids: eligibleRoomIds,
    eligible_room_count: Math.max(
      eligibleRoomIds.length,
      Math.trunc(Number(item.eligible_room_count || 0))
    ),
    calculation: item.calculation && typeof item.calculation === "object"
      ? { ...item.calculation }
      : {},
    unit_amount: Number.isFinite(unitAmount) ? unitAmount : 0,
    total: Number.isFinite(total) ? total : 0,
    currency: item.currency || item.display_currency || currency,
    is_selected: isSelected,
    is_required: isRequired,
    is_automatic: isAutomatic,
    is_selectable: item.is_selectable !== false && !isRequired && !isAutomatic,
    can_remove: item.can_remove !== false && !isRequired && !isAutomatic,
  };
}

function isVisibleBookingExtra(item) {
  const extraId = normalizeBookingExtraId(item?.extra_id ?? item?.id);
  const total = Number(item?.total);
  return (
    !!extraId &&
    item?.is_active !== false &&
    item?.inclusive !== true &&
    Number.isFinite(total) &&
    Math.round(total * 100) > 0
  );
}

function sortBookingExtraSelections(entries) {
  return [...entries].sort((left, right) => {
    const leftDraftId = left.reservation_draft_id == null ? -1 : left.reservation_draft_id;
    const rightDraftId = right.reservation_draft_id == null ? -1 : right.reservation_draft_id;
    return leftDraftId - rightDraftId || left.extra_id - right.extra_id;
  });
}

export function normalizeBookingExtrasCatalogPayload(payload, fallbackCurrency = "EUR") {
  const root = unwrapBookingApiPayload(payload);
  const catalog = root.catalog && typeof root.catalog === "object" ? root.catalog : root;
  const selectionNode = catalog.selection || root.selection || {};
  const rawSelectedIds =
    selectionNode.extra_ids ?? catalog.selected_extra_ids ?? root.selected_extra_ids ?? [];
  const selectedExtraIds = Array.from(new Set(
    (Array.isArray(rawSelectedIds) ? rawSelectedIds : [])
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0)
  ));
  const legacySelectedSet = new Set();
  const currency = catalog.currency || root.currency || fallbackCurrency;
  const hasExplicitSelectionEntries =
    Array.isArray(selectionNode.entries) ||
    Array.isArray(catalog.selections) ||
    Array.isArray(root.selections);
  const rawSelectionEntries =
    (Array.isArray(selectionNode.entries) && selectionNode.entries) ||
    (Array.isArray(catalog.selections) && catalog.selections) ||
    (Array.isArray(root.selections) && root.selections) ||
    [];
  const normalizedSelectionEntries = Array.from(
    rawSelectionEntries.reduce((entriesByKey, entry) => {
      const normalized = normalizeBookingExtraSelectionEntry(entry);
      if (!normalized) return entriesByKey;
      entriesByKey.set(
        bookingExtraSelectionKey(normalized.reservation_draft_id, normalized.extra_id),
        normalized
      );
      return entriesByKey;
    }, new Map()).values()
  );
  // `extra_ids` is only a legacy alias: once the v2 entries contract is
  // present, even an empty entries array is authoritative. Falling back to a
  // flat id list here would select the same per-room extra in every room.
  if (!hasExplicitSelectionEntries) {
    selectedExtraIds.forEach((extraId) => legacySelectedSet.add(extraId));
  }
  const selectedKeys = new Set(
    normalizedSelectionEntries.map((entry) => (
      bookingExtraSelectionKey(entry.reservation_draft_id, entry.extra_id)
    ))
  );
  const rawRooms =
    (Array.isArray(catalog.rooms) && catalog.rooms) ||
    (Array.isArray(root.rooms) && root.rooms) ||
    [];
  const rawCart = root.cart || catalog.cart || {};
  const rawCartItems =
    (Array.isArray(rawCart.items) && rawCart.items) ||
    (Array.isArray(rawCart.rooms) && rawCart.rooms) ||
    [];
  const cartItemByDraftId = new Map();
  rawCartItems.forEach((rawCartItem) => {
    const reservationDraftId = normalizeBookingExtraDraftId(
      rawCartItem?.reservation_draft_id ?? rawCartItem?.draft_id ?? rawCartItem?.id
    );
    if (reservationDraftId) cartItemByDraftId.set(reservationDraftId, rawCartItem);
  });
  const rooms = rawRooms.map((rawRoom, roomIndex) => {
    const room = rawRoom && typeof rawRoom === "object" ? rawRoom : {};
    const reservationDraftId = normalizeBookingExtraDraftId(
      room.reservation_draft_id ?? room.draft_id ?? room.id
    );
    if (!reservationDraftId) return null;
    const cartItem = cartItemByDraftId.get(reservationDraftId) || {};
    const criteriaSource = room.criteria && typeof room.criteria === "object" ? room.criteria : room;
    const roomCategorySource = room.room_category || room.selected_category || room.category || {};
    const cartCategorySource =
      cartItem.selected_category || cartItem.room_category || cartItem.category || {};
    const roomRateSource = room.room_rate || room.selected_rate || room.rate || {};
    const cartRateSource = cartItem.selected_rate || cartItem.room_rate || cartItem.rate || {};
    const rawRoomItems =
      (Array.isArray(room.items) && room.items) ||
      (Array.isArray(room.extras) && room.extras) ||
      [];
    return {
      ...room,
      reservation_draft_id: reservationDraftId,
      group_index: Number.isFinite(Number(room.group_index))
        ? Math.max(0, Math.trunc(Number(room.group_index)))
        : roomIndex,
      room_category: {
        ...(cartCategorySource && typeof cartCategorySource === "object" ? cartCategorySource : {}),
        ...(roomCategorySource && typeof roomCategorySource === "object" ? roomCategorySource : {}),
        id:
          roomCategorySource?.id ?? room.room_category_id ?? room.category_id ??
          cartCategorySource?.id ?? cartItem.room_category_id ?? cartItem.category_id ?? null,
        name:
          roomCategorySource?.name || room.room_category_name || room.category_name ||
          cartCategorySource?.name || cartItem.room_category_name || cartItem.category_name || "",
      },
      room_rate: {
        ...(cartRateSource && typeof cartRateSource === "object" ? cartRateSource : {}),
        ...(roomRateSource && typeof roomRateSource === "object" ? roomRateSource : {}),
        id:
          roomRateSource?.id ?? room.room_rate_id ?? room.rate_id ??
          cartRateSource?.id ?? cartItem.room_rate_id ?? cartItem.rate_id ?? null,
        name:
          roomRateSource?.name || room.room_rate_name || room.rate_name ||
          cartRateSource?.name || cartItem.room_rate_name || cartItem.rate_name || "",
      },
      criteria: {
        ...criteriaSource,
        check_in_date:
          criteriaSource.check_in_date ?? criteriaSource.checkIn ?? criteriaSource.arrival_date ??
          cartItem.check_in_date ?? cartItem.checkIn ?? null,
        check_out_date:
          criteriaSource.check_out_date ?? criteriaSource.checkOut ?? criteriaSource.departure_date ??
          cartItem.check_out_date ?? cartItem.checkOut ?? null,
        adults: Number(
          criteriaSource.adults ?? criteriaSource.number_of_adults ?? cartItem.adults ?? 0
        ),
        children: Number(
          criteriaSource.children ?? criteriaSource.number_of_children ?? cartItem.children ?? 0
        ),
        babies: Number(
          criteriaSource.babies ?? criteriaSource.number_of_babies ?? cartItem.babies ?? 0
        ),
      },
      items: rawRoomItems
        .map((item) => normalizeBookingExtraItem(item, {
          currency,
          reservationDraftId,
          selectedKeys,
          legacySelectedIds: legacySelectedSet,
          selectionEntriesAreAuthoritative: hasExplicitSelectionEntries,
        }))
        .filter(Boolean),
    };
  }).filter(Boolean).sort((left, right) => (
    left.group_index - right.group_index || left.reservation_draft_id - right.reservation_draft_id
  ));

  const reservationSource =
    (catalog.reservation && typeof catalog.reservation === "object" && catalog.reservation) ||
    (root.reservation && typeof root.reservation === "object" && root.reservation) ||
    {};
  const reservationItems = (
    (Array.isArray(reservationSource.items) && reservationSource.items) ||
    (Array.isArray(reservationSource.extras) && reservationSource.extras) ||
    []
  ).map((item) => normalizeBookingExtraItem(item, {
    currency,
    reservationDraftId: null,
    selectedKeys,
    legacySelectedIds: legacySelectedSet,
    selectionEntriesAreAuthoritative: hasExplicitSelectionEntries,
  })).filter(Boolean);
  const rawItems =
    (Array.isArray(catalog.items) && catalog.items) ||
    (Array.isArray(catalog.extras) && catalog.extras) ||
    (Array.isArray(root.items) && root.items) ||
    [];
  const items = rawItems.map((item) => normalizeBookingExtraItem(item, {
    currency,
    selectedKeys,
    legacySelectedIds: legacySelectedSet,
    selectionEntriesAreAuthoritative: hasExplicitSelectionEntries,
  })).filter(Boolean);

  // Contract-v1 fallback: keep old catalogs usable while the v2 migration is
  // rolling out by distributing flat items into their explicit room coverage.
  if (rooms.length === 0 && reservationItems.length === 0 && items.length > 0) {
    const cartRooms = (
      (Array.isArray(rawCart.items) && rawCart.items) ||
      (Array.isArray(rawCart.rooms) && rawCart.rooms) ||
      []
    );
    cartRooms.forEach((rawRoom, roomIndex) => {
      const reservationDraftId = normalizeBookingExtraDraftId(
        rawRoom?.reservation_draft_id ?? rawRoom?.draft_id ?? rawRoom?.id
      );
      if (!reservationDraftId) return;
      const roomItems = items
        .filter((item) => (
          String(item.scope || "per_room").toLowerCase() !== "per_reservation" &&
          (
            item.eligible_room_ids.length === 0 ||
            item.eligible_room_ids.includes(reservationDraftId)
          )
        ))
        .map((item) => normalizeBookingExtraItem(item, {
          currency,
          reservationDraftId,
          selectedKeys,
          legacySelectedIds: legacySelectedSet,
          selectionEntriesAreAuthoritative: hasExplicitSelectionEntries,
        }))
        .filter(Boolean);
      rooms.push({
        reservation_draft_id: reservationDraftId,
        group_index: roomIndex,
        room_category: rawRoom?.selected_category || rawRoom?.room_category || rawRoom?.category || null,
        room_rate: rawRoom?.selected_rate || rawRoom?.room_rate || rawRoom?.rate || null,
        criteria: {
          check_in_date: rawRoom?.check_in_date ?? null,
          check_out_date: rawRoom?.check_out_date ?? null,
          adults: Number(rawRoom?.adults || 0),
          children: Number(rawRoom?.children || 0),
          babies: Number(rawRoom?.babies || 0),
        },
        items: roomItems,
      });
    });
    items
      .filter((item) => String(item.scope || "").toLowerCase() === "per_reservation")
      .forEach((item) => reservationItems.push(normalizeBookingExtraItem(item, {
        currency,
        reservationDraftId: null,
        selectedKeys,
        legacySelectedIds: legacySelectedSet,
        selectionEntriesAreAuthoritative: hasExplicitSelectionEntries,
      })));
  }

  const entriesByKey = new Map(
    normalizedSelectionEntries.map((entry) => [
      bookingExtraSelectionKey(entry.reservation_draft_id, entry.extra_id),
      entry,
    ])
  );
  const collectSelectedEntry = (item, reservationDraftId) => {
    if (!item?.is_selected && !item?.is_required && !item?.is_automatic) return;
    const entry = {
      reservation_draft_id: normalizeBookingExtraDraftId(reservationDraftId),
      extra_id: item.extra_id,
    };
    entriesByKey.set(
      bookingExtraSelectionKey(entry.reservation_draft_id, entry.extra_id),
      entry
    );
  };
  rooms.forEach((room) => room.items.forEach((item) => (
    collectSelectedEntry(item, room.reservation_draft_id)
  )));
  reservationItems.forEach((item) => collectSelectedEntry(item, null));
  const selectionEntries = sortBookingExtraSelections(entriesByKey.values());
  const normalizedSelectedExtraIds = Array.from(new Set(
    [
      ...(hasExplicitSelectionEntries ? [] : selectedExtraIds),
      ...selectionEntries.map((entry) => entry.extra_id),
    ]
  )).sort((left, right) => left - right);

  return {
    ...catalog,
    items,
    rooms,
    reservation: { ...reservationSource, items: reservationItems },
    selections: selectionEntries,
    selected_extra_ids: normalizedSelectedExtraIds,
    selection: {
      ...selectionNode,
      entries: selectionEntries,
      extra_ids: normalizedSelectedExtraIds,
    },
    currency,
    cart: rawCart,
  };
}

export function hasBookableExtras(catalog) {
  const roomItems = (Array.isArray(catalog?.rooms) ? catalog.rooms : [])
    .flatMap((room) => (Array.isArray(room?.items) ? room.items : []));
  const reservationItems = Array.isArray(catalog?.reservation?.items)
    ? catalog.reservation.items
    : [];
  const legacyItems = Array.isArray(catalog?.items) ? catalog.items : [];
  const hasScopedContract =
    Number(catalog?.contract_version || 0) >= 2 ||
    (Array.isArray(catalog?.rooms) && catalog.rooms.length > 0) ||
    reservationItems.length > 0;
  const candidates = hasScopedContract
    ? [...roomItems, ...reservationItems]
    : legacyItems;
  return candidates.some(isVisibleBookingExtra);
}

export function buildBookingExtrasSelections(catalog) {
  const selectedKeys = new Set(
    (
      (Array.isArray(catalog?.selection?.entries) && catalog.selection.entries) ||
      (Array.isArray(catalog?.selections) && catalog.selections) ||
      []
    ).map((entry) => bookingExtraSelectionKey(entry?.reservation_draft_id, entry?.extra_id))
      .filter(Boolean)
  );
  const selectionsByKey = new Map();
  const collect = (item, reservationDraftId) => {
    if (!isVisibleBookingExtra(item)) return;
    const key = bookingExtraSelectionKey(reservationDraftId, item?.extra_id);
    if (!key) return;
    if (!item?.is_selected && !item?.is_required && !item?.is_automatic && !selectedKeys.has(key)) return;
    selectionsByKey.set(key, {
      reservation_draft_id: normalizeBookingExtraDraftId(reservationDraftId),
      extra_id: normalizeBookingExtraId(item.extra_id),
    });
  };

  (Array.isArray(catalog?.rooms) ? catalog.rooms : []).forEach((room) => {
    (Array.isArray(room?.items) ? room.items : []).forEach((item) => (
      collect(item, room.reservation_draft_id)
    ));
  });
  (Array.isArray(catalog?.reservation?.items) ? catalog.reservation.items : [])
    .forEach((item) => collect(item, null));

  return sortBookingExtraSelections(selectionsByKey.values());
}

async function requestReconciledBookingExtrasCatalog({
  apiUrl,
  groupId,
  publicSessionToken,
  displayCurrency,
  isCurrent = () => true,
}) {
  const headers = { "x-public-session-token": publicSessionToken };
  const { data } = await axios.get(
    `${apiUrl}/api/reservation-draft-groups2/${groupId}/extras/catalog`,
    {
      headers,
      params: { display_currency: displayCurrency },
    }
  );
  if (!isCurrent()) return null;

  const catalog = normalizeBookingExtrasCatalogPayload(data, displayCurrency);
  const selections = buildBookingExtrasSelections(catalog);
  const reconciledResponse = await axios.put(
    `${apiUrl}/api/reservation-draft-groups2/${groupId}/extras/selections`,
    { selections, display_currency: displayCurrency },
    { headers }
  );
  if (!isCurrent()) return null;
  return normalizeBookingExtrasCatalogPayload(reconciledResponse.data, displayCurrency);
}

function bookingCartToRoomUi(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const roomDraftIds = [];
  const roomStateByDraftId = {};

  items.forEach((item, index) => {
    const rawId = item?.reservation_draft_id;
    const draftId = Number(rawId);
    const key = Number.isFinite(draftId) && draftId > 0 ? draftId : String(rawId || `cart-item-${index}`);
    roomDraftIds.push(key);
    roomStateByDraftId[key] = {
      checkIn: item?.check_in_date || null,
      checkOut: item?.check_out_date || null,
      adults: Number(item?.adults || 0),
      children: Number(item?.children || 0),
      babies: Number(item?.babies || 0),
      selectionKey: String(item?.selection_key || "").trim(),
      selectedCategory: item?.selected_category || null,
      selectedRate: item?.selected_rate || null,
    };
  });

  return { roomDraftIds, roomStateByDraftId };
}

function readBookingEntryParams() {
  if (typeof window === "undefined") {
    return {
      checkIn: null,
      checkOut: null,
      adults: 2,
      children: 0,
      lang: "en",
      shouldAutoSearch: false,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const checkIn = String(params.get("checkIn") || "").trim();
  const checkOut = String(params.get("checkOut") || "").trim();
  const hasValidDates =
    BOOKING_ENTRY_DATE_PATTERN.test(checkIn) &&
    BOOKING_ENTRY_DATE_PATTERN.test(checkOut) &&
    checkOut > checkIn;
  const toBoundedInteger = (value, fallback, min, max) => {
    const parsed = Number.parseInt(String(value || ""), 10);
    return Number.isInteger(parsed)
      ? Math.min(max, Math.max(min, parsed))
      : fallback;
  };
  const requestedLanguage = String(params.get("lang") || "").toLowerCase();

  return {
    checkIn: hasValidDates ? checkIn : null,
    checkOut: hasValidDates ? checkOut : null,
    adults: toBoundedInteger(params.get("adults"), 2, 1, 20),
    children: toBoundedInteger(params.get("children"), 0, 0, 20),
    lang: requestedLanguage.startsWith("es") ? "es" : "en",
    shouldAutoSearch:
      params.get("source") === "widget" && params.get("autoSearch") === "1" && hasValidDates,
  };
}

export default function BookingApp() {
  const { slug } = useParams(); // ✅ no groupId in URL
  const [bookingEntryParams] = useState(readBookingEntryParams);

  //24.01.2026 - para mostrar amenities en booking engine
  const [hotelContext, setHotelContext] = useState(null);
  const cardRequired = normalizeBookingCardPolicy(hotelContext?.booking_engine_card_policy) === "required";
  const bookingTheme = normalizeBookingTheme(hotelContext?.booking_engine_theme);

  useEffect(
    () => applyBookingThemeToRoot(bookingTheme),
    [bookingTheme]
  );

  // room_category_id -> { cover, gallery[] }
  const [roomCategoryAssetsMap, setRoomCategoryAssetsMap] = useState({});
  // room_category_id -> amenities[]
  const [roomCategoryAmenitiesMap, setRoomCategoryAmenitiesMap] = useState({});
  // room_category_id -> booking_engine_room_categories row (commercial copy)
  const [beRoomCategoryCopyMap, setBeRoomCategoryCopyMap] = useState({});
  const [error] = useState(null);

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const termsAcceptancePaymentEvidenceKeyRef = useRef(null);
  const widgetAutoSearchAttemptedRef = useRef(false);
  const [widgetAutoSearchPending, setWidgetAutoSearchPending] = useState(
    bookingEntryParams.shouldAutoSearch
  );



    // ✅ i18n minimal (ES/EN)
  const [lang, setLang] = useState(bookingEntryParams.lang); // "en" | "es"
  const [bookingEngineTexts, setBookingEngineTexts] = useState(null); // from DB (public endpoint)
  const [displayCurrency, setDisplayCurrency] = useState("");

  const currencyConfiguration = hotelContext?.currency_configuration || null;
  const bookingCurrencies = useMemo(
    () => currencyConfiguration?.enabled
      ? (currencyConfiguration.currencies || [])
          .map((entry) => normalizeCurrencyCode(entry?.code || entry))
          .filter(Boolean)
      : [],
    [currencyConfiguration]
  );
  const bookingCurrencyCatalog = useMemo(
    () => Array.isArray(currencyConfiguration?.available_currencies)
      ? currencyConfiguration.available_currencies
      : [],
    [currencyConfiguration?.available_currencies]
  );
  const bookingCurrency = resolveBookingCurrency(currencyConfiguration, displayCurrency);

  useEffect(() => {
    if (!currencyConfiguration?.enabled) {
      setDisplayCurrency("");
      return;
    }
    setDisplayCurrency((current) =>
      bookingCurrencies.includes(current)
        ? current
        : currencyConfiguration.base_currency_code
    );
  }, [bookingCurrencies, currencyConfiguration?.base_currency_code, currencyConfiguration?.enabled]);

  

  const I18N = {
    en: {
      missing_group_id: "Missing reservation group id",
      missing_business_unit: "Missing business unit",
      enter_first_name: "Please enter your first name.",
      enter_last_name: "Please enter your last name.",
      enter_email: "Please enter your email.",
      enter_phone: "Please enter your phone.",
      unable_create_customer: "Unable to create customer.",
      unable_save_guest_details: "Unable to save guest details.",
      booking_confirm: "Confirm booking",
      booking_confirming: "Confirming booking…",
      booking_confirm_error: "We could not confirm your booking. Please try again.",
      booking_confirm_availability: "There is no longer enough availability for this booking. Go back to room selection to review your stay.",
      booking_terms_required: "Please accept the terms and conditions before continuing.",
      booking_card_policy_changed: "The hotel has changed its card requirement. Please review this step and continue again.",
      booking_without_card_hint: "No card is required. Confirming this booking does not take a payment.",
      booking_without_card_confirmed: "Booking confirmed. The hotel does not require a card to confirm this booking.",
      unable_load_rooms: "Unable to load available rooms",
      select_dates: "Please select check-in and check-out dates",
      invalid_dates: "Invalid dates",
      unable_start_booking_missing_bu: "Unable to start booking (missing business unit)",
      unable_update_availability: "Unable to update availability",
      unable_check_availability: "Unable to check availability",
      unable_load_room_rates: "Unable to load room rates",
      unable_select_rate: "Unable to select this rate",
      unable_add_room_now: "Unable to add a room right now.",
      error_adding_another_room: "Error adding another room.",
      cannot_delete_last_room: "You cannot delete the last room.",
      confirm_remove_room: "Remove this room from your booking?",
      error_removing_room: "Error removing this room.",
      select_your_stay: "Select your stay",
      check_in: "Check-in",
      check_out: "Check-out",
      adults_label: "Adults",
      children_label: "Children",
      no_children: "No children",
      adult_singular: "adult",
      adult_plural: "adults",
      child_singular: "child",
      child_plural: "children",
      check_availability: "CHECK AVAILABILITY",

      continue_guest_details: "CONTINUE TO GUEST DETAILS",
      continue_to_extras: "CONTINUE TO EXTRAS",
      select_rate_for_each_room: "Select a room rate for each room before continuing.",

      choose_room: "Choose your room",
      loading_available_rooms: "Loading available rooms…",
      no_rooms_for_dates: "No rooms available for these dates.",

      guest_details_title: "Guest details and special requests",
      guest_details_subtitle: "Please enter the booker information",

      extras_title: "Make your stay even better",
      extras_subtitle: "Add a little more comfort, convenience or indulgence to your booking.",
      extras_reservation_section_title: "For the whole booking",
      extras_reservation_section_subtitle: "These extras are added once and apply to your complete booking.",
      extras_room_section_title: "Room {{n}}",
      extras_room_section_subtitle: "Choose only what you want for this room.",
      extras_room_empty: "There are no room-specific extras available for this room.",
      extras_stay_dates: "Stay",
      extras_occupancy: "Occupancy",
      extras_loading: "Finding the best extras for your stay…",
      extras_load_error: "We could not load the available extras. Please try again before continuing.",
      extras_empty_title: "Everything is ready",
      extras_empty_body: "There are no additional extras available for the rooms in your booking.",
      extras_add: "ADD",
      extras_added: "ADDED",
      extras_remove: "REMOVE",
      extras_updating: "UPDATING…",
      extras_retry: "TRY AGAIN",
      extras_continue: "CONTINUE TO GUEST DETAILS",
      extras_coverage_room: "Applies to 1 room",
      extras_coverage_rooms: "Applies to {{count}} rooms",
      extras_once_per_booking: "Once per booking",
      extras_for_your_booking: "for your booking",
      extras_price_label: "Price",
      extras_applied_label: "Applied",
      extras_total_label: "Extra total",
      extras_unit_price: "{{amount}} per {{unit}}{{period}}",
      extras_percentage_price: "{{amount}}% of the {{base}}",
      extras_percentage_unit: " per {{unit}}",
      extras_unit_booking: "booking",
      extras_unit_room: "room",
      extras_unit_adult: "adult",
      extras_unit_child: "child",
      extras_unit_guest: "guest",
      extras_base_booking: "booking price",
      extras_base_room: "room price",
      extras_period_night: ", per night",
      extras_period_stay: ", per stay",
      extras_application_once: "Once for this booking",
      extras_application_stay: "{{units}} for the stay",
      extras_application_nights: "{{units}} × {{nights}}",
      extras_count_room: "{{count}} room",
      extras_count_rooms: "{{count}} rooms",
      extras_count_adult: "{{count}} adult",
      extras_count_adults: "{{count}} adults",
      extras_count_child: "{{count}} child",
      extras_count_children: "{{count}} children",
      extras_count_baby: "{{count}} baby",
      extras_count_babies: "{{count}} babies",
      extras_count_guest: "{{count}} guest",
      extras_count_guests: "{{count}} guests",
      extras_count_booking: "{{count}} booking",
      extras_count_bookings: "{{count}} bookings",
      extras_count_night: "{{count}} night",
      extras_count_nights: "{{count}} nights",
      extras_required: "REQUIRED",
      extras_automatic: "AUTOMATIC",
      extras_selected: "SELECTED",
      extras_included: "Included",
      extras_fallback_name: "Extra {{id}}",
      extras_selected_title: "EXTRAS",
      extras_none_selected: "No extras selected",
      extras_subtotal: "Extras",
      rooms_subtotal: "Rooms",
      extras_room_subtotal: "Room subtotal",
      extras_reservation_subtotal: "Booking extras",
      extras_calculation_basis: "Calculation base: {{amount}}",
      total_booking: "TOTAL BOOKING",
      extras_update_error: "We could not update this extra. Your previous selection is unchanged.",

      gender: "Gender",
      first_name: "First name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
      address: "Address",
      city: "City",
      postal_code: "Postal code",
      country: "Country",
      birth_date: "Birth date",
      id_type: "ID type",
      id_number: "ID number",
      rate_select_available_rates: "Available Rates",

      mandatory: "MANDATORY",
      required_fields_note: "* Required fields",
      date_format_hint: "MM/DD/YYYY",

      gender_male: "Male",
      gender_female: "Female",
      gender_other: "Other",
      gender_undisclosed: "Prefer not to say",

      id_type_passport: "Passport",
      id_type_national_id: "National ID",
      id_type_driving_license: "Driving license",

      special_request_for_room: "Special request for Room {{n}}",

      back: "BACK",
      continue: "CONTINUE",
      saving: "SAVING...",

      gdpr_notice:
        "GDPR notice: data is collected only to manage your booking and legal obligations, and will not be used for unrelated purposes.",

      cta_check_availability: "CHECK AVAILABILITY",
      select_date: "Select date",
      modal_adults_title: "Adults",
      modal_children_title: "Children",
      confirm: "Confirm",
      payment_details_title: "Payment details",
      payment_details_subtitle: "Confirm with card guarantee",

      reservation_confirmed_title: "Reservation confirmed",
      reservation_confirmed_subtitle: "Please save this page as your booking confirmation.",

      booking_summary: "BOOKING SUMMARY",
      my_booking: "MY BOOKING",
      open_booking_summary: "Open booking summary",
      close_booking_summary: "Close booking summary",
      room_singular: "room",
      room_plural: "rooms",
      guest_singular: "guest",
      guest_plural: "guests",
      different_stays: "Different stays",
      view_details: "View details",
      selected_subtotal: "SELECTED SUBTOTAL",
      selected_rooms_count: "{{selected}} of {{total}} rooms selected",
      select_rate_to_see_total: "Select a rate to see the total",
      language: "Language",
      currency_label: "Currency",
      hotel_contact: "HOTEL CONTACT",
      need_help: "Need help?",
      call_hotel: "Call hotel",
      edit_dates: "Edit dates",
      edit_guests: "Edit guests",
      edit_adults: "Edit adults",
      edit_children: "Edit children",

      label_hotel: "Hotel",
      label_locator: "Locator",
      label_booker: "Booker",
      label_email: "Email",
      label_phone: "Phone",

      label_room_n: "ROOM {{n}}",
      label_dates: "Dates",
      label_guests: "Guests",
      label_category: "Category",
      label_rate: "Rate",
      label_total: "Total",
      label_total_all: "TOTAL",

      payment_reference: "Payment reference",

      print_save_pdf: "PRINT / SAVE PDF",
      close_tab: "CLOSE TAB",
      total_all_rooms: "TOTAL (ALL ROOMS)",
      stay_details_room: "STAY DETAILS — ROOM {{n}}",
      room_details_dates: "Dates",
      room_details_guests: "Guests",
      room_details_category: "Category",
      room_details_rate: "Rate",
      room_details_total: "TOTAL",
      room_details_total_value: "Total: {{currency}}{{total}}",
      room_details_avg_per_night: "{{currency}}{{avg}} / night",
      room_details_change: "Change",
      room_details_remove: "Remove",
      no_rooms_for_dates: "No rooms available for these dates.",
      no_rooms_for_dates_combination: "No availability for {{adults}} adults and {{children}} children on these dates.",
      no_rooms_for_dates_help: "Try other dates with the same stay length.",
      no_rooms_for_dates_suggestions_title: "Closest available dates",
      no_rooms_for_dates_suggestions_loading: "Searching nearby available dates...",
      no_rooms_for_dates_no_suggestions: "No nearby alternatives found.",
      no_rooms_for_dates_apply: "Use these dates",
      no_rooms_for_dates_reason_capacity: "This guest combination is not supported by this hotel.",
      hotel_max_occupancy_hint: "Our rooms cannot accommodate more than {{max}} guests in total.",
      active: "ACTIVE",
      back: "BACK",
      continue: "CONTINUE",
      select_date_one: "Select date",
      adults: "Adults",
      children: "Children",
      add_room_title: "Add another room",
      add_room_subtitle: "We prefilled your previous stay details. Change them if needed.",
      close: "CLOSE",
      stay_details: "STAY DETAILS",
      change: "Change",
      adults_label: "Adults",
      children_label: "Children",
      confirm_choose_room: "CONFIRM AND CHOOSE ROOM",
      // ✅ CalendarModal
      calendar_select_checkin: "Select check-in date",
      calendar_select_checkout: "Select check-out date",
      calendar_confirm_stay: "Confirm your stay",

      calendar_dow_mon: "Mon",
      calendar_dow_tue: "Tue",
      calendar_dow_wed: "Wed",
      calendar_dow_thu: "Thu",
      calendar_dow_fri: "Fri",
      calendar_dow_sat: "Sat",
      calendar_dow_sun: "Sun",

      calendar_prev: "Previous",
      calendar_next: "Next",
      calendar_confirm_dates: "Confirm dates",
      room_details: "Room details",
      select_room: "Select room",
      available_rooms: "Available rooms",
      room_details_select: "Select",
      room_details_total: "TOTAL",
      room_details_night: "night",
      rate_select_select_rate: "Select a rate",
      rate_select_category: "Category: ",
      rate_select_loading: "Loading rates…",
      rate_select_no_rates: "No rates available for these dates.",
      rate_select_selected_rate: "SELECTED RATE",
      rate_select_total: "TOTAL",
      rate_select_confirm_rate: "CONFIRM RATE",
      rate_select_add_more_rooms: "You can add more rooms on the next step.",
      inline_rates_title: "Choose a rate",
      inline_rate_refundable: "Refundable",
      inline_rate_non_refundable: "Non-refundable",
      inline_rate_total_stay: "Stay total",
      inline_rate_selecting: "Selecting…",
      inline_rate_select: "Select rate",
      inline_rate_penalty_percent: "{{amount}}% cancellation penalty",
      inline_rate_penalty_fixed: "{{amount}} cancellation penalty",
      inline_rate_no_policy: "See booking conditions",
      inline_rate_selected: "Selected",
      inline_rate_add: "Add room",
      inline_rate_remove: "Remove room",
      inline_rate_sold_out: "No more rooms are available for these dates.",
      inline_rate_last_room_added: "You have added the last available room for these dates.",
      inline_rate_cart_limit: "You have reached the maximum number of rooms for one booking.",
      inline_rate_availability_missing: "Availability could not be confirmed for every night of this stay.",
      inline_rate_quantity: "Rooms selected",
      inline_rate_view_conditions: "View conditions",
      inline_rate_conditions_hint: "Select the info icon to view each rate’s conditions.",
      inline_rate_conditions_title: "Rate conditions",
      inline_rate_conditions_close: "Close rate conditions",
      inline_rate_conditions_refundability: "Refundability",
      inline_rate_conditions_cancellation: "Cancellation policy",
      inline_rate_conditions_penalty: "Cancellation penalty",
      inline_rate_conditions_no_policy: "No cancellation policy has been specified for this rate.",
      inline_rates_retry: "Try again",
      inline_rates_load_error: "We could not load the available rates. Please try again.",
      inline_rate_unavailable: "This rate is no longer available. We have refreshed the options.",
      inline_rate_retry_conflict: "The room options changed at the same time. Please try again.",
      counter_decrease: "Decrease",
      counter_increase: "Increase",
      rate_row_selected: "SELECTED",
      rate_row_select: "SELECT",
      rate_row_night: "night",
      payment_setup_missing_group_id: "Missing reservation group id",
      payment_setup_stripe_not_ready: "Stripe is not ready yet",
      payment_setup_enter_name: "Please enter the cardholder name.",
      payment_setup_card_element_not_found: "Card element not found",
      payment_setup_missing_client_secret: "Missing client_secret",
      payment_setup_card_confirmation_failed: "Card confirmation failed",
      payment_setup_commit_failed: "Unable to save card",
      payment_details_title: "PAYMENT DETAILS",
      cardholder_name: "CARDHOLDER NAME",
      mandatory: "MANDATORY",
      name_on_card_placeholder: "Name on card",
      card_details: "CARD DETAILS",
      card_secure_helper:
        "Your card will be stored securely. Charges or pre-authorizations may be applied according to the booking policies.",
      back: "BACK",
      processing: "PROCESSING...",
      pay_and_confirm_reservation: "CONFIRM WITH CARD GUARANTEE",
      category: "Category",
      rate: "Rate",
      total: "Total",
      rate_select_night: "night",
      invalid_email_format: "Invalid email. Use format like name@domain.xx",
      amenities_label: "Amenities",
      view_all_amenities: "View all amenities",
      amenities_title: "AMENIDADES",
      room_details_from: "From",
      room_details_view_rates: "View rates",
      room_details_photo: "photo",
      room_details_photos: "photos",
      room_photo_previous: "Previous photo",
      room_photo_next: "Next photo",
      room_photo_go_to: "Show photo {{current}} of {{total}}",
      room_photo_open_gallery: "Open gallery: {{room}}, photo {{current}} of {{total}}",
      room_features_title: "ROOM FEATURES",
      room_view_label: "View",
      room_highlights_label: "Highlights",
      room_amenities_label: "Amenities",
      room_details_all: "View all details",
      room_details_empty: "No room details listed",
      view_all_room_features: "View all features",
      no_amenities: "No amenities listed",
      taxes_including_prefix: "Including",
      taxes_not_including_any: "Not including any taxes or fees",
      tax_label_vat: "VAT",
      tax_label_other_taxes: "other taxes",
      tax_label_fees: "fees",
      tax_label_city_tax: "city tax",
      list_conjunction_and: "and",
      taxes_all_included: "All taxes and fees included",
      booking_terms_link: "Booking Terms & Conditions",

      cancel_title: "Cancel reservation",
      cancel_intro: "You are about to cancel your reservation.",
      cancel_reference: "Reference",
      cancel_reason_label: "Reason (optional)",
      cancel_reason_placeholder: "Tell us why you are cancelling (optional)",
      cancel_button: "Cancel booking",
      cancel_missing_params: "Invalid cancellation link. Please contact the hotel.",
      cancel_submitting: "Cancelling...",
      cancel_success_title: "Reservation cancelled",
      cancel_success_body: "Your reservation has been cancelled successfully.",
      cancel_failed_generic: "Cancellation failed. Please try again or contact the hotel.",
      payment_error_generic: "We couldn’t start the payment. Please try again or contact the hotel.",
      taxes_vat: "VAT",
      taxes_other: "other taxes",
      taxes_fees: "fees",
      taxes_city: "city tax",

    },
    es: {
      missing_group_id: "Falta el id del grupo de reserva",
      missing_business_unit: "Falta la unidad de negocio",
      enter_first_name: "Introduce tu nombre.",
      enter_last_name: "Introduce tu apellido.",
      enter_email: "Introduce tu email.",
      enter_phone: "Introduce tu teléfono.",
      unable_create_customer: "No se pudo crear el cliente.",
      unable_save_guest_details: "No se pudieron guardar los datos del huésped.",
      booking_confirm: "Confirmar reserva",
      booking_confirming: "Confirmando reserva…",
      booking_confirm_error: "No hemos podido confirmar tu reserva. Inténtalo de nuevo.",
      booking_confirm_availability: "Ya no hay disponibilidad suficiente para esta reserva. Vuelve a la selección de habitaciones para revisar tu estancia.",
      booking_terms_required: "Acepta los términos y condiciones antes de continuar.",
      booking_card_policy_changed: "El hotel ha cambiado el requisito de tarjeta. Revisa este paso y vuelve a continuar.",
      booking_without_card_hint: "No se requiere tarjeta. Al confirmar esta reserva no se realiza ningún cobro.",
      booking_without_card_confirmed: "Reserva confirmada. El hotel no exige tarjeta para confirmar esta reserva.",
      unable_load_rooms: "No se pudieron cargar las habitaciones disponibles",
      select_dates: "Selecciona las fechas de entrada y salida",
      invalid_dates: "Fechas inválidas",
      unable_start_booking_missing_bu: "No se puede iniciar la reserva (falta la unidad de negocio)",
      unable_update_availability: "No se pudo actualizar la disponibilidad",
      unable_check_availability: "No se pudo comprobar la disponibilidad",
      unable_load_room_rates: "No se pudieron cargar las tarifas",
      unable_select_rate: "No se pudo seleccionar esta tarifa",
      unable_add_room_now: "No se puede añadir una habitación ahora mismo.",
      error_adding_another_room: "Error al añadir otra habitación.",
      cannot_delete_last_room: "No puedes eliminar la última habitación.",
      confirm_remove_room: "¿Eliminar esta habitación de tu reserva?",
      error_removing_room: "Error al eliminar esta habitación.",
      select_your_stay: "Selecciona tu estancia",
      check_in: "Entrada",
      check_out: "Salida",
      adults_label: "Adultos",
      children_label: "Niños",
      no_children: "Sin niños",
      adult_singular: "adulto",
      adult_plural: "adultos",
      child_singular: "niño",
      child_plural: "niños",
      check_availability: "COMPROBAR DISPONIBILIDAD",

      continue_guest_details: "CONTINUAR A DATOS DEL HUÉSPED",
      continue_to_extras: "CONTINUAR A EXTRAS",
      select_rate_for_each_room: "Selecciona una tarifa para cada habitación antes de continuar.",

      choose_room: "Elige habitación",
      loading_available_rooms: "Cargando habitaciones disponibles…",
      no_rooms_for_dates: "No hay habitaciones disponibles para esas fechas.",

      guest_details_title: "Datos del huésped y peticiones especiales",
      guest_details_subtitle: "Introduce los datos del titular de la reserva",

      extras_title: "Haz que tu estancia sea aún mejor",
      extras_subtitle: "Añade un poco más de comodidad, conveniencia o capricho a tu reserva.",
      extras_reservation_section_title: "Para toda la reserva",
      extras_reservation_section_subtitle: "Estos extras se añaden una sola vez y se aplican a la reserva completa.",
      extras_room_section_title: "Habitación {{n}}",
      extras_room_section_subtitle: "Elige únicamente lo que quieres para esta habitación.",
      extras_room_empty: "No hay extras específicos disponibles para esta habitación.",
      extras_stay_dates: "Estancia",
      extras_occupancy: "Ocupación",
      extras_loading: "Buscando los mejores extras para tu estancia…",
      extras_load_error: "No pudimos cargar los extras disponibles. Inténtalo de nuevo antes de continuar.",
      extras_empty_title: "Todo está listo",
      extras_empty_body: "No hay extras adicionales disponibles para las habitaciones de tu reserva.",
      extras_add: "AÑADIR",
      extras_added: "AÑADIDO",
      extras_remove: "QUITAR",
      extras_updating: "ACTUALIZANDO…",
      extras_retry: "REINTENTAR",
      extras_continue: "CONTINUAR A DATOS DEL HUÉSPED",
      extras_coverage_room: "Se aplica a 1 habitación",
      extras_coverage_rooms: "Se aplica a {{count}} habitaciones",
      extras_once_per_booking: "Una vez por reserva",
      extras_for_your_booking: "para tu reserva",
      extras_price_label: "Precio",
      extras_applied_label: "Aplicado",
      extras_total_label: "Total del extra",
      extras_unit_price: "{{amount}} por {{unit}}{{period}}",
      extras_percentage_price: "{{amount}}% del {{base}}",
      extras_percentage_unit: " por {{unit}}",
      extras_unit_booking: "reserva",
      extras_unit_room: "habitación",
      extras_unit_adult: "adulto",
      extras_unit_child: "niño",
      extras_unit_guest: "persona",
      extras_base_booking: "precio de la reserva",
      extras_base_room: "precio de la habitación",
      extras_period_night: ", por noche",
      extras_period_stay: ", por estancia",
      extras_application_once: "Una vez para esta reserva",
      extras_application_stay: "{{units}} durante la estancia",
      extras_application_nights: "{{units}} × {{nights}}",
      extras_count_room: "{{count}} habitación",
      extras_count_rooms: "{{count}} habitaciones",
      extras_count_adult: "{{count}} adulto",
      extras_count_adults: "{{count}} adultos",
      extras_count_child: "{{count}} niño",
      extras_count_children: "{{count}} niños",
      extras_count_baby: "{{count}} bebé",
      extras_count_babies: "{{count}} bebés",
      extras_count_guest: "{{count}} persona",
      extras_count_guests: "{{count}} personas",
      extras_count_booking: "{{count}} reserva",
      extras_count_bookings: "{{count}} reservas",
      extras_count_night: "{{count}} noche",
      extras_count_nights: "{{count}} noches",
      extras_required: "OBLIGATORIO",
      extras_automatic: "AUTOMÁTICO",
      extras_selected: "SELECCIONADO",
      extras_included: "Incluido",
      extras_fallback_name: "Extra {{id}}",
      extras_selected_title: "EXTRAS",
      extras_none_selected: "No has seleccionado extras",
      extras_subtotal: "Extras",
      rooms_subtotal: "Habitaciones",
      extras_room_subtotal: "Subtotal de la habitación",
      extras_reservation_subtotal: "Extras de la reserva",
      extras_calculation_basis: "Base de cálculo: {{amount}}",
      total_booking: "TOTAL RESERVA",
      extras_update_error: "No pudimos actualizar este extra. Tu selección anterior no ha cambiado.",

      gender: "Género",
      first_name: "Nombre",
      last_name: "Apellidos",
      email: "Email",
      phone: "Teléfono",
      address: "Dirección",
      city: "Ciudad",
      postal_code: "Código postal",
      country: "País",
      birth_date: "Fecha de nacimiento",
      id_type: "Tipo de documento",
      id_number: "Número de documento",
      rate_select_available_rates: "Tarifas disponibles",

      mandatory: "OBLIGATORIO",
      required_fields_note: "* Campos obligatorios",
      date_format_hint: "DD/MM/AAAA",

      gender_male: "Hombre",
      gender_female: "Mujer",
      gender_other: "Otro",
      gender_undisclosed: "Prefiero no decirlo",

      id_type_passport: "Pasaporte",
      id_type_national_id: "DNI / ID nacional",
      id_type_driving_license: "Carnet de conducir",

      special_request_for_room: "Petición especial para Habitación {{n}}",

      back: "ATRÁS",
      continue: "CONTINUAR",
      saving: "GUARDANDO...",

      gdpr_notice:
        "Aviso GDPR: los datos se recogen solo para gestionar tu reserva y obligaciones legales, y no se usarán para fines no relacionados.",
      cta_check_availability: "COMPROBAR DISPONIBILIDAD",
      select_date: "Seleccionar fecha",
      modal_adults_title: "Adultos",
      modal_children_title: "Niños",
      confirm: "Confirmar",
      payment_details_title: "Datos de pago",
      payment_details_subtitle: "Paga y confirma la reserva",

      reservation_confirmed_title: "Reserva confirmada",
      reservation_confirmed_subtitle: "Guarda esta página como confirmación de tu reserva.",

      booking_summary: "RESUMEN DE LA RESERVA",
      my_booking: "MI RESERVA",
      open_booking_summary: "Abrir resumen de la reserva",
      close_booking_summary: "Cerrar resumen de la reserva",
      room_singular: "habitación",
      room_plural: "habitaciones",
      guest_singular: "huésped",
      guest_plural: "huéspedes",
      different_stays: "Estancias diferentes",
      view_details: "Ver detalles",
      selected_subtotal: "SUBTOTAL SELECCIONADO",
      selected_rooms_count: "{{selected}} de {{total}} habitaciones seleccionadas",
      select_rate_to_see_total: "Selecciona una tarifa para ver el total",
      language: "Idioma",
      currency_label: "Divisa",
      hotel_contact: "CONTACTO DEL HOTEL",
      need_help: "¿Necesitas ayuda?",
      call_hotel: "Llamar",
      edit_dates: "Cambiar fechas",
      edit_guests: "Cambiar huéspedes",
      edit_adults: "Cambiar adultos",
      edit_children: "Cambiar niños",

      label_hotel: "Hotel",
      label_locator: "Localizador",
      label_booker: "Titular",
      label_email: "Email",
      label_phone: "Teléfono",

      label_room_n: "HABITACIÓN {{n}}",
      label_dates: "Fechas",
      label_guests: "Huéspedes",
      label_category: "Categoría",
      label_rate: "Tarifa",
      label_total: "Total",
      label_total_all: "TOTAL",

      payment_reference: "Referencia de pago",

      print_save_pdf: "IMPRIMIR / GUARDAR PDF",
      close_tab: "CERRAR PESTAÑA",
      total_all_rooms: "TOTAL (TODAS LAS HABITACIONES)",
      stay_details_room: "DETALLES DE LA ESTANCIA — HABITACIÓN {{n}}",
      room_details_dates: "Fechas",
      room_details_guests: "Huéspedes",
      room_details_category: "Categoría",
      room_details_rate: "Tarifa",
      room_details_total: "TOTAL",
      room_details_total_value: "Total: {{currency}}{{total}}",
      room_details_avg_per_night: "{{currency}}{{avg}} / noche",
      room_details_change: "Cambiar",
      room_details_remove: "Eliminar",
      no_rooms_for_dates: "No hay habitaciones disponibles para esas fechas.",
      no_rooms_for_dates_combination: "No hay disponibilidad para {{adults}} adultos y {{children}} ninos en estas fechas.",
      no_rooms_for_dates_help: "Prueba otras fechas con la misma duracion de estancia.",
      no_rooms_for_dates_suggestions_title: "Fechas disponibles mas cercanas",
      no_rooms_for_dates_suggestions_loading: "Buscando fechas disponibles cercanas...",
      no_rooms_for_dates_no_suggestions: "No se encontraron alternativas cercanas.",
      no_rooms_for_dates_apply: "Usar estas fechas",
      no_rooms_for_dates_reason_capacity: "Esta combinacion de huespedes no existe en este hotel.",
      hotel_max_occupancy_hint: "Nuestras habitaciones no pueden alojar mas de {{max}} personas en total.",
      active: "ACTIVA",
      back: "ATRÁS",
      continue: "CONTINUAR",
      select_date_one: "Selecciona fecha",
      adults: "Adultos",
      children: "Niños",
      add_room_title: "Añadir otra habitación",
      add_room_subtitle: "Hemos rellenado en base a tu estancia anterior. Cámbiala si lo necesitas.",
      close: "CERRAR",
      stay_details: "DETALLES DE LA ESTANCIA",
      change: "Cambiar",
      adults_label: "Adultos",
      children_label: "Niños",
      confirm_choose_room: "CONFIRMAR Y ELEGIR HABITACIÓN",
      // ✅ CalendarModal (ES)
      calendar_select_checkin: "Selecciona fecha de entrada",
      calendar_select_checkout: "Selecciona fecha de salida",
      calendar_confirm_stay: "Confirma tu estancia",

      calendar_dow_mon: "Lun",
      calendar_dow_tue: "Mar",
      calendar_dow_wed: "Mié",
      calendar_dow_thu: "Jue",
      calendar_dow_fri: "Vie",
      calendar_dow_sat: "Sáb",
      calendar_dow_sun: "Dom",

      calendar_prev: "Anterior",
      calendar_next: "Siguiente",
      calendar_confirm_dates: "Confirmar fechas",
      room_details: "Detalles de la habitación",
      select_room: "Seleccionar habitación",
      available_rooms: "Habitaciones disponibles",
      room_details_select: "Seleccionar",
      room_details_total: "TOTAL",
      room_details_night: "noche",
      rate_select_select_rate: "Selecciona una tarifa",
      rate_select_category: "Categoría: ",
      rate_select_loading: "Cargando tarifas…",
      rate_select_no_rates: "No hay tarifas disponibles para estas fechas.",
      rate_select_selected_rate: "TARIFA SELECCIONADA",
      rate_select_total: "TOTAL",
      rate_select_confirm_rate: "CONFIRMAR TARIFA",
      rate_select_add_more_rooms: "Puedes añadir más habitaciones en el siguiente paso.",
      inline_rates_title: "Elige una tarifa",
      inline_rate_refundable: "Reembolsable",
      inline_rate_non_refundable: "No reembolsable",
      inline_rate_total_stay: "Total estancia",
      inline_rate_selecting: "Seleccionando…",
      inline_rate_select: "Seleccionar tarifa",
      inline_rate_penalty_percent: "Penalización de cancelación del {{amount}}%",
      inline_rate_penalty_fixed: "Penalización de cancelación de {{amount}}",
      inline_rate_no_policy: "Consulta las condiciones de reserva",
      inline_rate_selected: "Seleccionada",
      inline_rate_add: "Añadir habitación",
      inline_rate_remove: "Quitar habitación",
      inline_rate_sold_out: "No quedan más habitaciones para estas fechas.",
      inline_rate_last_room_added: "Has añadido la última habitación disponible para estas fechas.",
      inline_rate_cart_limit: "Has alcanzado el máximo de habitaciones para una reserva.",
      inline_rate_availability_missing: "No se ha podido confirmar la disponibilidad para todas las noches de la estancia.",
      inline_rate_quantity: "Habitaciones seleccionadas",
      inline_rate_view_conditions: "Ver condiciones",
      inline_rate_conditions_hint: "Pulsa el icono de información para ver las condiciones de cada tarifa.",
      inline_rate_conditions_title: "Condiciones de la tarifa",
      inline_rate_conditions_close: "Cerrar condiciones de la tarifa",
      inline_rate_conditions_refundability: "Reembolsabilidad",
      inline_rate_conditions_cancellation: "Política de cancelación",
      inline_rate_conditions_penalty: "Penalización por cancelación",
      inline_rate_conditions_no_policy: "No se ha especificado una política de cancelación para esta tarifa.",
      inline_rates_retry: "Volver a intentar",
      inline_rates_load_error: "No hemos podido cargar las tarifas disponibles. Inténtalo de nuevo.",
      inline_rate_unavailable: "Esta tarifa ya no está disponible. Hemos actualizado las opciones.",
      inline_rate_retry_conflict: "Las opciones han cambiado al mismo tiempo. Inténtalo de nuevo.",
      counter_decrease: "Reducir",
      counter_increase: "Aumentar",
      rate_row_selected: "SELECCIONADA",
      rate_row_select: "SELECCIONAR",
      rate_row_night: "noche",
      payment_setup_missing_group_id: "Falta el id del grupo de reserva",
      payment_setup_stripe_not_ready: "Stripe no está listo aún",
      payment_setup_enter_name: "Introduce el nombre del titular de la tarjeta.",
      payment_setup_card_element_not_found: "Elemento de tarjeta no encontrado",
      payment_setup_missing_client_secret: "Falta client_secret",
      payment_setup_card_confirmation_failed: "Confirmación de la tarjeta fallida",
      payment_setup_commit_failed: "No se pudo guardar la tarjeta",
      payment_details_title: "DETALLES DE PAGO",
      cardholder_name: "TITULAR DE LA TARJETA",
      mandatory: "OBLIGATORIO",
      name_on_card_placeholder: "Nombre en la tarjeta",
      card_details: "DATOS DE LA TARJETA",
      card_secure_helper:
        "Tu tarjeta se almacenará de forma segura. Se podrán aplicar cargos o preautorizaciones según las políticas de reserva.",
      back: "VOLVER",
      processing: "PROCESANDO...",
      pay_and_confirm_reservation: "CONFIRMAR CON GARANTÍA DE TARJETA",
      category: "Categoría",
      rate: "Tarifa",
      total: "Total",
      rate_select_night: "noche",
      invalid_email_format: "Email inválido. Debe tener formato tipo nombre@dominio.xx",
      amenities_label: "Amenidades",
      view_all_amenities: "Ver todas las amenidades",
      amenities_title: "AMENIDADES",
      room_details_from: "Desde",
      room_details_view_rates: "Ver tarifas",
      room_details_photo: "foto",
      room_details_photos: "fotos",
      room_photo_previous: "Foto anterior",
      room_photo_next: "Foto siguiente",
      room_photo_go_to: "Mostrar foto {{current}} de {{total}}",
      room_photo_open_gallery: "Abrir galería: {{room}}, foto {{current}} de {{total}}",
      room_features_title: "CARACTERÍSTICAS DE LA HABITACIÓN",
      room_view_label: "Vista",
      room_highlights_label: "Destacados",
      room_amenities_label: "Servicios",
      room_details_all: "Ver todos los detalles",
      room_details_empty: "Sin detalles de la habitación",
      view_all_room_features: "Ver todas las características",
      no_amenities: "Sin servicios destacados",
      taxes_including_prefix: "Incluye",
      taxes_not_including_any: "No incluye impuestos ni tasas",
      tax_label_vat: "IVA",
      tax_label_other_taxes: "otros impuestos",
      tax_label_fees: "tasas",
      tax_label_city_tax: "tasa turística",
      list_conjunction_and: "y",
      taxes_all_included: "Todos los impuestos y tasas incluidos",
      booking_terms_link: "Términos y condiciones de la reserva",

      cancel_title: "Cancelar reserva",
      cancel_intro: "Estás a punto de cancelar tu reserva.",
      cancel_reference: "Referencia",
      cancel_reason_label: "Motivo (opcional)",
      cancel_reason_placeholder: "Cuéntanos por qué cancelas (opcional)",
      cancel_button: "Cancelar reserva",
      cancel_missing_params: "Enlace de cancelación inválido. Contacta con el hotel.",
      cancel_submitting: "Cancelando...",
      cancel_success_title: "Reserva cancelada",
      cancel_success_body: "Tu reserva ha sido cancelada correctamente.",
      cancel_failed_generic: "No se pudo cancelar. Inténtalo de nuevo o contacta con el hotel.",
      payment_error_generic: "No se pudo iniciar el pago. Inténtalo de nuevo o contacta con el hotel.",
      taxes_vat: "IVA",
      taxes_other: "otros impuestos",
      taxes_fees: "tasas",
      taxes_city: "tasa turística",




    },
  };

  const t = (key, fallback) => I18N?.[lang]?.[key] ?? I18N?.en?.[key] ?? fallback ?? key;

  function getBookingEngineText(key) {
    const dict = bookingEngineTexts && typeof bookingEngineTexts === "object" ? bookingEngineTexts : {};
    const node = dict?.[key];

    // allow string or { en/es/... }
    if (typeof node === "string") return node.trim();

    const v = node?.[lang] ?? node?.en ?? node?.es ?? "";
    return (v != null && String(v).trim() !== "") ? String(v) : "";
  }



  const [calendarOpen, setCalendarOpen] = useState(false);

  // ✅ remember pax before changing dates (so we can restore automatically)
  const [paxBeforeDatesChange, setPaxBeforeDatesChange] = useState({
    adults: null,
    children: null,
  });

  function openCalendarRememberingPax() {
    setPaxBeforeDatesChange({ adults, children });
    setCalendarOpen(true);
  }
  const [checkIn, setCheckIn] = useState(bookingEntryParams.checkIn);
  const [checkOut, setCheckOut] = useState(bookingEntryParams.checkOut);
  const [adults, setAdults] = useState(bookingEntryParams.adults);
  const [children, setChildren] = useState(bookingEntryParams.children);
  const [adultsOpen, setAdultsOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [paxLimits, setPaxLimits] = useState(null);
  const [businessUnitId, setBusinessUnitId] = useState(null);

    // wizard
  const [step, setStep] = useState("search"); // search | categories | extras | guest_details | payment | confirmation | cancel

  // cancel (public)
  const [cancelReason, setCancelReason] = useState("");
  const [cancelStatus, setCancelStatus] = useState({ state: "idle", error: "" }); // idle | submitting | success | error

  const [confirmationData, setConfirmationData] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null);

  const [categories, setCategories] = useState(null); // array

  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesRefreshing, setCategoriesRefreshing] = useState(false);
  const [categoriesReloadKey, setCategoriesReloadKey] = useState(0); // forces refetch after PATCH
  const [cartReloadKey, setCartReloadKey] = useState(0);
  const [bookingCart, setBookingCart] = useState(() => normalizeBookingCartPayload(null));
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState(null);
  const [extrasCatalog, setExtrasCatalog] = useState(() => normalizeBookingExtrasCatalogPayload(null));
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [extrasError, setExtrasError] = useState(null);
  const [extrasReloadKey, setExtrasReloadKey] = useState(0);
  const [mutatingExtraKey, setMutatingExtraKey] = useState(null);
  const [shoppingError, setShoppingError] = useState(null);
  const [selectingRateId, setSelectingRateId] = useState(null);
  const [selectionError, setSelectionError] = useState(null);
  const [rateConditionsDialog, setRateConditionsDialog] = useState(null);
  const shoppingRequestSeqRef = useRef(0);
  const preserveCategoriesOnNextReloadRef = useRef(false);
  const cartRequestSeqRef = useRef(0);
  const extrasRequestSeqRef = useRef(0);
  const skipNextExtrasLoadRef = useRef(false);
  const extrasEmptyDestinationRef = useRef("guest_details");
  const extrasMutationInFlightRef = useRef(false);
  const selectRateInFlightRef = useRef(false);
  const [noAvailabilitySuggestions, setNoAvailabilitySuggestions] = useState([]);
  const [noAvailabilitySuggestionsLoading, setNoAvailabilitySuggestionsLoading] = useState(false);
  const [noAvailabilityEndpointUnsupported, setNoAvailabilityEndpointUnsupported] = useState(false);
  const [noAvailabilityMeta, setNoAvailabilityMeta] = useState({
    noCombinationInHotel: false,
    hotelMaxOccupancy: null,
  });
  const [applyingSuggestedDatesKey, setApplyingSuggestedDatesKey] = useState("");

  const pricedCategories = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    // Shopping deliberately keeps sold-out categories visible so that guests can
    // understand why the + control has become unavailable.
    return list
      .filter(Boolean)
      .map((category, originalIndex) => {
        const publishedRateTotals = (Array.isArray(category?.rates) ? category.rates : [])
          .map((rate) => Number(rate?.min_total))
          .filter((total) => Number.isFinite(total) && total >= 0);
        return {
          category,
          originalIndex,
          cheapestPublishedRate: publishedRateTotals.length
            ? Math.min(...publishedRateTotals)
            : Number.POSITIVE_INFINITY,
        };
      })
      .sort((left, right) => (
        left.cheapestPublishedRate - right.cheapestPublishedRate ||
        left.originalIndex - right.originalIndex
      ))
      .map(({ category }) => category);
  }, [categories]);

  const fallbackHotelMaxOccupancy = useMemo(() => {
    const toPosInt = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return null;
      return Math.trunc(n);
    };

    const candidates = [
      hotelContext?.max_occupancy,
      hotelContext?.maxOccupancy,
      hotelContext?.max_pax,
      hotelContext?.maxPax,
      hotelContext?.booking_engine_profile?.max_occupancy,
      hotelContext?.booking_engine_profile?.maxOccupancy,
      hotelContext?.booking_engine_profile?.max_pax,
      hotelContext?.booking_engine_profile?.maxPax,
      paxLimits?.max_occupancy,
    ];

    for (const c of candidates) {
      const n = toPosInt(c);
      if (n != null) return n;
    }
    return null;
  }, [hotelContext, paxLimits]);

  function getRoomCategoryDisplayName(category) {
    const id = category?.id != null ? Number(category.id) : null;
    const beCopy = id != null ? beRoomCategoryCopyMap?.[String(id)] : null;

    const beName =
      lang === "es"
        ? String(beCopy?.name_es || "").trim()
        : String(beCopy?.name_en || "").trim();

    if (beName) return beName;

    const fallback = String(category?.name || "").trim();
    return fallback || null;
  }

  const deletingDraftId = null;



//aquí comienzan los cambios para mejorar tarjetas - 16.01.2026
    // ✅ Draft “actual” (lo que antes venía en la URL)
  //02.02.2026 - token para protección
  const [draftCtx, setDraftCtx] = useState({
    reservation_group_id: null,
    reservation_draft_id: null,

    // ✅ public session (booking_public_sessions)
    public_session_token: null,
    public_session_expires_at: null,
  });
  // ✅ Rooms in this group (draft IDs) - UI list (kept in sync by this client)
  const [roomDraftIds, setRoomDraftIds] = useState([]); // [draftId1, draftId2, ...]

  // ✅ Per-room UI state (selection + stay details shown in the left cards)
  const [roomStateByDraftId, setRoomStateByDraftId] = useState({}); 
  // shape:
  // {
  //   [draftId]: {
  //     checkIn, checkOut, adults, children,
  //     selectedCategory, selectedRate
  //   }
  // }

  const cartItems = Array.isArray(bookingCart?.items) ? bookingCart.items : [];
  const cartItemCount = Number.isFinite(Number(bookingCart?.count))
    ? Number(bookingCart.count)
    : cartItems.length;
  const hasBookingSession =
    Number(draftCtx?.reservation_group_id) > 0 &&
    String(draftCtx?.public_session_token || "").trim().length > 0;
  const bookingEngineEntryMode =
    hotelContext?.booking_engine_entry_mode === "widget" ? "widget" : "hosted";
  const showBookingHeroOpeningMessage =
    !hasBookingSession && step === "search" && bookingEngineEntryMode !== "widget";
  const activeHeroBackgroundUrl = isBookingHeroBackgroundEnabled(
    hotelContext?.booking_engine_hero_background_screens,
    step,
    bookingEngineEntryMode
  )
    ? hotelContext?.hero_background_url || null
    : null;
  const shoppingCriteria = useMemo(() => ({
    check_in_date: String(checkIn || "").trim(),
    check_out_date: String(checkOut || "").trim(),
    adults: Number(adults || 0),
    children: Number(children || 0),
    babies: 0,
    display_currency: bookingCurrency,
  }), [checkIn, checkOut, adults, children, bookingCurrency]);

  const applyCanonicalBookingCart = useCallback((payload) => {
    const nextCart = normalizeBookingCartPayload(payload, bookingCurrency);
    const ui = bookingCartToRoomUi(nextCart);
    const numericDraftIds = ui.roomDraftIds
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0);

    setBookingCart(nextCart);
    setRoomDraftIds(ui.roomDraftIds);
    setRoomStateByDraftId(ui.roomStateByDraftId);
    setDraftCtx((current) => {
      const activeId = Number(current?.reservation_draft_id);
      const nextActiveId = numericDraftIds.includes(activeId)
        ? activeId
        : (numericDraftIds[0] || null);
      return { ...current, reservation_draft_id: nextActiveId };
    });

    return nextCart;
  }, [bookingCurrency]);

  //16.01.2026 - para el booker en el booking engine

  // ✅ step solo fuerza "search" cuando NO hay draft
  // ✅ y SOLO empuja a "categories" si aún estabas en "search"
  // (no pisa "guest_details")

//17.01.2026 - añadiendo special requests
  const [guestForm, setGuestForm] = useState({
    gender: "",

    first_name: "",
    last_name: "",

    email: "",
    phone: "",

    address: "",
    city: "",
    postal_code: "",
    country: "",

    birth_date: "",
    id_type: "",
    id_number: "",

    // ✅ per-room special requests (key = draftId)
    special_requests_by_room: {},
  });

    // ✅ Payment setup-intent bootstrap (must happen BEFORE Stripe.js confirm)
  const [paymentSetup, setPaymentSetup] = useState(null); // { client_secret, setup_intent_id, stripe_account_id }
  const [paymentSetupErr, setPaymentSetupErr] = useState(null);

  useEffect(() => {
    // Create SetupIntent when entering payment step so we can mount Elements with the correct stripe_account_id.
    const hasDraftNow = !!draftCtx?.reservation_draft_id;
    if (!hasDraftNow) return;
    if (step !== "payment") return;
    if (!cardRequired) return;

    const reservationGroupId = Number(draftCtx?.reservation_group_id);
    if (!Number.isFinite(reservationGroupId) || !reservationGroupId) return;

    const token = String(draftCtx?.public_session_token || "").trim();
    if (!token) {
      //console.error("[BOOKING][PAYMENT][BOOTSTRAP][ABORT] Missing public_session_token");
      setPaymentSetupErr("Missing public session token");
      return;
    }

    // If we already have a setup for this group, do not recreate it.
    if (paymentSetup?.reservation_group_id === reservationGroupId && paymentSetup?.client_secret) {
      return;
    }

    setPaymentSetup(null);
    setPaymentSetupErr(null);

    const apiUrl = process.env.REACT_APP_API_URL;

    (async () => {
      try {
        /*console.log("[BOOKING][PAYMENT][BOOTSTRAP][START]", {
          reservation_group_id: reservationGroupId,
          business_unit_id: hotelContext?.business_unit_id ?? businessUnitId ?? null,
          has_token: true,
          token_length: token.length,
        });*/

        const resp = await axios.post(
          `${apiUrl}/api/public/stripe/setup-intents`,
          {
            reservation_group_draft_id: reservationGroupId,
            business_unit_id:
              (hotelContext?.business_unit_id ?? businessUnitId) != null
                ? Number(hotelContext?.business_unit_id ?? businessUnitId)
                : null,
            billing_details: {
              name: `${guestForm.first_name || ""} ${guestForm.last_name || ""}`.trim() || null,
              email: (guestForm.email || "").trim() || null,
              phone: (guestForm.phone || "").trim() || null,
            },
          },
          { headers: { "x-public-session-token": token } }
        );

        const data = resp?.data || {};
        const payload =
          (data && typeof data === "object" && data.data && typeof data.data === "object")
            ? data.data
            : data;

        // ✅ MAKE IT READABLE (no more "Object")
        //console.log("[BOOKING][PAYMENT][BOOTSTRAP][RESP_STATUS]", resp?.status);
        //console.log("[BOOKING][PAYMENT][BOOTSTRAP][RESP_DATA]", JSON.stringify(data, null, 2));

        // ✅ If backend returned an error shape, stop here (do NOT treat as OK)
        if (payload?.error) {
          //console.error("[BOOKING][PAYMENT][BOOTSTRAP][API_ERROR]", payload);
          setPaymentSetupErr(String(payload.error || "Unable to start payment"));
          return;
        }

        const client_secret = payload?.client_secret || null;
        const setup_intent_id = payload?.setup_intent_id || null;
        const stripe_account_id = String(payload?.stripe_account_id || "").trim() || null;

        /*console.log("[BOOKING][PAYMENT][BOOTSTRAP][PARSED]", {
          has_client_secret: !!client_secret,
          setup_intent_id,
          stripe_account_id,
          payload_keys: Object.keys(payload || {}),
        });*/



        if (!stripe_account_id) {
          //console.error("[BOOKING][PAYMENT][BOOTSTRAP][ERROR] Missing stripe_account_id", data);
          setPaymentSetupErr("Missing stripe connected account id");
          return;
        }
        if (!client_secret) {
          //console.error("[BOOKING][PAYMENT][BOOTSTRAP][ERROR] Missing client_secret", data);
          setPaymentSetupErr("Missing client_secret");
          return;
        }

        /*console.log("[BOOKING][PAYMENT][BOOTSTRAP][OK]", {
          setup_intent_id,
          stripe_account_id,
        });*/

        setPaymentSetup({
          reservation_group_id: reservationGroupId,
          client_secret,
          setup_intent_id,
          stripe_account_id,
        });
      } catch (e) {
        if (e?.response?.data?.code === "BOOKING_CARD_NOT_REQUIRED") {
          setHotelContext((current) => ({ ...current, booking_engine_card_policy: "not_required" }));
          setStep("guest_details");
          setGuestError(t("booking_card_policy_changed"));
          return;
        }
        /*console.error("[BOOKING][PAYMENT][BOOTSTRAP][ERROR]", {
          message: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });*/
        setPaymentSetupErr(e?.response?.data?.error || "Unable to start payment");
      }
    })();
    }, [
    step,
    cardRequired,
    draftCtx?.reservation_draft_id,
    draftCtx?.reservation_group_id,
    draftCtx?.public_session_token,
    hotelContext?.business_unit_id,
    businessUnitId,
    guestForm.first_name,
    guestForm.last_name,
    guestForm.email,
    guestForm.phone,
    paymentSetup?.reservation_group_id,
    paymentSetup?.client_secret,
  ]);

  const stripePromise = useMemo(() => {
    if (!cardRequired) return null;
    const acct = String(paymentSetup?.stripe_account_id || "").trim();
    if (!acct) return null;

    return loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: acct,
    });
  }, [cardRequired, paymentSetup?.stripe_account_id]);


  const [guestSaving, setGuestSaving] = useState(false);
  const [guestError, setGuestError] = useState(null);
  const guestSubmitInFlightRef = useRef(false);

  const getTermsAcceptanceEvidenceCopy = useCallback((currentLang) => {
    const isEs = String(currentLang || "").toLowerCase().startsWith("es");
    if (isEs) {
      return {
        checkbox_text:
          "Al completar esta reserva, estoy de acuerdo con los términos y condiciones de la misma.",
        terms_link_label: "términos y condiciones",
        required_notice: "Debes aceptarlos para continuar.",
      };
    }

    return {
      checkbox_text: "By completing this booking, I agree to the terms & conditions.",
      terms_link_label: "terms & conditions",
      required_notice: "You must accept them to continue.",
    };
  }, []);

  const recordTermsAcceptanceEvidence = useCallback(async (groupId, publicSessionToken, accepted = true) => {
    try {
      const apiBase = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
      const cleanSlug = String(slug || "").trim();
      const token = String(publicSessionToken || "").trim();
      const draftGroupId = Number(groupId);

      if (!cleanSlug || !token || !Number.isFinite(draftGroupId) || draftGroupId <= 0) {
        return false;
      }

      const copy = getTermsAcceptanceEvidenceCopy(lang);
      const payload = {
        reservation_group_draft_id: draftGroupId,
        public_session_token: token,
        accepted: accepted !== false,
        language: lang,
        checkbox_text: copy.checkbox_text,
        terms_link_label: copy.terms_link_label,
        required_notice: copy.required_notice,
        client_recorded_at: new Date().toISOString(),
      };

      const url = `${apiBase}/api/public/booking/terms-acceptance/${encodeURIComponent(cleanSlug)}`;

      // Keep the existing best-effort audit contract; do not make its failure
      // an additional booking requirement. Bound the wait before final commit.
      const response = await axios.post(url, payload, { timeout: 5000 });
      return response.data?.recorded === true;
    } catch (e) {
      return false;
    }
  }, [getTermsAcceptanceEvidenceCopy, lang, slug]);

  function handleTermsAcceptanceChange(e) {
    const nextAccepted = !!e.target.checked;
    setAcceptTerms(nextAccepted);

    if (!nextAccepted) {
      termsAcceptancePaymentEvidenceKeyRef.current = null;
      recordTermsAcceptanceEvidence(
        draftCtx?.reservation_group_id,
        draftCtx?.public_session_token,
        false
      );
    }
  }

  useEffect(() => {
    if (step !== "payment") return;
    if (!acceptTerms) return;

    const groupId = Number(draftCtx?.reservation_group_id || 0);
    const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
    if (!Number.isFinite(groupId) || groupId <= 0 || !publicSessionToken) return;

    const evidenceKey = `${String(slug || "").trim()}:${groupId}:${publicSessionToken}:accepted`;
    if (termsAcceptancePaymentEvidenceKeyRef.current === evidenceKey) return;

    termsAcceptancePaymentEvidenceKeyRef.current = evidenceKey;
    recordTermsAcceptanceEvidence(groupId, publicSessionToken, true);
  }, [
    acceptTerms,
    draftCtx?.public_session_token,
    draftCtx?.reservation_group_id,
    recordTermsAcceptanceEvidence,
    slug,
    step,
  ]);

  

  async function submitGuestDetails() {
    if (guestSubmitInFlightRef.current) return;
    setGuestError(null);
    const groupId = Number(draftCtx.reservation_group_id);
    const business_unit_id = hotelContext?.business_unit_id ?? businessUnitId ?? null;

    const first_name = String(guestForm.first_name || "").trim();
    const last_name = String(guestForm.last_name || "").trim();
    const email = String(guestForm.email || "").trim();
    const phone = String(guestForm.phone || "").trim();

    if (!groupId) {
      alert(t("missing_group_id"));
      return;
    }
    if (!business_unit_id) {
      alert(t("missing_business_unit"));
      return;
    }
    if (!first_name) {
      alert(t("enter_first_name"));
      return;
    }
    if (!last_name) {
      alert(t("enter_last_name"));
      return;
    }
    if (!email) {
      alert(t("enter_email"));
      return;
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    if (!emailOk) {
      alert(t("invalid_email_format"));
      return;
    }
    if (!phone) {
      alert(t("enter_phone"));
      return;
    }

    if (!acceptTerms) {
      setGuestError(t("booking_terms_required"));
      return;
    }


    guestSubmitInFlightRef.current = true;
    try {
      setGuestSaving(true);

      const public_session_token = String(draftCtx?.public_session_token || "").trim();
      if (!public_session_token) {
        /*console.error("[BOOKING][GUEST_DETAILS][ERROR] Missing public_session_token (guest details flow)", {
          reservation_group_id: groupId,
          business_unit_id,
        });*/
        alert(t("unable_save_guest_details"));
        return;
      }

      /*console.log("[BOOKING][GUEST_DETAILS][CREATE_CUSTOMER][START]", {
        reservation_group_id: groupId,
        business_unit_id,
        first_name,
        last_name,
        email,
        phone,
        has_token: true,
        token_len: public_session_token.length,
      });*/

      // 1) ✅ Create customer (booker) (protected)
      const country = String(guestForm.country || "").trim();
      const id_type = String(guestForm.id_type || "").trim();
      const id_number = String(guestForm.id_number || "").trim();
      const address = String(guestForm.address || "").trim();
      const birth_date = String(guestForm.birth_date || "").trim();
      const postal_code = String(guestForm.postal_code || "").trim();
      const city = String(guestForm.city || "").trim();
      const gender = String(guestForm.gender || "").trim();

      const createRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/customers2`,
        {
          business_unit_id: Number(business_unit_id),
          first_name,
          last_name,
          email,
          phone,

          // optional (send null when empty)
          country: country || null,
          id_type: id_type || null,
          id_number: id_number || null,
          address: address || null,
          birth_date: birth_date || null,
          postal_code: postal_code || null,
          city: city || null,
          gender: gender || null,

          created_by: "PublicBookingGuestDetails",
        },
        {
          headers: { "x-public-session-token": public_session_token },
        }
      );

      const newCustomerId =
        createRes.data?.id ??
        createRes.data?.customer?.id ??
        createRes.data?.customer_id ??
        null;

      if (!newCustomerId) {
        //console.error("[BOOKING][GUEST_DETAILS][CREATE_CUSTOMER][ERROR] Missing customer id", createRes.data);
        alert(t("unable_create_customer"));
        return;
      }

      //console.log("[BOOKING][GUEST_DETAILS][CREATE_CUSTOMER][OK]", { customer_id: newCustomerId });

      // 2) ✅ Assign booker to reservation group (protected)
      /*console.log("[BOOKING][GUEST_DETAILS][SET_BOOKER][START]", {
        reservation_group_id: groupId,
        booker_id: newCustomerId,
      });*/

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups2/${groupId}/booker`,
        {
          reservation_group_id: groupId,
          booker_id: Number(newCustomerId),
          updated_by: "PublicBookingSetBooker",
        },
        { headers: { "x-public-session-token": public_session_token } }
      );

      /*console.log("[BOOKING][GUEST_DETAILS][SET_BOOKER][OK]", {
        reservation_group_id: groupId,
        booker_id: newCustomerId,
      });*/

      // 3) ✅ Save special requests per room (per draft) (protected)
      const ids = Array.isArray(roomDraftIds) ? roomDraftIds.map(Number) : [];
      const reqMap = guestForm?.special_requests_by_room || {};

      for (let i = 0; i < ids.length; i++) {
        const draftId = ids[i];
        const text = String(reqMap?.[draftId] || "").trim();

        /*console.log("[BOOKING][GUEST_DETAILS][SPECIAL_REQUESTS][PATCH_START]", {
          reservation_draft_id: draftId,
          special_requests_len: text.length,
        });*/

        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/reservation-drafts2/${draftId}`,
          {
            special_requests: text || null,
            updated_by: "PublicBookingSpecialRequests",
          },
          {
            headers: { "x-public-session-token": public_session_token },
          }
        );

        /*console.log("[BOOKING][GUEST_DETAILS][SPECIAL_REQUESTS][PATCH_OK]", {
          reservation_draft_id: draftId,
        });*/
      }

      await recordTermsAcceptanceEvidence(groupId, public_session_token, true);

      if (cardRequired) {
        setStep("payment");
      } else {
        const confirmation = await commitBookingDraftGroup({
          apiUrl: process.env.REACT_APP_API_URL,
          groupId,
          publicSessionToken: public_session_token,
          language: lang,
          currencyCode: bookingCurrency,
          fxRateSetId: currencyConfiguration?.current_rate_set_id,
          updatedBy: "PublicBookingGuestDetailsCommit",
        });
        setPaymentResult(confirmation);
        setStep("confirmation");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });

    } catch (err) {
      /*console.error("[BOOKING][GUEST_DETAILS][ERROR]", {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
      });*/
      const code = err?.response?.data?.code;
      if (["SETUP_INTENT_NOT_CONFIRMED", "PAYMENT_METHOD_NOT_CONFIRMED"].includes(code)) {
        setHotelContext((current) => ({ ...current, booking_engine_card_policy: "required" }));
        setGuestError(t("booking_card_policy_changed"));
      } else {
        setGuestError(t(code === "COMMIT_INSUFFICIENT_AVAILABILITY"
          ? "booking_confirm_availability" : "booking_confirm_error"));
      }
    } finally {
      guestSubmitInFlightRef.current = false;
      setGuestSaving(false);
    }
  }

  useEffect(() => {
    if (!draftCtx.reservation_group_id || !draftCtx.public_session_token) {
      setStep("search");
      return;
    }
    setStep((prev) => (prev === "search" ? "categories" : prev));
  }, [draftCtx.reservation_group_id, draftCtx.public_session_token]);

  useEffect(() => {
    const ids = Array.isArray(roomDraftIds) ? roomDraftIds.map(Number) : [];
    if (!ids.length) return;

    setGuestForm((p) => {
      const prevMap = (p && p.special_requests_by_room) ? p.special_requests_by_room : {};
      const nextMap = { ...prevMap };

      // ✅ crea keys nuevas (sin borrar lo que el user ya escribió)
      for (const id of ids) {
        if (nextMap[id] === undefined) nextMap[id] = "";
      }

      return { ...p, special_requests_by_room: nextMap };
    });
  }, [roomDraftIds]);
//21.01.2026 - para imágenes en las cards de room categories
  useEffect(() => {
  async function loadContext() {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/public/booking/context/${slug}`
    );

    //console.log("[BOOKING][CONTEXT][RAW_KEYS]", Object.keys(res.data || {}));
    //console.log("[BOOKING][CONTEXT][RAW]", res.data);

    // Try a few common shapes without assuming the exact one.
    const buId =
      res.data?.business_unit_id ??
      res.data?.businessUnitId ??
      res.data?.business_unit?.id ??
      res.data?.business_unit?.business_unit_id ??
      null;

    setHotelContext(res.data);
    setBusinessUnitId(buId);

    setPaxLimits({
      max_adults: res.data.max_adults,
      max_children: res.data.max_children,
    });

    // ✅ Load room category assets map (covers + galleries)
    try {
      const assetsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/public/booking/room-category-assets/${slug}`
      );

      //console.log("[BOOKING][ROOM_CATEGORY_ASSETS][RAW_KEYS]", Object.keys(assetsRes.data || {}));
      //console.log("[BOOKING][ROOM_CATEGORY_ASSETS][RAW]", assetsRes.data);

         setRoomCategoryAssetsMap(assetsRes.data?.room_category_assets || {});
    } catch (e) {
      //console.warn("[BOOKING][ROOM_CATEGORY_ASSETS][ERROR] Failed to load", e);
      setRoomCategoryAssetsMap({});
    }

      // ✅ Load room category amenities map
      try {
        const amRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/public/booking/room-category-amenities/${slug}`,
          { params: { v: "room-details-2" } }
        );

      //console.log("[BOOKING][ROOM_CATEGORY_AMENITIES][RAW_KEYS]", Object.keys(amRes.data || {}));
      //console.log("[BOOKING][ROOM_CATEGORY_AMENITIES][RAW]", amRes.data);

        setRoomCategoryAmenitiesMap(amRes.data?.room_category_amenities || {});
      } catch (e) {
        //console.warn("[BOOKING][ROOM_CATEGORY_AMENITIES][ERROR] Failed to load", e);
        setRoomCategoryAmenitiesMap({});
      }

      // ✅ Booking Engine commercial copy (room-category specific, i18n)
      // Prefer public endpoint (if exists), fallback to private endpoint when running inside admin (JWT present).
      try {
        setBeRoomCategoryCopyMap({});

        const copyRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/public/booking/room-category-copy/${slug}`
        );

        const rows =
          Array.isArray(copyRes.data?.rows) ? copyRes.data.rows :
          Array.isArray(copyRes.data?.room_category_copy) ? copyRes.data.room_category_copy :
          Array.isArray(copyRes.data) ? copyRes.data :
          [];

        const map = {};
        for (const r of rows) {
          const rcId = Number(r?.room_category_id);
          if (!rcId) continue;
          map[String(rcId)] = r;
        }

        setBeRoomCategoryCopyMap(map);
      } catch (e) {
        try {
          const token =
            typeof window !== "undefined" && window?.localStorage
              ? String(localStorage.getItem("palantir_token") || "").trim()
              : "";

          if (!token || !buId) {
            setBeRoomCategoryCopyMap({});
            return;
          }

          const privateRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/booking_engine_room_categories/${buId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const rows = Array.isArray(privateRes.data) ? privateRes.data : [];
          const map = {};
          for (const r of rows) {
            const rcId = Number(r?.room_category_id);
            if (!rcId) continue;
            map[String(rcId)] = r;
          }

          setBeRoomCategoryCopyMap(map);
        } catch (e2) {
          setBeRoomCategoryCopyMap({});
        }
      }
    }


  loadContext();
}, [slug]);


  useEffect(() => {
  async function loadBookingEngineTexts() {
    const buId =
      hotelContext?.business_unit_id ??
      businessUnitId ??
      null;

    if (!buId) return;

    try {
      //console.log("[BOOKING][TEXTS][LOAD][START]", { business_unit_id: buId });

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/hotel-settings/public/booking-engine-texts`,
        { params: { business_unit_id: buId } }
      );

      const texts = res.data?.booking_engine_texts ?? {};
      //console.log("[BOOKING][TEXTS][LOAD][OK]", { keys: Object.keys(texts || {}) });

      setBookingEngineTexts(texts && typeof texts === "object" ? texts : {});
    } catch (e) {
      /*console.error("[BOOKING][TEXTS][LOAD][ERROR]", {
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
      });*/
      setBookingEngineTexts({}); // ✅ never undefined
    }
  }

  loadBookingEngineTexts();
}, [hotelContext?.business_unit_id, businessUnitId]);



useEffect(() => {
  setSelectionError(null);
}, [draftCtx.reservation_group_id, checkIn, checkOut, adults, children]);

useEffect(() => {
  let cancelled = false;
  const requestSeq = cartRequestSeqRef.current + 1;
  cartRequestSeqRef.current = requestSeq;

  async function loadCart() {
    const groupId = Number(draftCtx?.reservation_group_id || 0);
    const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
    if (!groupId || !publicSessionToken) return;

    setCartLoading(true);
    setCartError(null);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups2/${groupId}/cart`,
        {
          headers: { "x-public-session-token": publicSessionToken },
          params: { display_currency: bookingCurrency },
        }
      );
      if (!cancelled && cartRequestSeqRef.current === requestSeq) {
        applyCanonicalBookingCart(data);
      }
    } catch (error) {
      if (!cancelled && cartRequestSeqRef.current === requestSeq) {
        setCartError("unable_load_rooms");
      }
    } finally {
      if (!cancelled && cartRequestSeqRef.current === requestSeq) {
        setCartLoading(false);
      }
    }
  }

  loadCart();
  return () => {
    cancelled = true;
  };
}, [
  draftCtx?.reservation_group_id,
  draftCtx?.public_session_token,
  bookingCurrency,
  cartReloadKey,
  applyCanonicalBookingCart,
]);

useEffect(() => {
  let cancelled = false;
  const requestSeq = extrasRequestSeqRef.current + 1;
  extrasRequestSeqRef.current = requestSeq;

  async function loadExtrasCatalog() {
    const groupId = Number(draftCtx?.reservation_group_id || 0);
    const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
    if (step !== "extras" || !groupId || !publicSessionToken) return;
    if (skipNextExtrasLoadRef.current) {
      skipNextExtrasLoadRef.current = false;
      setExtrasLoading(false);
      return;
    }

    setExtrasLoading(true);
    setExtrasError(null);
    extrasMutationInFlightRef.current = true;
    cartRequestSeqRef.current += 1;
    setCartLoading(false);
    setMutatingExtraKey("reconcile");
    try {
      // Every read is reconciled through the absolute v2 mutation. That both
      // materializes automatic/mandatory rules and removes stale room intent.
      const nextCatalog = await requestReconciledBookingExtrasCatalog({
        apiUrl: process.env.REACT_APP_API_URL,
        groupId,
        publicSessionToken,
        displayCurrency: bookingCurrency,
        isCurrent: () => !cancelled && extrasRequestSeqRef.current === requestSeq,
      });
      if (!nextCatalog) return;

      setExtrasCatalog(nextCatalog);
      if (nextCatalog.cart) applyCanonicalBookingCart(nextCatalog.cart);
      if (!hasBookableExtras(nextCatalog)) {
        const emptyDestination =
          extrasEmptyDestinationRef.current === "categories"
            ? "categories"
            : "guest_details";
        extrasMutationInFlightRef.current = false;
        setMutatingExtraKey(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
        setStep(emptyDestination);
        setExtrasLoading(false);
      }
    } catch (_error) {
      if (!cancelled && extrasRequestSeqRef.current === requestSeq) {
        setExtrasError("extras_load_error");
      }
    } finally {
      extrasMutationInFlightRef.current = false;
      setMutatingExtraKey(null);
      if (!cancelled && extrasRequestSeqRef.current === requestSeq) {
        setExtrasLoading(false);
      }
    }
  }

  loadExtrasCatalog();
  return () => {
    cancelled = true;
  };
}, [
  step,
  draftCtx?.reservation_group_id,
  draftCtx?.public_session_token,
  bookingCurrency,
  extrasReloadKey,
  applyCanonicalBookingCart,
]);

useEffect(() => {
  let cancelled = false;
  const requestSeq = shoppingRequestSeqRef.current + 1;
  shoppingRequestSeqRef.current = requestSeq;
  const preserveCurrentCategories = preserveCategoriesOnNextReloadRef.current;
  preserveCategoriesOnNextReloadRef.current = false;

  async function loadShopping() {
    const groupId = Number(draftCtx.reservation_group_id);
    const ci = String(shoppingCriteria.check_in_date || "").trim();
    const co = String(shoppingCriteria.check_out_date || "").trim();
    if (!groupId || !ci || !co) return;

    if (preserveCurrentCategories) {
      setCategoriesRefreshing(true);
    } else {
      setCategoriesRefreshing(false);
      setCategoriesLoading(true);
      setCategories(null);
    }
    setShoppingError(null);

    try {
      const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
      if (!publicSessionToken) {
        throw new Error("missing_public_session");
      }

      const shoppingRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups2/${groupId}/shopping`,
        {
          headers: { "x-public-session-token": publicSessionToken },
          params: shoppingCriteria,
        }
      );

      if (cancelled || shoppingRequestSeqRef.current !== requestSeq) return;

      const payload = unwrapBookingApiPayload(shoppingRes.data);
      const payloadCurrency = payload.currency || bookingCurrency;
      const shoppingCategories = Array.isArray(payload.categories) ? payload.categories : [];
      const normalizedCategories = shoppingCategories.map((category) => {
        const availableToAddRaw =
          category?.available_to_add ?? category?.available_rooms ?? category?.availability ?? null;
        const availableToAdd = Number(availableToAddRaw);
        const categoryAvailable = category?.is_available != null
          ? category.is_available === true
          : (!Number.isFinite(availableToAdd) || availableToAdd > 0);
        const categoryRates = Array.isArray(category?.rates)
          ? category.rates
          : (Array.isArray(category?.room_rates) ? category.room_rates : []);
        const rates = categoryRates.map((rate) => {
          const quantity = Math.max(0, Math.trunc(Number(
            rate?.selected_quantity ?? rate?.quantity ?? rate?.cart_quantity ?? 0
          ) || 0));
          const rateAvailable = rate?.is_available != null
            ? rate.is_available === true
            : categoryAvailable;
          const canIncrement = rate?.can_increment != null
            ? rate.can_increment === true
            : rateAvailable && categoryAvailable;
          const fallbackSelectionKey = [
            shoppingCriteria.check_in_date,
            shoppingCriteria.check_out_date,
            shoppingCriteria.adults,
            shoppingCriteria.children,
            category?.id,
            rate?.id,
          ].join(":");
          return {
            ...rate,
            selection_key: String(rate?.selection_key || fallbackSelectionKey),
            selected_quantity: quantity,
            selected: quantity > 0,
            is_available: rateAvailable,
            can_increment: canIncrement,
            min_total: rate?.min_total ?? rate?.total ?? null,
            min_avg_night: rate?.min_avg_night ?? rate?.avg_night ?? null,
            base_min_total: rate?.base_min_total ?? rate?.base_total ?? rate?.min_total ?? rate?.total ?? null,
            base_min_avg_night:
              rate?.base_min_avg_night ?? rate?.base_avg_night ?? rate?.min_avg_night ?? rate?.avg_night ?? null,
            nightly_prices: rate?.nightly_prices ?? rate?.nightly ?? [],
            currency: rate?.currency || payloadCurrency,
          };
        });
        const totals = rates
          .map((rate) => Number(rate?.min_total))
          .filter((value) => Number.isFinite(value));
        const averages = rates
          .map((rate) => Number(rate?.min_avg_night))
          .filter((value) => Number.isFinite(value));

        return {
          ...category,
          rates,
          available_to_add: Number.isFinite(availableToAdd) ? Math.max(0, Math.trunc(availableToAdd)) : null,
          is_available: categoryAvailable,
          from_total:
            category?.min_total ?? category?.from_total ?? (totals.length ? Math.min(...totals) : null),
          from_avg_night:
            category?.min_avg_night ?? category?.from_avg_night ?? (averages.length ? Math.min(...averages) : null),
          currency: category?.currency || payloadCurrency,
        };
      });

      setCategories(normalizedCategories);
    } catch (e) {
      if (cancelled || shoppingRequestSeqRef.current !== requestSeq) return;
      if (!preserveCurrentCategories) {
        setCategories([]);
        setShoppingError("inline_rates_load_error");
      }
    } finally {
      if (!cancelled && shoppingRequestSeqRef.current === requestSeq) {
        if (preserveCurrentCategories) {
          setCategoriesRefreshing(false);
        } else {
          setCategoriesLoading(false);
        }
      }
    }
  }

  loadShopping();
  return () => {
    cancelled = true;
  };
}, [
  draftCtx.reservation_group_id,
  draftCtx.public_session_token,
  shoppingCriteria,
  categoriesReloadKey,
  bookingCurrency,
]);

useEffect(() => {
  const hasNoAvailability =
    !categoriesLoading &&
    !shoppingError &&
    Array.isArray(categories) &&
    pricedCategories.length === 0;

  if (!hasNoAvailability) {
    setNoAvailabilitySuggestions([]);
    setNoAvailabilitySuggestionsLoading(false);
    setNoAvailabilityMeta({
      noCombinationInHotel: false,
      hotelMaxOccupancy: null,
    });
    return;
  }

  const draftId = Number(draftCtx?.reservation_draft_id || 0);
  const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
  const ci = String(checkIn || "").trim();
  const co = String(checkOut || "").trim();

  if (!draftId || !publicSessionToken || !ci || !co) return;
  if (noAvailabilityEndpointUnsupported) return;

  const roomNights = (() => {
    try {
      const a = new Date(`${ci}T00:00:00`);
      const b = new Date(`${co}T00:00:00`);
      const diff = Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
      return Number.isFinite(diff) && diff > 0 ? diff : null;
    } catch {
      return null;
    }
  })();

  if (!roomNights) return;

  let isCancelled = false;

  (async () => {
    setNoAvailabilitySuggestionsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservation-drafts2/${draftId}/alternative-dates`,
        {
          params: {
            check_in_date: ci,
            check_out_date: co,
            adults: Number(adults || 0),
            children: Number(children || 0),
            room_nights: roomNights,
            limit: 6,
          },
          headers: { "x-public-session-token": publicSessionToken },
        }
      );

      if (isCancelled) return;

      const payload =
        res?.data && typeof res.data === "object" && res.data?.data && typeof res.data.data === "object"
          ? res.data.data
          : (res?.data || {});

      const rows =
        Array.isArray(payload?.suggested_dates) ? payload.suggested_dates :
        Array.isArray(payload?.alternative_dates) ? payload.alternative_dates :
        Array.isArray(payload?.dates) ? payload.dates :
        [];

      const seen = new Set();
      const normalized = rows
        .map((r) => {
          const nextCheckIn = String(
            r?.check_in_date ?? r?.check_in ?? r?.start_date ?? r?.from ?? ""
          ).trim();
          const nextCheckOut = String(
            r?.check_out_date ?? r?.check_out ?? r?.end_date ?? r?.to ?? ""
          ).trim();

          if (!/^\d{4}-\d{2}-\d{2}$/.test(nextCheckIn)) return null;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(nextCheckOut)) return null;

          const key = `${nextCheckIn}|${nextCheckOut}`;
          if (seen.has(key)) return null;
          seen.add(key);

          return {
            checkIn: nextCheckIn,
            checkOut: nextCheckOut,
            key,
          };
        })
        .filter(Boolean)
        .slice(0, 6);

      const maxOccRaw =
        payload?.hotel_max_occupancy ??
        payload?.hotelMaxOccupancy ??
        payload?.max_occupancy ??
        null;
      const maxOcc = Number(maxOccRaw);
      const hotelMaxOccupancy =
        Number.isFinite(maxOcc) && maxOcc > 0 ? Math.trunc(maxOcc) : null;

      const noCombinationInHotel =
        payload?.no_combination_in_hotel === true ||
        String(payload?.reason || "").trim() === "no_combination_in_hotel";

      setNoAvailabilitySuggestions(normalized);
      setNoAvailabilityMeta({
        noCombinationInHotel,
        hotelMaxOccupancy,
      });
    } catch (e) {
      if (isCancelled) return;
      const status = Number(e?.response?.status || 0);
      if (status === 404 || status === 405) {
        setNoAvailabilityEndpointUnsupported(true);
      }
      setNoAvailabilitySuggestions([]);
      setNoAvailabilityMeta({
        noCombinationInHotel: false,
        hotelMaxOccupancy: null,
      });
    } finally {
      if (!isCancelled) setNoAvailabilitySuggestionsLoading(false);
    }
  })();

  return () => {
    isCancelled = true;
  };
}, [
  categoriesLoading,
  categories,
  shoppingError,
  pricedCategories.length,
  draftCtx?.reservation_draft_id,
  draftCtx?.public_session_token,
  checkIn,
  checkOut,
  adults,
  children,
  noAvailabilityEndpointUnsupported,
]);

  useEffect(() => {
    if (!bookingEntryParams.shouldAutoSearch || !businessUnitId || !paxLimits || !hotelContext) {
      return;
    }

    if (hotelContext.booking_engine_entry_mode !== "widget") {
      setWidgetAutoSearchPending(false);
      return;
    }

    if (widgetAutoSearchAttemptedRef.current) return;

    const maxAdults = Math.max(1, Number(paxLimits.max_adults) || bookingEntryParams.adults);
    const maxChildren = Math.max(0, Number(paxLimits.max_children) || 0);
    const nextAdults = Math.min(maxAdults, bookingEntryParams.adults);
    const nextChildren = Math.min(maxChildren, bookingEntryParams.children);

    widgetAutoSearchAttemptedRef.current = true;
    setCheckIn(bookingEntryParams.checkIn);
    setCheckOut(bookingEntryParams.checkOut);
    setAdults(nextAdults);
    setChildren(nextChildren);

    Promise.resolve(createOrUpdateDraftAndGo({
      nextCheckIn: bookingEntryParams.checkIn,
      nextCheckOut: bookingEntryParams.checkOut,
      nextAdults,
      nextChildren,
    })).finally(() => setWidgetAutoSearchPending(false));
  }, [
    bookingEntryParams,
    businessUnitId,
    hotelContext?.booking_engine_entry_mode,
    paxLimits,
  ]);






  if (error) {
    return (
      <CenteredMessage>{error}</CenteredMessage>
    );
  }

  if (widgetAutoSearchPending) {
    return <CenteredLoadingMessage>{t("loading_available_rooms")}</CenteredLoadingMessage>;
  }

  if (!hotelContext || !paxLimits) return null;

  
    async function createOrUpdateDraftAndGo({
      nextCheckIn,
      nextCheckOut,
      nextAdults,
      nextChildren,
    }) {


    if (!nextCheckIn || !nextCheckOut) {
      alert(t("select_dates"));
      return;
    }


  // Helper local: nights between YYYY-MM-DD (end exclusive)
  function calcRoomNights(ci, co) {
    try {
      const a = new Date(`${ci}T00:00:00`);
      const b = new Date(`${co}T00:00:00`);
      const diff = Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
      return Math.max(0, diff);
    } catch {
      return null;
    }
  }

  const roomNights = calcRoomNights(nextCheckIn, nextCheckOut);
  if (!roomNights) {
    alert(t("invalid_dates"));
    return;
  }

  // We need BU to create the draft group via /api/reservation-drafts2/create
  const business_unit_id =
    hotelContext?.business_unit_id ??
    businessUnitId ??
    null;

  if (!business_unit_id) {
    /*console.error("[BOOKING][DRAFT2][ERROR] Missing business_unit_id (context not updated or not loaded)", {
      slug,
      businessUnitId,
      hotelContext_keys: Object.keys(hotelContext || {}),
      hotelContext,
    });*/
    //console.error("[BOOKING][DRAFT2][ERROR] Missing business_unit_id", {
      //slug: String(slug || "").slice(0, 64),
      //businessUnitId,
      //hasHotelContext: !!hotelContext,
    //});
    alert(t("unable_start_booking_missing_bu"))
    return;
  }

  // Cart shopping criteria are deliberately independent from selected drafts.
  // Changing dates or occupancy only refreshes shopping; the backend snapshot is
  // mutated later, and only by PUT /selections.
  try {
    let groupId = Number(draftCtx?.reservation_group_id || 0);
    let publicSessionToken = String(draftCtx?.public_session_token || "").trim();

    if (!groupId || !publicSessionToken) {
      const cleanSlug = String(slug || "").trim();
      if (!cleanSlug) throw new Error("missing_slug");

      const createRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/reservation-drafts2/create`,
        {
          slug: cleanSlug,
          business_unit_id: Number(business_unit_id),
          number_of_rooms: 0,
          cart_mode: true,
          created_by: "BookingCartSession",
        }
      );

      groupId = Number(createRes.data?.reservation_group_id || 0);
      publicSessionToken = String(createRes.data?.public_session_token || "").trim();
      if (!groupId || !publicSessionToken) throw new Error("invalid_cart_session");

      setDraftCtx({
        reservation_group_id: groupId,
        reservation_draft_id: null,
        public_session_token: publicSessionToken,
        public_session_expires_at: createRes.data?.public_session_expires_at || null,
      });
      applyCanonicalBookingCart({ items: [], count: 0, total: 0, currency: bookingCurrency });
    }

    setCheckIn(nextCheckIn);
    setCheckOut(nextCheckOut);
    setAdults(Number(nextAdults || 0));
    setChildren(Number(nextChildren || 0));
    setStep("categories");
    setSelectionError(null);
    setCategoriesReloadKey((value) => value + 1);
    return;
  } catch (err) {
    alert(t("unable_check_availability"));
    return;
  }
}



  async function handleCheckAvailability() {
    await createOrUpdateDraftAndGo({
      nextCheckIn: checkIn,
      nextCheckOut: checkOut,
      nextAdults: adults,
      nextChildren: children,
    });
  }

  function formatDateForLang(ymd) {
    const raw = String(ymd || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || "—";
    try {
      const [y, m, d] = raw.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      return dt.toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return raw;
    }
  }

  async function handleApplySuggestedDates(nextCheckIn, nextCheckOut) {
    const key = `${nextCheckIn}|${nextCheckOut}`;
    setApplyingSuggestedDatesKey(key);
    try {
      await createOrUpdateDraftAndGo({
        nextCheckIn,
        nextCheckOut,
        nextAdults: adults,
        nextChildren: children,
      });
    } finally {
      setApplyingSuggestedDatesKey("");
    }
  }

    const hasDraft = !!draftCtx.reservation_draft_id;
    const selectionPending = selectingRateId != null || categoriesRefreshing;
    const extrasNavigationPending = extrasLoading || mutatingExtraKey != null;
    const isMobile =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      (window.matchMedia("(pointer: coarse)").matches ||
        window.matchMedia("(max-width: 768px)").matches);
    const todayYMD = formatLocalYMD(new Date());
    const selectedGuestsTotal = Number(adults || 0) + Number(children || 0);
    const noAvailabilityHotelMaxOccupancy =
      (Number(noAvailabilityMeta?.hotelMaxOccupancy) > 0
        ? Number(noAvailabilityMeta?.hotelMaxOccupancy)
        : null) ??
      fallbackHotelMaxOccupancy;
    const showNoAvailabilityCapacityReason =
      noAvailabilityMeta?.noCombinationInHotel === true ||
      (
        Number.isFinite(noAvailabilityHotelMaxOccupancy) &&
        noAvailabilityHotelMaxOccupancy > 0 &&
        selectedGuestsTotal > noAvailabilityHotelMaxOccupancy
      );

    function handleBookingCurrencyChange(nextCurrency) {
      if (selectRateInFlightRef.current || extrasMutationInFlightRef.current || extrasLoading) return;
      const rate = Number(currencyConfiguration?.rates?.[nextCurrency]);
      setDisplayCurrency(nextCurrency);
      setRoomStateByDraftId((current) => Object.fromEntries(
        Object.entries(current || {}).map(([draftId, state]) => {
          const selectedRate = state?.selectedRate;
          if (!selectedRate || !Number.isFinite(rate) || rate <= 0) return [draftId, state];

          const baseTotal = Number(selectedRate.base_min_total ?? selectedRate.min_total);
          const baseAverage = Number(selectedRate.base_min_avg_night ?? selectedRate.min_avg_night);

          return [draftId, {
            ...state,
            selectedRate: {
              ...selectedRate,
              currency: nextCurrency,
              min_total: Number.isFinite(baseTotal) ? baseTotal / rate : selectedRate.min_total,
              min_avg_night: Number.isFinite(baseAverage) ? baseAverage / rate : selectedRate.min_avg_night,
            },
          }];
        })
      ));
      setCategoriesReloadKey((value) => value + 1);
    }

    async function navigateThroughExtras(emptyDestination) {
      const groupId = Number(draftCtx?.reservation_group_id || 0);
      const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
      if (
        extrasLoading ||
        extrasMutationInFlightRef.current ||
        !groupId ||
        !publicSessionToken
      ) return;

      const requestSeq = extrasRequestSeqRef.current + 1;
      extrasRequestSeqRef.current = requestSeq;
      extrasEmptyDestinationRef.current = emptyDestination === "categories"
        ? "categories"
        : "guest_details";
      setExtrasLoading(true);
      setExtrasError(null);
      extrasMutationInFlightRef.current = true;
      cartRequestSeqRef.current += 1;
      setCartLoading(false);
      setMutatingExtraKey("reconcile");

      try {
        const nextCatalog = await requestReconciledBookingExtrasCatalog({
          apiUrl: process.env.REACT_APP_API_URL,
          groupId,
          publicSessionToken,
          displayCurrency: bookingCurrency,
          isCurrent: () => extrasRequestSeqRef.current === requestSeq,
        });
        if (!nextCatalog) return;
        setExtrasCatalog(nextCatalog);
        if (nextCatalog.cart) applyCanonicalBookingCart(nextCatalog.cart);

        const destination = hasBookableExtras(nextCatalog) ? "extras" : emptyDestination;
        skipNextExtrasLoadRef.current = destination === "extras";
        window.scrollTo({ top: 0, behavior: "smooth" });
        setStep(destination);
      } catch (_error) {
        if (extrasRequestSeqRef.current === requestSeq) {
          setExtrasError("extras_load_error");
          alert(t("extras_load_error"));
        }
      } finally {
        extrasMutationInFlightRef.current = false;
        setMutatingExtraKey(null);
        if (extrasRequestSeqRef.current === requestSeq) {
          setExtrasLoading(false);
        }
      }
    }

    async function goToExtras() {
      if (cartItemCount <= 0 || !draftCtx?.reservation_draft_id) {
        alert(t("select_rate_for_each_room"));
        return;
      }
      await navigateThroughExtras("guest_details");
    }

    async function handleBackFromGuestDetails() {
      await navigateThroughExtras("categories");
    }

    function handleBackFromExtras() {
      if (extrasMutationInFlightRef.current) return;
      extrasRequestSeqRef.current += 1;
      setExtrasLoading(false);
      setMutatingExtraKey(null);
      setExtrasError(null);
      setStep("categories");
    }

    async function handleSetExtraSelected(extra, shouldSelect, reservationDraftId = null) {
      const groupId = Number(draftCtx?.reservation_group_id || 0);
      const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
      const extraId = normalizeBookingExtraId(extra?.extra_id ?? extra?.id);
      const normalizedDraftId = String(extra?.scope || "per_room").toLowerCase() === "per_reservation"
        ? null
        : normalizeBookingExtraDraftId(
            reservationDraftId ?? extra?.reservation_draft_id
          );
      const targetSelectionKey = bookingExtraSelectionKey(normalizedDraftId, extraId);
      if (
        extrasMutationInFlightRef.current ||
        !groupId ||
        !publicSessionToken ||
        !extraId ||
        !targetSelectionKey ||
        extra?.is_required ||
        extra?.is_automatic ||
        extra?.is_selectable === false
      ) return;

      const selectionsByKey = new Map(
        buildBookingExtrasSelections(extrasCatalog).map((entry) => [
          bookingExtraSelectionKey(entry.reservation_draft_id, entry.extra_id),
          entry,
        ])
      );
      if (shouldSelect) {
        selectionsByKey.set(targetSelectionKey, {
          reservation_draft_id: normalizedDraftId,
          extra_id: extraId,
        });
      } else {
        selectionsByKey.delete(targetSelectionKey);
      }
      const selections = sortBookingExtraSelections(selectionsByKey.values());

      extrasMutationInFlightRef.current = true;
      extrasRequestSeqRef.current += 1;
      cartRequestSeqRef.current += 1;
      setExtrasLoading(false);
      setCartLoading(false);
      setMutatingExtraKey(targetSelectionKey);
      setExtrasError(null);

      try {
        const { data } = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups2/${groupId}/extras/selections`,
          {
            selections,
            display_currency: bookingCurrency,
          },
          { headers: { "x-public-session-token": publicSessionToken } }
        );
        const nextCatalog = normalizeBookingExtrasCatalogPayload(data, bookingCurrency);
        setExtrasCatalog(nextCatalog);
        if (nextCatalog.cart) applyCanonicalBookingCart(nextCatalog.cart);
      } catch (error) {
        const status = Number(error?.response?.status || 0);
        setExtrasError(status === 409 ? "extras_load_error" : "extras_update_error");
        if (status === 409) {
          setExtrasReloadKey((value) => value + 1);
          setCartReloadKey((value) => value + 1);
        }
      } finally {
        extrasMutationInFlightRef.current = false;
        setMutatingExtraKey(null);
      }
    }

    async function handleContinueFromExtras() {
      const groupId = Number(draftCtx?.reservation_group_id || 0);
      const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
      if (
        extrasLoading ||
        extrasError ||
        extrasMutationInFlightRef.current ||
        !groupId ||
        !publicSessionToken
      ) return;

      const selections = buildBookingExtrasSelections(extrasCatalog);

      extrasMutationInFlightRef.current = true;
      extrasRequestSeqRef.current += 1;
      cartRequestSeqRef.current += 1;
      setMutatingExtraKey("reconcile");
      setExtrasError(null);
      try {
        const { data } = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups2/${groupId}/extras/selections`,
          { selections, display_currency: bookingCurrency },
          { headers: { "x-public-session-token": publicSessionToken } }
        );
        const nextCatalog = normalizeBookingExtrasCatalogPayload(data, bookingCurrency);
        setExtrasCatalog(nextCatalog);
        if (nextCatalog.cart) applyCanonicalBookingCart(nextCatalog.cart);
        goToGuestDetails();
      } catch (_error) {
        setExtrasError("extras_update_error");
      } finally {
        extrasMutationInFlightRef.current = false;
        setMutatingExtraKey(null);
      }
    }

    function goToGuestDetails() {
      if (cartItemCount <= 0 || !draftCtx?.reservation_draft_id) {
        alert(t("select_rate_for_each_room"));
        return;
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      setStep("guest_details");
    }


    async function handleSetSelectionQuantity(category, rate, requestedQuantity, criteriaOverride = null) {
      const groupId = Number(draftCtx.reservation_group_id);
      const categoryId = Number(category?.id);
      const rateId = Number(rate?.id);
      const publicSessionToken = String(draftCtx?.public_session_token || "").trim();
      const quantity = Math.max(0, Math.trunc(Number(requestedQuantity) || 0));
      const currentQuantity = Math.max(0, Math.trunc(Number(rate?.selected_quantity) || 0));
      const selectionKey = String(rate?.selection_key || "").trim();
      const mutationCriteria = criteriaOverride && typeof criteriaOverride === "object"
        ? {
            check_in_date: String(criteriaOverride.check_in_date || "").trim(),
            check_out_date: String(criteriaOverride.check_out_date || "").trim(),
            adults: Number(criteriaOverride.adults || 0),
            children: Number(criteriaOverride.children || 0),
            babies: Number(criteriaOverride.babies || 0),
            display_currency: criteriaOverride.display_currency || bookingCurrency,
          }
        : shoppingCriteria;

      if (
        selectRateInFlightRef.current ||
        !groupId ||
        !categoryId ||
        !rateId ||
        !publicSessionToken ||
        !selectionKey ||
        quantity === currentQuantity ||
        (quantity > currentQuantity && rate?.can_increment === false)
      ) {
        return;
      }

      // A cart GET started before this mutation must never overwrite the
      // canonical cart returned by the PUT when it resolves later.
      cartRequestSeqRef.current += 1;
      setCartLoading(false);
      selectRateInFlightRef.current = true;
      setSelectingRateId(selectionKey);
      setSelectionError(null);
      setCartError(null);

      try {
        const { data } = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups2/${groupId}/selections`,
          {
            ...mutationCriteria,
            room_category_id: categoryId,
            room_rate_id: rateId,
            selection_key: selectionKey,
            quantity,
          },
          {
            headers: { "x-public-session-token": publicSessionToken },
          }
        );

        const responseRoot = unwrapBookingApiPayload(data);
        if (responseRoot?.cart || responseRoot?.items || responseRoot?.selections || responseRoot?.rooms) {
          applyCanonicalBookingCart(responseRoot.cart || responseRoot);
        } else {
          const cartRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups2/${groupId}/cart`,
            {
              headers: { "x-public-session-token": publicSessionToken },
              params: { display_currency: bookingCurrency },
            }
          );
          applyCanonicalBookingCart(cartRes.data);
        }
        setCategories((currentCategories) => (
          Array.isArray(currentCategories)
            ? currentCategories.map((currentCategory) => ({
                ...currentCategory,
                rates: (Array.isArray(currentCategory?.rates) ? currentCategory.rates : []).map(
                  (currentRate) => (
                    String(currentRate?.selection_key || "") === selectionKey
                      ? {
                          ...currentRate,
                          selected_quantity: quantity,
                          selected: quantity > 0,
                        }
                      : currentRate
                  )
                ),
              }))
            : currentCategories
        ));
        preserveCategoriesOnNextReloadRef.current = true;
        setCategoriesRefreshing(true);
        setCategoriesReloadKey((value) => value + 1);
      } catch (e) {
        const errorPayload = unwrapBookingApiPayload(e?.response?.data);
        const errorCode = String(
          errorPayload?.error_code || errorPayload?.errorCode || errorPayload?.code || ""
        ).trim();
        const refreshableAvailabilityCodes = new Set([
          "CATEGORY_SOLD_OUT",
          "RATE_SELECTION_NOT_AVAILABLE",
          "RATE_PRICE_BREAKDOWN_INVALID",
          "COMMIT_INSUFFICIENT_AVAILABILITY",
          "SELECTION_QUANTITY_UNAVAILABLE",
        ]);
        const isAvailabilityConflict = refreshableAvailabilityCodes.has(errorCode) || Number(e?.response?.status) === 409;
        const isConcurrentConflict = errorCode === "SELECTION_CONFLICT_RETRY";
        setSelectionError({
          categoryId,
          messageKey:
            isConcurrentConflict
              ? "inline_rate_retry_conflict"
              : isAvailabilityConflict
                ? "inline_rate_unavailable"
                : "unable_select_rate",
        });
        if (isAvailabilityConflict || isConcurrentConflict) {
          setCategoriesReloadKey((value) => value + 1);
        }
      } finally {
        selectRateInFlightRef.current = false;
        setSelectingRateId(null);
      }
    }

    async function handleSelectInlineRate(category, rate) {
      const currentQuantity = Math.max(0, Math.trunc(Number(rate?.selected_quantity) || 0));
      await handleSetSelectionQuantity(category, rate, currentQuantity > 0 ? currentQuantity : 1);
    }

  async function handleRemoveRoomDraft(draftIdToRemove) {
    const groupId = Number(draftCtx.reservation_group_id);
    const draftId = Number(draftIdToRemove);

    // Cart-mode removal is the same idempotent absolute-quantity mutation as
    // pressing the compact minus button. The final room can be removed too.
    if (groupId) {
      const state = roomStateByDraftId?.[draftId] || {};
      const selectedCategory = state?.selectedCategory || {};
      const selectedRate = state?.selectedRate || {};
      const selectionKey = String(state?.selectionKey || selectedRate?.selection_key || "").trim();
      if (selectionKey && selectedCategory?.id && selectedRate?.id) {
        const matchingItems = Object.values(roomStateByDraftId || {}).filter((candidate) => (
          String(candidate?.selectionKey || candidate?.selectedRate?.selection_key || "").trim() === selectionKey
        ));
        const currentQuantity = matchingItems.length;
        await handleSetSelectionQuantity(
          selectedCategory,
          { ...selectedRate, selection_key: selectionKey, selected_quantity: currentQuantity },
          Math.max(0, currentQuantity - 1),
          {
            check_in_date: state?.checkIn,
            check_out_date: state?.checkOut,
            adults: state?.adults,
            children: state?.children,
            babies: state?.babies,
            display_currency: bookingCurrency,
          }
        );
        return;
      }
    }
  }






  return (
    <>
      <style>{`
        /* Base */
        .bookingPage { font-size: 15px; }

        .bookingSrOnly {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }

        .bookingTopBar {
          --booking-topbar-height: 74px;
          position: fixed;
          inset: 0 0 auto;
          z-index: 2100;
          min-height: var(--booking-topbar-height);
          width: 100%;
          display: grid;
          grid-template-columns: minmax(160px, 1fr) minmax(420px, auto) auto;
          align-items: stretch;
          box-sizing: border-box;
          padding: 0 clamp(16px, 3vw, 46px);
          color: #fff;
          background: rgba(7, 7, 7, 0.96);
          border-bottom: 1px solid rgba(255,255,255,0.12);
          box-shadow: 0 12px 32px rgba(0,0,0,0.36);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }

        .bookingTopBar__brand {
          min-width: 0;
          display: flex;
          align-items: center;
          padding-right: 16px;
          overflow: hidden;
          color: #e6c36a;
          font-size: 0.92rem;
          font-weight: 900;
          letter-spacing: 0.12em;
          line-height: 1.2;
          text-overflow: ellipsis;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .bookingTopBar__contactPhone {
          color: rgba(255,255,255,0.92);
          font-weight: 760;
          font-variant-numeric: tabular-nums;
        }

        .bookingTopBar__contact {
          text-decoration: none;
        }

        .bookingTopBar__stay {
          display: flex;
          align-items: stretch;
          min-width: 0;
        }

        .bookingTopBar__stayControl {
          min-width: 138px;
          min-height: var(--booking-topbar-height);
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
          padding: 10px 18px;
          border: 0;
          border-left: 1px solid rgba(255,255,255,0.09);
          background: transparent;
          color: #fff;
          text-align: left;
          cursor: pointer;
          transition: background 160ms ease;
        }

        .bookingTopBar__stayControl:last-child {
          border-right: 1px solid rgba(255,255,255,0.09);
        }

        .bookingTopBar__stayControl:hover,
        .bookingTopBar__stayControl:focus-visible {
          background: rgba(255,255,255,0.07);
          outline: none;
        }

        .bookingTopBar__controlLabel {
          color: rgba(255,255,255,0.52);
          font-size: 0.66rem;
          font-weight: 900;
          letter-spacing: 0.13em;
          text-transform: uppercase;
        }

        .bookingTopBar__controlValue {
          overflow: hidden;
          color: rgba(255,255,255,0.92);
          font-size: 0.87rem;
          font-weight: 760;
          line-height: 1.2;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bookingTopBar__roomsControl {
          min-width: 218px;
        }

        .bookingTopBar__roomsValue {
          width: 100%;
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 14px;
        }

        .bookingTopBar__roomsCount {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bookingTopBar__roomsTotal {
          flex: 0 0 auto;
          color: #fff;
          font-size: 0.94rem;
          font-weight: 900;
          font-variant-numeric: tabular-nums;
          letter-spacing: 0.01em;
        }

        .bookingTopBar__actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          min-width: 0;
          padding-left: 18px;
        }

        .bookingTopBar__select {
          min-width: 58px;
          height: 42px;
          padding: 0 10px;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 10px;
          background: rgba(255,255,255,0.06);
          color: #fff;
          font-size: 0.76rem;
          font-weight: 900;
          letter-spacing: 0.08em;
          outline: none;
          cursor: pointer;
        }

        .bookingTopBar__select:focus-visible {
          border-color: #e6c36a;
          box-shadow: 0 0 0 3px rgba(230,195,106,0.18);
        }

        .bookingTopBar__summaryButton {
          min-height: 46px;
          min-width: 158px;
          display: none;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 8px 13px;
          border: 1px solid rgba(230,195,106,0.55);
          border-radius: 11px;
          background: rgba(230,195,106,0.10);
          color: #fff;
          cursor: pointer;
        }

        .bookingTopBar__summaryButton:hover,
        .bookingTopBar__summaryButton:focus-visible {
          background: rgba(230,195,106,0.17);
          outline: none;
          box-shadow: 0 0 0 3px rgba(230,195,106,0.14);
        }

        .bookingTopBar__summaryText {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
          color: #e6c36a;
          font-size: 0.68rem;
          font-weight: 900;
          letter-spacing: 0.11em;
          line-height: 1.15;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .bookingTopBar__summaryText small {
          color: rgba(255,255,255,0.58);
          font-size: 0.62rem;
          letter-spacing: 0.08em;
        }

        .bookingTopBar__summaryButton strong {
          color: #fff;
          font-size: 1rem;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }

        .bookingTopBar__summaryArrow {
          color: #e6c36a;
          font-size: 1.6rem;
          font-weight: 300;
          line-height: 1;
        }

        .bookingTopBar__continueButton {
          min-height: 46px;
          padding: 0 16px;
          border: 1px solid var(--be-primary, #e6c36a);
          border-radius: 11px;
          background: var(--be-primary, #e6c36a);
          color: var(--be-on-primary, #fff);
          font: inherit;
          font-size: 0.72rem;
          font-weight: 900;
          letter-spacing: 0.09em;
          text-transform: uppercase;
          white-space: nowrap;
          cursor: pointer;
        }

        .bookingTopBar__continueButton:disabled {
          border-color: rgba(255,255,255,0.14);
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.42);
          cursor: not-allowed;
        }

        .bookingTopBar__continueButton:not(:disabled):focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(230,195,106,0.22);
        }

        :root {
          --booking-summary-panel-width: min(440px, 100vw);
        }

        .bookingFlowRoot {
          --booking-topbar-offset: 74px;
          width: 100%;
          min-height: 100dvh;
          padding-top: var(--booking-topbar-offset);
          box-sizing: border-box;
        }

        .bookingFlowRoot.bookingFlowRoot--guest {
          height: 100dvh;
          min-height: 0;
          overflow-y: auto;
          overscroll-behavior-y: contain;
        }

        .bookingFlowRoot .bookingShell {
          min-height: calc(100dvh - var(--booking-topbar-offset)) !important;
        }

        .bookingFlowRoot .bookingShell.bookingShell--rooms {
          padding: 24px clamp(10px, 1.4vw, 24px) 40px !important;
        }

        .bookingFlowRoot .bookingViewport.bookingViewport--rooms {
          max-width: 1920px !important;
        }

        .bookingFlowRoot .bookingCard.bookingCard--rooms {
          padding: 0 !important;
          background: transparent !important;
          border: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
        }

        .bookingFlowRoot .bookingCard.bookingCard--guest {
          padding: 0 !important;
          background: transparent !important;
          border: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          overflow: visible !important;
        }

        .bookingCard--rooms .wizardHeader {
          width: fit-content;
          max-width: 100%;
          box-sizing: border-box;
          margin: 0 auto 22px !important;
          padding: 16px clamp(18px, 3vw, 32px);
          color: var(--be-text, #223A7A);
          background: var(--be-surface, #fff);
          border: 1px solid var(--be-border, #C8D7E4);
          border-radius: var(--be-radius-card, 16px);
          box-shadow: var(--be-shadow-control, 0 1px 2px rgba(34, 58, 122, 0.08));
        }

        .bookingCard--rooms .wizardHeader > div:first-child {
          font-size: 1.45rem !important;
          letter-spacing: 0.06em !important;
        }

        .bookingCard--rooms .wizardHeader > div:last-child {
          display: none;
        }

        .bookingFlowRoot .bookingShell.bookingShell--guest {
          overflow: visible !important;
          align-items: flex-start !important;
          padding: 22px 0 36px !important;
        }

        .bookingFlowRoot .bookingViewport.bookingViewport--guest {
          max-width: none !important;
          zoom: 1 !important;
          transform: none !important;
        }

        .bookingGuestDetailsLayout {
          width: 100%;
          display: grid;
          grid-template-columns: minmax(0, 1fr) var(--booking-summary-panel-width);
          align-items: start;
          gap: 0;
        }

        .bookingExtrasLayout {
          width: 100%;
          display: grid;
          grid-template-columns: minmax(0, 1fr) var(--booking-summary-panel-width);
          align-items: start;
          gap: 0;
        }

        .bookingExtrasLayout__main {
          grid-column: 1;
          width: 100%;
          max-width: 1040px;
          min-width: 0;
          justify-self: center;
          padding: 0 clamp(20px, 3.2vw, 48px);
          box-sizing: border-box;
        }

        .bookingExtrasStep {
          width: 100%;
          color: var(--be-text, #fff);
        }

        .bookingExtrasStep__hero {
          position: relative;
          max-width: 760px;
          margin-bottom: 22px;
          padding-left: 34px;
        }

        .bookingExtrasStep__eyebrow {
          position: absolute;
          top: -2px;
          left: 0;
          color: var(--be-accent-text, #e6c36a);
          font-size: 1.25rem;
        }

        .bookingExtrasStep__hero h1 {
          margin: 0;
          color: var(--be-text, #fff);
          font-family: var(--be-font-heading, inherit);
          font-size: clamp(1.45rem, 2.2vw, 2.15rem);
          font-weight: 850;
          letter-spacing: 0.035em;
          line-height: 1.15;
        }

        .bookingExtrasStep__hero p {
          margin: 8px 0 0;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          font-size: 0.95rem;
          line-height: 1.55;
        }

        .bookingExtrasStep__sections {
          display: grid;
          gap: 22px;
        }

        .bookingExtrasSection {
          min-width: 0;
          padding: 18px;
          background: color-mix(in srgb, var(--be-surface, rgba(255,255,255,0.04)) 82%, transparent);
          border: 1px solid var(--be-border, rgba(255,255,255,0.12));
          border-radius: var(--be-radius-card, 14px);
          box-shadow: var(--be-shadow-card, 0 14px 34px rgba(0,0,0,0.12));
        }

        .bookingExtrasSection--reservation {
          background:
            linear-gradient(135deg, color-mix(in srgb, var(--be-accent, #e6c36a) 10%, transparent), transparent 64%),
            var(--be-surface, rgba(255,255,255,0.04));
          border-color: color-mix(in srgb, var(--be-accent, #e6c36a) 38%, var(--be-border, transparent));
        }

        .bookingExtrasSection__header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 15px;
        }

        .bookingExtrasSection__header > div {
          min-width: 0;
        }

        .bookingExtrasSection__kicker {
          display: block;
          margin-bottom: 5px;
          color: var(--be-accent-text, #e6c36a);
          font-size: 0.7rem;
          font-weight: 900;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .bookingExtrasSection__header h2 {
          margin: 0;
          color: var(--be-text, #fff);
          font-family: var(--be-font-heading, inherit);
          font-size: 1.18rem;
          font-weight: 850;
          line-height: 1.25;
        }

        .bookingExtrasSection__header p {
          margin: 5px 0 0;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          font-size: 0.8rem;
          line-height: 1.4;
        }

        .bookingExtrasSection__metadata {
          min-width: min(100%, 345px);
          display: grid;
          gap: 7px;
          margin: 0;
          padding: 10px 12px;
          background: var(--be-surface-muted, rgba(255,255,255,0.035));
          border: 1px solid color-mix(in srgb, var(--be-border, rgba(255,255,255,0.12)) 72%, transparent);
          border-radius: var(--be-radius-control, 8px);
        }

        .bookingExtrasSection__metadata > div {
          display: grid;
          grid-template-columns: 76px minmax(0, 1fr);
          gap: 9px;
        }

        .bookingExtrasSection__metadata dt,
        .bookingExtrasSection__metadata dd {
          margin: 0;
        }

        .bookingExtrasSection__metadata dt {
          color: var(--be-text-subtle, rgba(255,255,255,0.55));
          font-size: 0.62rem;
          font-weight: 850;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .bookingExtrasSection__metadata dd {
          color: var(--be-text, #fff);
          font-size: 0.76rem;
          font-weight: 720;
          line-height: 1.35;
        }

        .bookingExtrasSection__empty {
          margin: 0;
          padding: 13px 14px;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          background: var(--be-surface-muted, rgba(255,255,255,0.03));
          border: 1px dashed var(--be-border, rgba(255,255,255,0.12));
          border-radius: var(--be-radius-control, 8px);
          font-size: 0.82rem;
        }

        .bookingExtrasStep__grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .bookingExtraCard {
          position: relative;
          min-height: 190px;
          overflow: hidden;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          grid-template-rows: 1fr;
          gap: 16px;
          padding: 20px;
          color: var(--be-text, #fff);
          background: var(--be-surface, rgba(255,255,255,0.04));
          background:
            radial-gradient(circle at 100% 0%, color-mix(in srgb, var(--be-accent, #e6c36a) 13%, transparent), transparent 42%),
            var(--be-surface, rgba(255,255,255,0.04));
          border: 1px solid var(--be-border, rgba(255,255,255,0.12));
          border-radius: var(--be-radius-card, 14px);
          box-shadow: var(--be-shadow-card, 0 14px 34px rgba(0,0,0,0.16));
          transition: border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
        }

        .bookingExtraCard:hover {
          transform: translateY(-2px);
          border-color: color-mix(in srgb, var(--be-accent, #e6c36a) 55%, var(--be-border, transparent));
        }

        .bookingExtraCard--selected {
          border-color: var(--be-selected-border, var(--be-accent, #e6c36a));
          background: var(--be-selected-bg, var(--be-surface, rgba(255,255,255,0.04)));
          background:
            linear-gradient(135deg, color-mix(in srgb, var(--be-selected-bg, #e6c36a) 16%, transparent), transparent 58%),
            var(--be-surface, rgba(255,255,255,0.04));
        }

        .bookingExtraCard__ornament {
          position: absolute;
          right: 12px;
          top: 4px;
          color: color-mix(in srgb, var(--be-accent-text, #e6c36a) 18%, transparent);
          font-size: 4.5rem;
          line-height: 1;
          pointer-events: none;
        }

        .bookingExtraCard__content,
        .bookingExtraCard__purchase {
          position: relative;
          z-index: 1;
        }

        .bookingExtraCard__content {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .bookingExtraCard__heading {
          display: flex;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 8px;
        }

        .bookingExtraCard__heading h3 {
          flex: 1 1 180px;
          margin: 0;
          color: var(--be-text, #fff);
          font-family: var(--be-font-heading, inherit);
          font-size: 1.05rem;
          font-weight: 850;
          line-height: 1.25;
        }

        .bookingExtraCard__badges {
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          gap: 5px;
        }

        .bookingExtraCard__status,
        .bookingExtraCard__selectedBadge,
        .bookingExtraCard__required {
          display: inline-flex;
          align-items: center;
          min-height: 22px;
          padding: 3px 7px;
          color: var(--be-selected-text, #111);
          background: var(--be-selected-bg, #e6c36a);
          border: 1px solid var(--be-selected-border, #e6c36a);
          border-radius: 999px;
          font-size: 0.58rem;
          font-weight: 900;
          letter-spacing: 0.08em;
          line-height: 1.2;
          text-transform: uppercase;
        }

        .bookingExtraCard__status--automatic {
          color: var(--be-on-primary, #fff);
          background: var(--be-primary, #223a7a);
          border-color: var(--be-primary, #223a7a);
        }

        .bookingExtraCard__description {
          display: -webkit-box;
          margin: 10px 0 0;
          overflow: hidden;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          font-size: 0.86rem;
          line-height: 1.48;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }

        .bookingExtraCard__pricingBreakdown {
          display: grid;
          gap: 7px;
          margin: 14px 0 0;
          padding: 10px 12px;
          background: color-mix(in srgb, var(--be-surface, rgba(255,255,255,0.04)) 75%, transparent);
          border: 1px solid color-mix(in srgb, var(--be-border, rgba(255,255,255,0.12)) 78%, transparent);
          border-radius: var(--be-radius-control, 8px);
        }

        .bookingExtraCard__pricingBreakdown > div {
          display: grid;
          grid-template-columns: 62px minmax(0, 1fr);
          gap: 8px;
          align-items: baseline;
        }

        .bookingExtraCard__pricingBreakdown dt,
        .bookingExtraCard__pricingBreakdown dd {
          margin: 0;
        }

        .bookingExtraCard__pricingBreakdown dt {
          color: var(--be-text-subtle, rgba(255,255,255,0.55));
          font-size: 0.65rem;
          font-weight: 850;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .bookingExtraCard__pricingBreakdown dd {
          color: var(--be-text, #fff);
          font-size: 0.78rem;
          font-weight: 750;
          line-height: 1.35;
        }

        .bookingExtraCard__pricingBreakdown dd small {
          display: block;
          margin-top: 3px;
          color: var(--be-text-subtle, rgba(255,255,255,0.55));
          font-size: 0.68rem;
          font-weight: 650;
        }

        .bookingExtraCard__coverage {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-top: auto;
          padding-top: 14px;
          color: var(--be-text-muted, rgba(255,255,255,0.7));
          font-size: 0.75rem;
          font-weight: 750;
        }

        .bookingExtraCard__coverage span {
          color: var(--be-accent-text, #e6c36a);
        }

        .bookingExtraCard__purchase {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-end;
          gap: 12px;
          min-width: 122px;
        }

        .bookingExtraCard__price {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          text-align: right;
        }

        .bookingExtraCard__price strong {
          color: var(--be-accent-text, #e6c36a);
          font-size: 1.3rem;
          font-weight: 900;
          white-space: nowrap;
        }

        .bookingExtraCard__price small {
          margin-bottom: 2px;
          color: var(--be-text-subtle, rgba(255,255,255,0.55));
          font-size: 0.66rem;
          font-weight: 750;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .bookingExtraCard__action {
          width: 122px;
          min-height: 40px;
          padding: 8px 12px;
          color: var(--be-on-primary, #fff);
          background: var(--be-primary, #223a7a);
          border: 1px solid var(--be-primary, #223a7a);
          border-radius: var(--be-radius-control, 8px);
          font: inherit;
          font-size: 0.7rem;
          font-weight: 900;
          letter-spacing: 0.09em;
          text-transform: uppercase;
          cursor: pointer;
        }

        .bookingExtraCard--selected .bookingExtraCard__action {
          color: var(--be-text, #fff);
          background: transparent;
          border-color: var(--be-selected-border, #e6c36a);
        }

        .bookingExtraCard__action:disabled {
          opacity: 0.58;
          cursor: not-allowed;
        }

        .bookingExtraCard__action:focus-visible {
          outline: 3px solid var(--be-focus, rgba(230,195,106,0.35));
          outline-offset: 2px;
        }

        .bookingExtrasStep__status,
        .bookingExtrasStep__empty,
        .bookingExtrasStep__error {
          min-height: 210px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 28px;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          text-align: center;
          background: var(--be-surface, rgba(255,255,255,0.04));
          border: 1px solid var(--be-border, rgba(255,255,255,0.12));
          border-radius: var(--be-radius-card, 14px);
        }

        .bookingExtrasStep__refreshStatus,
        .bookingExtrasStep__refreshError {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
          padding: 10px 12px;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          background: var(--be-surface-muted, rgba(255,255,255,0.04));
          border: 1px solid var(--be-border, rgba(255,255,255,0.12));
          border-radius: var(--be-radius-control, 8px);
          font-size: 0.78rem;
          font-weight: 720;
        }

        .bookingExtrasStep__refreshStatus .bookingExtrasStep__spinner {
          width: 16px;
          height: 16px;
          border-width: 2px;
        }

        .bookingExtrasStep__refreshError {
          justify-content: space-between;
          color: var(--be-error, #ffb3b3);
          background: var(--be-error-bg, rgba(255,179,179,0.08));
          border-color: var(--be-error-border, rgba(255,179,179,0.35));
        }

        .bookingExtrasStep__refreshError button {
          flex: 0 0 auto;
          padding: 6px 9px;
          color: var(--be-error, #ffb3b3);
          background: transparent;
          border: 1px solid currentColor;
          border-radius: var(--be-radius-control, 8px);
          font: inherit;
          cursor: pointer;
        }

        .bookingExtrasStep__empty > span {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          color: var(--be-selected-text, #111);
          background: var(--be-selected-bg, #e6c36a);
          border-radius: 50%;
          font-weight: 900;
        }

        .bookingExtrasStep__empty h2,
        .bookingExtrasStep__empty p,
        .bookingExtrasStep__error p {
          margin: 0;
        }

        .bookingExtrasStep__error p { color: var(--be-error, #ffb3b3); }

        .bookingExtrasStep__error button {
          min-height: 40px;
          padding: 8px 16px;
          color: var(--be-on-primary, #fff);
          background: var(--be-primary, #223a7a);
          border: 1px solid var(--be-primary, #223a7a);
          border-radius: var(--be-radius-control, 8px);
          font-weight: 900;
          cursor: pointer;
        }

        .bookingExtrasStep__spinner {
          width: 26px;
          height: 26px;
          border: 3px solid var(--be-border, rgba(255,255,255,0.18));
          border-top-color: var(--be-accent, #e6c36a);
          border-radius: 50%;
          animation: bookingExtrasSpin 700ms linear infinite;
        }

        @keyframes bookingExtrasSpin { to { transform: rotate(360deg); } }

        .bookingExtrasStep__actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 18px;
        }

        .bookingExtrasStep__actions > button {
          min-height: 44px;
          padding-inline: 22px !important;
        }

        .bookingGuestDetailsLayout__main {
          grid-column: 1;
          width: 100%;
          max-width: 920px;
          min-width: 0;
          justify-self: center;
          padding: 0 clamp(20px, 3.2vw, 42px);
          box-sizing: border-box;
        }

        .bookingGuestDetailsLayout__main .bookingGuestPanel {
          max-width: none !important;
          margin: 0 !important;
          padding: clamp(14px, 1.45vw, 20px) !important;
        }

        .bookingGuestDetailsLayout__main .wizardHeader {
          margin-bottom: 14px !important;
          text-align: left !important;
        }

        .bookingGuestDetailsLayout__main .wizardHeader > div:first-child {
          font-size: clamp(1.3rem, 1.7vw, 1.55rem) !important;
          letter-spacing: 0.045em !important;
        }

        .bookingGuestDetailsLayout__main .wizardHeader > div:last-child {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        .bookingGuestForm {
          display: grid;
          gap: 8px;
        }

        .bookingGuestForm__requiredNote {
          margin: -1px 0 0;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          line-height: 1.25;
          text-align: right;
        }

        .bookingGuestForm__grid {
          display: grid;
          grid-template-columns: repeat(12, minmax(0, 1fr));
          column-gap: 10px;
          row-gap: 8px;
        }

        .bookingGuestForm__field {
          min-width: 0;
        }

        .bookingGuestForm__label {
          display: block;
          margin: 0 0 4px;
          color: var(--be-text, #fff);
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.11em;
          line-height: 1.2;
          text-transform: uppercase;
        }

        .bookingGuestForm__requiredMark {
          margin-left: 4px;
          color: var(--be-error, #ffb3b3);
          font-weight: 900;
        }

        .bookingGuestForm__dateControlWrap {
          position: relative;
        }

        .bookingGuestForm__datePlaceholder {
          position: absolute;
          inset: 1px 38px 1px 1px;
          z-index: 2;
          display: flex;
          align-items: center;
          padding-left: 10px;
          color: var(--be-text-muted, #777);
          background: var(--be-input-bg, #fff);
          font-size: 0.92rem;
          line-height: 1;
          pointer-events: none;
        }

        .bookingGuestForm__dateControlWrap:focus-within .bookingGuestForm__datePlaceholder {
          display: none;
        }

        .bookingGuestForm__control {
          width: 100%;
          min-height: 42px;
          padding: 8px 11px !important;
          font-size: 0.92rem !important;
          line-height: 1.2;
        }

        textarea.bookingGuestForm__control {
          min-height: 42px;
          resize: vertical;
        }

        .bookingGuestForm__specialRequests {
          grid-column: 1 / -1;
          margin-top: 4px;
          padding-top: 9px;
          border-top: 1px solid var(--be-border, rgba(255,255,255,0.10));
        }

        .bookingGuestForm__specialRequestsGrid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .bookingGuestForm__terms {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          margin-top: 1px;
          padding-top: 8px;
          border-top: 1px solid var(--be-border, rgba(255,255,255,0.10));
        }

        .bookingGuestForm__termsCheckbox {
          flex: 0 0 auto;
          width: 17px !important;
          height: 17px;
          margin-top: 2px;
        }

        .bookingGuestForm__termsCopy {
          color: var(--be-text-muted, rgba(255,255,255,0.82));
          font-size: 0.82rem;
          line-height: 1.4;
        }

        .bookingGuestForm__termsButton {
          padding: 0;
          color: var(--be-text, #fff);
          background: transparent;
          border: 0;
          font: inherit;
          font-weight: 800;
          text-decoration: underline;
          text-underline-offset: 2px;
          cursor: pointer;
        }

        .bookingGuestForm__termsError {
          margin-top: 3px;
          color: var(--be-error, #ffb3b3);
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 0.035em;
        }

        .bookingGuestForm__actions {
          display: flex;
          gap: 10px;
          margin-top: 1px;
        }

        .bookingGuestForm__actions > button {
          flex: 1 1 0;
          min-height: 42px;
          padding: 9px 12px !important;
          font-size: 0.78rem;
          font-weight: 900;
          letter-spacing: 0.1em !important;
        }

        .bookingGuestForm__gdpr {
          margin-top: 0;
          color: var(--be-text-subtle, rgba(255,255,255,0.58));
          font-size: 0.66rem;
          line-height: 1.35;
        }

        @media (forced-colors: active) {
          .bookingGuestForm__datePlaceholder {
            display: none;
          }
        }

        .bookingGuestDetailsLayout__summary {
          position: fixed;
          z-index: 2050;
          top: var(--booking-topbar-offset);
          right: 0;
          bottom: 0;
          margin: 0;
          width: var(--booking-summary-panel-width);
          height: calc(100dvh - var(--booking-topbar-offset));
          max-height: calc(100dvh - var(--booking-topbar-offset));
          overflow: hidden;
          border-top: 0;
          border-right: 0;
          border-bottom: 0;
          border-left: 1px solid rgba(255,255,255,0.12);
          border-radius: 0 !important;
          box-shadow: none !important;
        }

        @media (max-width: 1200px) {
          .bookingFlowRoot .bookingShell.bookingShell--guest {
            padding: 28px clamp(14px, 3vw, 32px) 46px !important;
          }

          .bookingFlowRoot .bookingViewport.bookingViewport--guest {
            max-width: 900px !important;
          }

          .bookingGuestDetailsLayout {
            display: block;
          }

          .bookingExtrasLayout {
            display: block;
          }

          .bookingGuestDetailsLayout__main {
            max-width: 900px;
            padding: 0;
          }

          .bookingExtrasLayout__main {
            max-width: 980px;
            padding: 0;
          }

          .bookingGuestDetailsLayout__summary {
            display: none !important;
          }
        }

        @media (max-width: 760px) {
          .bookingExtrasStep__hero {
            padding-left: 28px;
          }

          .bookingExtrasStep__grid {
            grid-template-columns: 1fr;
          }

          .bookingExtrasSection {
            padding: 14px;
          }

          .bookingExtrasSection__header,
          .bookingExtrasSection__header--room {
            display: grid;
          }

          .bookingExtrasSection__metadata {
            width: 100%;
            min-width: 0;
          }

          .bookingExtraCard {
            min-height: 0;
            grid-template-columns: 1fr;
          }

          .bookingExtraCard__purchase {
            width: 100%;
            flex-direction: row;
            align-items: flex-end;
            justify-content: space-between;
          }

          .bookingExtraCard__price {
            align-items: flex-start;
            text-align: left;
          }

          .bookingExtrasStep__actions {
            flex-direction: column-reverse;
          }

          .bookingExtrasStep__actions > button {
            width: 100%;
          }
        }

        .bookingSummaryBackdrop {
          z-index: 2140 !important;
          background: rgba(0,0,0,0.62) !important;
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }

        .bookingSummaryOffcanvas {
          --bs-offcanvas-width: var(--booking-summary-panel-width);
          z-index: 2150 !important;
          color: #fff;
          background: #0b0b0b !important;
          border-left: 1px solid rgba(255,255,255,0.12) !important;
          box-shadow: -24px 0 70px rgba(0,0,0,0.56);
        }

        .bookingSummaryPanel {
          color: #fff;
          background: #0b0b0b;
          display: flex;
          flex-direction: column;
        }

        .bookingSummaryPanel__header {
          flex: 0 0 70px;
          min-height: 70px;
          padding: 18px 20px;
          border-bottom: 1px solid rgba(255,255,255,0.10);
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-sizing: border-box;
        }

        .bookingSummaryPanel__title {
          margin: 0;
          color: #e6c36a;
          font-size: 0.9rem;
          font-weight: 900;
          letter-spacing: 0.14em;
          line-height: 1.25;
          text-transform: uppercase;
        }

        .bookingSummaryPanel__body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 0;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
        }

        .bookingSummaryPanel--drawer {
          min-height: 100%;
        }

        .bookingSummaryPanel .bookingSummaryContent {
          position: relative !important;
          top: auto !important;
          width: 100% !important;
          height: auto !important;
          min-height: 100% !important;
          padding: 16px !important;
          border: 0 !important;
          background: transparent !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }

        .bookingSummaryPanel .bookingSummaryContent__inner {
          width: 100% !important;
          transform: none !important;
        }

        .bookingSummaryPanel .bookingSummaryContent__scroll {
          overflow: visible !important;
          margin-top: 10px !important;
          padding-right: 2px !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryPanel__header {
          flex-basis: 58px;
          min-height: 58px;
          padding-block: 14px;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__room {
          margin-top: 8px !important;
          padding: 10px !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__grandTotal {
          margin-top: 8px !important;
          padding: 9px !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__grandTotalValue {
          margin-top: 4px !important;
          font-size: 1.65rem !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__dates {
          font-size: 0.84rem !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__datesRow,
        .bookingSummaryPanel--embedded .bookingSummaryContent__pax,
        .bookingSummaryPanel--embedded .bookingSummaryContent__selection,
        .bookingSummaryPanel--embedded .bookingSummaryContent__divider {
          margin-top: 8px !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__pax {
          gap: 6px !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__roomPrice {
          margin-top: 8px !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__roomText {
          font-size: 0.84rem;
          line-height: 1.35 !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__totalValue {
          font-size: 1.55rem !important;
        }

        .bookingSummaryPanel--embedded .bookingSummaryContent__nightly {
          font-size: 0.82rem !important;
        }

        .bookingSummaryContent__breakdown {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 5px 12px;
          margin-top: 9px;
          padding-top: 8px;
          color: var(--be-text-muted, rgba(255,255,255,0.7));
          border-top: 1px solid var(--be-border, rgba(255,255,255,0.1));
          font-size: 0.76rem;
        }

        .bookingSummaryContent__breakdown strong {
          color: var(--be-text, #fff);
          text-align: right;
        }

        .bookingSummaryContent__extras {
          margin-bottom: 10px;
          padding: 11px;
          color: var(--be-text, #fff);
          background: var(--be-surface-muted, rgba(255,255,255,0.04));
          border: 1px solid var(--be-selected-border, var(--be-border, rgba(255,255,255,0.12)));
          border-radius: var(--be-radius-sm, 10px);
        }

        .bookingSummaryContent__extras--room {
          margin: 12px 0 0;
          padding: 9px;
          border-color: var(--be-border, rgba(255,255,255,0.12));
        }

        .bookingSummaryContent__extras--reservation {
          margin-top: 12px;
        }

        .bookingSummaryContent__extrasTitle {
          color: var(--be-accent-text, #e6c36a);
          font-size: 0.72rem;
          font-weight: 900;
          letter-spacing: 0.14em;
        }

        .bookingSummaryContent__extrasList {
          display: grid;
          gap: 8px;
          margin-top: 9px;
        }

        .bookingSummaryContent__extraLine {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 12px;
          align-items: start;
          color: var(--be-text, #fff);
          font-size: 0.8rem;
          line-height: 1.3;
        }

        .bookingSummaryContent__extraLine > div {
          min-width: 0;
        }

        .bookingSummaryContent__extraLine strong {
          color: var(--be-text, #fff);
        }

        .bookingSummaryContent__extraSubtotal,
        .bookingSummaryContent__roomSubtotal {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          color: var(--be-text-muted, rgba(255,255,255,0.7));
          font-size: 0.76rem;
          font-weight: 800;
        }

        .bookingSummaryContent__extraSubtotal {
          margin-top: 9px;
          padding-top: 8px;
          border-top: 1px solid var(--be-border, rgba(255,255,255,0.1));
        }

        .bookingSummaryContent__roomSubtotal {
          margin-top: 10px;
          padding: 9px 0 0;
          color: var(--be-text, #fff);
          border-top: 1px solid var(--be-selected-border, var(--be-border, rgba(255,255,255,0.12)));
          font-size: 0.82rem;
        }

        .bookingSummaryContent__extraSubtotal strong,
        .bookingSummaryContent__roomSubtotal strong {
          color: var(--be-text, #fff);
          text-align: right;
        }

        .bookingSummaryContent__extraLine small {
          display: block;
          margin-top: 2px;
          color: var(--be-text-muted, rgba(255,255,255,0.68));
          font-size: 0.68rem;
        }

        .bookingTermsModalShell {
          z-index: 2300 !important;
        }

        .bookingTermsBackdrop {
          z-index: 2290 !important;
          background: rgba(0,0,0,0.66) !important;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .bookingTermsModal {
          color: #fff;
          background: #101010 !important;
          border: 1px solid rgba(255,255,255,0.14) !important;
          border-radius: 14px !important;
          box-shadow: 0 30px 100px rgba(0,0,0,0.72);
        }

        .bookingTermsModal .modal-header {
          border-bottom: 1px solid rgba(255,255,255,0.10);
        }

        .bookingTermsModal__title {
          color: #e6c36a;
          font-size: 0.94rem !important;
          font-weight: 900 !important;
          letter-spacing: 0.11em;
          text-transform: uppercase;
        }

        .bookingTermsModal__section {
          padding: 14px 0;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .bookingTermsModal__section:last-child { border-bottom: 0; }

        .bookingTermsModal__section h3 {
          margin: 0;
          color: #e6c36a;
          font-size: 0.9rem;
          font-weight: 900;
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }

        .bookingTermsModal__section div,
        .bookingTermsModal__empty {
          margin-top: 7px;
          color: rgba(255,255,255,0.76);
          line-height: 1.55;
          white-space: pre-wrap;
        }

        @media (min-width: 1101px) {
          .bookingTopBar__contact { display: none; }
        }

        @media (max-width: 1100px) {
          .bookingTopBar {
            grid-template-columns: minmax(0, 1fr) auto;
            padding: 0 16px;
          }

          .bookingTopBar__brand { min-height: 64px; }

          .bookingTopBar__stay {
            grid-column: 1 / -1;
            grid-row: 2;
            border-top: 1px solid rgba(255,255,255,0.08);
          }

          .bookingTopBar--search .bookingTopBar__stay { display: none; }

          .bookingTopBar__stayControl {
            flex: 1 1 0;
            min-width: 0;
            min-height: 52px;
            padding: 8px 14px;
          }

          .bookingTopBar__roomsControl { min-width: 0; }
          .bookingTopBar__roomsValue { gap: 8px; }

          .bookingTopBar__actions { padding-left: 8px; }
          .bookingFlowRoot--withStay { --booking-topbar-offset: 117px; }
        }

        @media (max-width: 768px) {
          .bookingTopBar {
            padding: env(safe-area-inset-top) 10px 0;
          }

          .bookingTopBar__brand {
            min-height: 58px;
            padding-right: 10px;
            font-size: 0.76rem;
            letter-spacing: 0.09em;
          }

          .bookingTopBar__actions { gap: 6px; padding-left: 0; }

          .bookingTopBar__select {
            min-width: 50px;
            height: 44px;
            padding: 0 7px;
          }

          .bookingTopBar__summaryButton {
            display: flex;
            min-width: 0;
            min-height: 44px;
            gap: 8px;
            padding: 7px 10px;
          }

          .bookingTopBar__summaryText { font-size: 0.61rem; }
          .bookingTopBar__summaryButton strong { font-size: 0.86rem; }

          .bookingTopBar__stayControl { min-height: 50px; padding: 7px 10px; }
          .bookingTopBar__controlLabel { font-size: 0.59rem; }
          .bookingTopBar__controlValue { font-size: 0.78rem; }
          .bookingTopBar__roomsControl { display: none; }

          .bookingSummaryOffcanvas--mobile {
            --bs-offcanvas-height: min(88dvh, 760px);
            border-top: 1px solid rgba(255,255,255,0.14) !important;
            border-left: 0 !important;
            border-radius: 18px 18px 0 0;
            box-shadow: 0 -24px 70px rgba(0,0,0,0.62);
          }

          .bookingSummaryOffcanvas .bookingSummaryContent { padding: 16px !important; }
          .bookingFlowRoot--search {
            --booking-topbar-offset: max(74px, calc(59px + env(safe-area-inset-top)));
          }
          .bookingFlowRoot--withStay {
            --booking-topbar-offset: calc(109px + env(safe-area-inset-top));
          }
        }

        @media (max-width: 520px) {
          .bookingTopBar__summaryText > span { display: none; }
          .bookingTopBar__summaryText small { display: block; }
          .bookingTopBar__summaryButton { padding-inline: 9px; }
          .bookingTopBar__continueButton {
            min-height: 44px;
            padding-inline: 10px;
            font-size: 0.64rem;
            letter-spacing: 0.05em;
          }
          .bookingTopBar__currency { max-width: 62px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .bookingTopBar__stayControl,
          .bookingTopBar__summaryButton {
            transition: none !important;
          }
        }

        /* Laptop width */
        @media (max-width: 1400px) {
          .heroTitle { font-size: 3.0rem !important; }
          .heroSubtitle { font-size: 2.0rem !important; }
          .fakeInput { padding: 14px 14px !important; font-size: 1.05rem !important; }
          .ctaBtn { padding: 18px !important; font-size: 1.05rem !important; }
          .topbarBtn { padding: 8px 10px !important; font-size: 0.9rem !important; }
        }

        /* When it starts to squeeze, stack inputs */
        @media (max-width: 1100px) {
          .searchGrid { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
          .topbarGrid { grid-template-columns: 1fr !important; }
          .topbarCenter { text-align: left !important; }
          .topbarActions { justify-content: flex-start !important; }
        }

        /* Room categories: compact comparison grid on desktop. */
        .roomGrid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          align-items: stretch;
          gap: 24px;
          width: 100%;
          max-width: 1920px;
          margin: 0 auto;
        }

        .roomCard {
          min-width: 0;
          min-height: 0;
          height: 100%;
          display: grid;
          grid-template-columns: minmax(0, 45fr) minmax(0, 55fr);
          align-items: stretch;
          overflow: hidden;
        }

        .roomCard--unavailable .roomCard__mediaPane {
          filter: grayscale(0.72);
          opacity: 0.72;
        }

        .roomCard--unavailable .roomCard__body > header,
        .roomCard--unavailable .roomCard__description {
          opacity: 0.66;
        }

        .roomCard__mediaPane {
          position: relative;
          min-width: 0;
          min-height: 0;
          width: 100%;
          height: 100%;
          aspect-ratio: auto;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: var(--be-surface-muted);
        }

        .roomCard__photoStage {
          position: relative;
          isolation: isolate;
          width: 100%;
          aspect-ratio: 3 / 2;
          flex: 0 0 auto;
          overflow: hidden;
        }

        .roomCard__media {
          position: relative;
          width: 100%;
          height: 100%;
          min-height: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          appearance: none;
          padding: 0;
          border: 0 !important;
          border-radius: 0 !important;
          background: var(--be-surface-muted);
          cursor: pointer;
          touch-action: pan-y;
        }

        .roomCard__image {
          position: absolute;
          z-index: 1;
          inset: 0;
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          pointer-events: none;
        }

        .roomCard__media--empty {
          cursor: default;
        }

        .roomCard__media--empty::after {
          display: none;
        }

        .roomCard__media::after {
          content: "";
          position: absolute;
          z-index: 2;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(180deg, transparent 62%, rgba(2, 7, 18, 0.38));
        }

        .roomCard__carouselDots {
          position: absolute;
          z-index: 3;
          inset: auto 42px 9px;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
        }

        .roomCard__carouselDot {
          width: 24px;
          height: 24px;
          display: grid;
          place-items: center;
          padding: 0;
          border: 0;
          border-radius: 999px;
          background: transparent;
          cursor: pointer;
        }

        .roomCard__carouselDot::after {
          content: "";
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: rgba(255,255,255,0.68);
          box-shadow: 0 1px 3px rgba(0,0,0,0.42);
          transition: transform 140ms ease, background 140ms ease;
        }

        .roomCard__carouselDot--active::after {
          background: #fff;
          transform: scale(1.35);
        }

        .roomCard__carouselArrow {
          position: absolute;
          z-index: 3;
          top: 50%;
          width: 34px;
          height: 34px;
          display: grid;
          place-items: center;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.42);
          border-radius: 999px;
          background: rgba(2,7,18,0.58);
          color: #fff;
          font-size: 1.25rem;
          line-height: 1;
          opacity: 0;
          cursor: pointer;
          transform: translateY(-50%);
          transition: opacity 140ms ease, background 140ms ease;
        }

        .roomCard__carouselArrow--prev { left: 9px; }
        .roomCard__carouselArrow--next { right: 9px; }

        .roomCard__photoStage:hover .roomCard__carouselArrow,
        .roomCard__photoStage:focus-within .roomCard__carouselArrow,
        .roomCard__carouselArrow:focus-visible {
          opacity: 1;
        }

        .roomCard__mediaPlaceholder {
          position: relative;
          z-index: 2;
          max-width: 18ch;
          color: var(--be-text-muted);
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-align: center;
          text-transform: uppercase;
        }

        .roomCard__body {
          min-width: 0;
          min-height: 100%;
          display: flex;
          flex-direction: column;
          padding: 20px 22px 22px;
        }

        .roomCard__title {
          margin: 0;
          font-size: clamp(1.25rem, 1.35vw, 1.5rem);
          font-weight: 700;
          letter-spacing: 0.01em;
          line-height: 1.2;
        }

        .roomCard__facts {
          display: flex;
          flex-wrap: wrap;
          gap: 5px 0;
          margin-top: 7px;
          color: var(--be-text-muted);
          font-size: 0.78rem;
        }

        .roomCard__fact {
          display: inline-flex;
          align-items: center;
        }

        .roomCard__fact + .roomCard__fact::before {
          content: "·";
          margin: 0 7px;
          color: var(--be-secondary);
          font-weight: 800;
        }

        .roomCard__description {
          display: -webkit-box;
          margin: 12px 0 0;
          overflow: hidden;
          color: var(--be-text-muted);
          font-size: 0.86rem;
          line-height: 1.45;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }

        .roomCard__rates {
          min-width: 0;
          margin-top: 14px;
          padding-top: 9px;
          border-top: 1px solid var(--be-border);
        }

        .roomCard__ratesHeader {
          min-height: 24px;
          display: flex;
          align-items: center;
          padding: 0 2px 5px;
        }

        .roomCard__ratesHeader h3 {
          margin: 0;
          color: var(--be-text);
          font-size: 0.69rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .roomCard__rateList {
          display: grid;
          gap: 3px;
        }

        .roomCard__rateOption {
          width: 100%;
          min-width: 0;
          min-height: 40px;
          display: grid;
          grid-template-columns: max-content minmax(0, 1fr) max-content max-content;
          grid-template-areas: "select name price conditions";
          align-items: center;
          gap: 7px;
          padding: 2px 4px 2px 0;
          border: 1px solid transparent;
          border-radius: var(--be-radius-sm);
          background: var(--be-surface-muted);
          color: var(--be-text);
          transition: background 140ms ease, border-color 140ms ease;
        }

        .roomCard__rateOption--selected {
          background: var(--be-selected-bg);
          border-color: var(--be-selected-border);
        }

        .roomCard__rateSelect {
          grid-area: select;
          width: 34px;
          height: 34px;
          display: grid;
          place-items: center;
          padding: 0;
          border: 0;
          border-radius: var(--be-radius-round);
          background: transparent;
          cursor: pointer;
        }

        .roomCard__rateSelect:disabled {
          cursor: not-allowed;
          opacity: 0.58;
        }

        .roomCard__rateMarker {
          width: 15px;
          height: 15px;
          border: 1.5px solid var(--be-accent);
          border-radius: var(--be-radius-round);
          background: transparent;
          box-shadow: inset 0 0 0 3px var(--be-surface);
        }

        .roomCard__rateOption--selected .roomCard__rateMarker {
          background: var(--be-accent);
        }

        .roomCard__rateSelect[aria-busy="true"] .roomCard__rateMarker {
          border-top-color: transparent;
          background: transparent;
          animation: bookingRateSpin 700ms linear infinite;
        }

        .roomCard__rateName {
          grid-area: name;
          display: block;
          min-width: 0;
          color: var(--be-text);
          font-size: 0.8rem;
          font-weight: 700;
          line-height: 1.25;
          overflow-wrap: anywhere;
          white-space: normal;
        }

        .roomCard__ratePrice {
          grid-area: price;
          color: var(--be-text);
          font-size: 0.86rem;
          font-weight: 800;
          line-height: 1;
          text-align: right;
          white-space: nowrap;
        }

        .bookingRateQuantity {
          grid-area: select;
          margin-left: 4px;
          height: 24px;
          display: inline-grid;
          grid-template-columns: 22px 20px 22px;
          align-items: center;
          overflow: hidden;
          border: 1px solid var(--be-selected-border);
          border-radius: var(--be-radius-round);
          background: var(--be-surface);
          color: var(--be-text);
        }

        .bookingRateQuantity__button {
          width: 22px;
          height: 22px;
          display: grid;
          place-items: center;
          padding: 0;
          border: 0;
          background: transparent;
          color: var(--be-accent-text);
          font: inherit;
          font-size: 0.84rem;
          font-weight: 900;
          line-height: 1;
          cursor: pointer;
        }

        .bookingRateQuantity__button:disabled {
          color: var(--be-disabled-text);
          cursor: not-allowed;
          opacity: 0.5;
        }

        .bookingRateQuantity__button:not(:disabled):focus-visible {
          outline: 2px solid var(--be-focus);
          outline-offset: -2px;
        }

        .bookingRateQuantity__value {
          min-width: 20px;
          color: var(--be-text);
          font-size: 0.72rem;
          font-weight: 850;
          line-height: 1;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }

        .inlineRateRow--unavailable:not(.roomCard__rateOption--selected) {
          color: var(--be-disabled-text);
          opacity: 0.62;
        }

        .roomCard__availabilityMessage {
          margin-top: 7px;
          padding: 7px 9px;
          border: 1px solid var(--be-border);
          border-radius: var(--be-radius-sm);
          background: var(--be-disabled-bg);
          color: var(--be-text-muted);
          font-size: 0.7rem;
          font-weight: 700;
          line-height: 1.35;
        }

        .roomCard__rateConditionsButton {
          grid-area: conditions;
          width: 30px;
          height: 30px;
          display: grid;
          place-items: center;
          padding: 0;
          border: 1px solid var(--be-control-border);
          border-radius: var(--be-radius-round);
          background: var(--be-surface);
          color: var(--be-accent-text);
          font: inherit;
          font-size: 0.84rem;
          font-weight: 850;
          line-height: 1;
          cursor: pointer;
          transition: border-color 140ms ease, background 140ms ease, color 140ms ease;
        }

        .roomCard__rateConditionsButton:hover {
          border-color: var(--be-accent);
          background: var(--be-selected-bg);
        }

        .roomCard__rateConditionsButton:focus-visible {
          outline: 2px solid var(--be-focus);
          outline-offset: 2px;
        }

        .roomCard__rateConditionsIcon {
          display: block;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 0.92rem;
          font-style: italic;
          font-weight: 700;
          transform: translateY(-0.5px);
        }

        .roomCard__rateConditionsHint {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 7px 2px 0;
          color: var(--be-text-muted);
          font-size: 0.67rem;
          line-height: 1.35;
        }

        .roomCard__rateConditionsHintIcon {
          width: 16px;
          height: 16px;
          flex: 0 0 16px;
          display: grid;
          place-items: center;
          border: 1px solid currentColor;
          border-radius: var(--be-radius-round);
          font-family: Georgia, "Times New Roman", serif;
          font-size: 0.65rem;
          font-style: italic;
          font-weight: 700;
          line-height: 1;
        }

        @keyframes bookingRateSpin {
          to { transform: rotate(360deg); }
        }

        .roomCard__ratesLoading,
        .roomCard__ratesError {
          padding: 8px 2px;
          color: var(--be-text-muted);
          font-size: 0.72rem;
          line-height: 1.4;
        }

        .roomCard__ratesError {
          color: var(--be-error);
        }

        .bookingRateConditionsModal .modal-header {
          align-items: flex-start;
          gap: 16px;
        }

        .bookingRateConditionsModal__heading { min-width: 0; }

        .bookingRateConditionsModal__eyebrow {
          display: block;
          margin-bottom: 4px;
          color: var(--be-accent-text);
          font-size: 0.67rem;
          font-weight: 800;
          letter-spacing: 0.09em;
          text-transform: uppercase;
        }

        .bookingRateConditionsModal__title {
          margin: 0;
          color: var(--be-text);
          font-family: var(--be-font-heading);
          font-size: 1.08rem;
          font-weight: 750;
          line-height: 1.25;
        }

        .bookingRateConditionsModal__category {
          margin-top: 4px;
          color: var(--be-text-muted);
          font-size: 0.76rem;
        }

        .roomCard__rateConditionsClose {
          width: 36px;
          height: 36px;
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          padding: 0;
          border: 1px solid var(--be-border);
          border-radius: var(--be-radius-round);
          background: var(--be-surface-muted);
          color: var(--be-text);
          font: inherit;
          font-size: 1.25rem;
          line-height: 1;
          cursor: pointer;
        }

        .bookingRateConditionsModal__body {
          display: grid;
          gap: 16px;
        }

        .bookingRateConditionsModal__item {
          margin: 0;
        }

        .bookingRateConditionsModal__item dt {
          margin: 0 0 5px;
          color: var(--be-text-muted);
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .bookingRateConditionsModal__item dd {
          margin: 0;
          color: var(--be-text);
          font-size: 0.88rem;
          line-height: 1.5;
          overflow-wrap: anywhere;
          white-space: pre-wrap;
        }

        @media (min-width: 1680px) {
          .roomGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .roomCard__body { padding: 18px; }
        }

        @media (max-width: 1100px) {
          .roomGrid { grid-template-columns: minmax(0, 1fr); max-width: 760px; }
          .finalActionsRow { flex-direction: column !important; }
        }

        @media (max-width: 700px) {
          .roomCard { grid-template-columns: minmax(0, 1fr); }
          .roomCard__mediaPane { aspect-ratio: auto; }
          .roomCard__rateSelect { width: 44px; height: 44px; }
          .roomCard__rateConditionsButton { width: 44px; height: 44px; }

          .bookingRateConditionsModalShell .modal-dialog {
            width: 100%;
            max-width: none;
            min-height: 100dvh;
            margin: 0;
            display: flex;
            align-items: flex-end;
          }

          .bookingRateConditionsModalShell .modal-content {
            max-height: 82dvh;
            border-radius: var(--be-radius-modal) var(--be-radius-modal) 0 0 !important;
            padding-bottom: env(safe-area-inset-bottom);
          }
        }

        @media (max-width: 520px) {
          .roomGrid { gap: 16px; }
          .roomCard__body { padding: 16px; }
          .roomCard__title { font-size: 1.2rem; }
          .roomCard__rateOption {
            min-height: 54px;
            padding: 4px 6px 4px 0;
          }
        }

        @media (hover: none), (pointer: coarse) {
          .roomCard__carouselArrow { display: none; }
        }

        @media (prefers-reduced-motion: reduce) {
          .roomCard__carouselDot::after,
          .roomCard__carouselArrow { transition: none; }
          .roomCard__rateSelect[aria-busy="true"] .roomCard__rateMarker { animation: none; }
        }



        /* ✅ COMPACT MODE (portátil): zoom real del contenido */
          @media (max-height: 820px) {
            .bookingPage { font-size: 14px; }

            .bookingShell { padding: 40px 16px 40px !important; }

            /* ✅ ZOOM 80% SOLO DEL CONTENIDO */
            .bookingViewport { zoom: 0.80; }

            /* ✅ Fallback para Firefox (no soporta zoom) */
            @supports not (zoom: 1) {
              .bookingViewport {
                transform: scale(0.80);
                transform-origin: top center;
                width: 100%; /* ✅ no romper el hueco disponible */
              }
            }


            .heroTitle { font-size: 2.4rem !important; }
            .heroSubtitle { font-size: 1.5rem !important; }

            .heroTitleWrap { margin-bottom: 12px !important; }
            .heroSubtitleWrap { margin-bottom: 18px !important; }

            .bookingCard { padding: 24px 22px 26px !important; }

            .wizardHeader { margin-bottom: 18px !important; }

            .searchGrid { gap: 14px !important; margin-bottom: 18px !important; }
            .ctaBtn { padding: 16px !important; font-size: 1.0rem !important; }
          }

           /* ✅ Mobile/tablet: 2nd screen sits higher so CONTINUE isn't hidden */
        @media (max-width: 900px) {
          .bookingShellNoHero {
            padding-top: 18px !important;
            padding-bottom: 18px !important;
            align-items: flex-start !important;
          }
        }

        /* Mobile friendly overrides */
        @media (max-width: 768px) {
          .bookingShell { padding: 28px 14px 28px !important; }
          .bookingCard { padding: 18px 16px !important; }
          .heroTitle { font-size: 2.1rem !important; }
          .heroSubtitle { font-size: 1.3rem !important; }

          .ctaBtn { width: 100% !important; }
          .finalActionsRow { flex-direction: column !important; gap: 10px !important; }
          .finalActionBtn { width: 100% !important; }

          .bookingGuestDetailsLayout__main .bookingGuestPanel {
            padding: 16px !important;
          }

          .bookingGuestForm__grid > .bookingGuestForm__field {
            grid-column: 1 / -1 !important;
          }

          .bookingGuestForm__specialRequestsGrid {
            grid-template-columns: minmax(0, 1fr);
          }

          .bookingGuestForm__control {
            min-height: 46px;
            font-size: 16px !important;
          }

          .bookingGuestForm__actions {
            flex-direction: column-reverse;
          }

          .bookingGuestForm__actions > button {
            width: 100%;
            min-height: 46px;
          }

          /* Mobile: native date inputs should not trigger iOS zoom */
          input.nativeDateInput { font-size: 16px !important; }
          select.nativeSelectInput { font-size: 16px !important; }
        }

        @media (max-width: 520px) {
          .bookingPage { font-size: 14px; }
          .fakeInput { padding: 12px 12px !important; font-size: 0.98rem !important; }
          .ctaBtn { padding: 14px !important; font-size: 0.98rem !important; }

        }

          /* ✅ Dates must stay in one line */
          .bookingSummaryContent__dates {
            white-space: nowrap;
            font-size: 0.92rem;
            letter-spacing: 0.02em;
          }

          /* ✅ Extra safety for very narrow phones */
          @media (max-width: 420px) {
            .bookingSummaryContent__dates {
              font-size: 0.82rem;
            }
          }



          /* Summary content is hosted inside the responsive drawer. */
          .bookingSummaryContent {
            width: 100%;
          }

          /* ✅ Stripe Elements breaks inside zoom/transform (iframes). Disable ONLY on payment step */
          .bookingFlowRoot.stripeSafe { zoom: 1 !important; transform: none !important; }
          .bookingFlowRoot.stripeSafe .bookingViewport { zoom: 1 !important; transform: none !important; width: 100% !important; }

          /* Keep Stripe Elements outside any compact-mode transform. */
          @media (max-width: 900px), (max-height: 820px) {
            .bookingFlowRoot.stripeSafe { zoom: 1 !important; transform: none !important; width: 100% !important; min-height: 100vh !important; }
          }







      `}</style>

        <BookingTopBar
          hotelName={hotelContext.hotel_name}
          hotelContext={hotelContext}
          hasDraft={hasBookingSession}
          step={step}
          lang={lang}
          setLang={setLang}
          bookingCurrencies={bookingCurrencies}
          bookingCurrency={bookingCurrency}
          currencyCatalog={bookingCurrencyCatalog}
          onCurrencyChange={handleBookingCurrencyChange}
          getRoomCategoryDisplayName={getRoomCategoryDisplayName}
          roomDraftIds={roomDraftIds}
          roomStateByDraftId={roomStateByDraftId}
          activeDraftId={Number(draftCtx.reservation_draft_id)}
          shoppingCriteria={{ checkIn, checkOut, adults, children }}
          t={t}
          onChangeDates={openCalendarRememberingPax}
          onChangeAdults={() => setAdultsOpen(true)}
          onChangeChildren={() => setChildrenOpen(true)}
          onChangeGuests={() => setGuestsOpen(true)}
          onRemoveRoom={handleRemoveRoomDraft}
          deletingDraftId={deletingDraftId}
          termsOpen={termsOpen}
          setTermsOpen={setTermsOpen}
          isMobile={isMobile}
          interactionDisabled={selectionPending || cartLoading || extrasLoading || mutatingExtraKey != null}
          canContinue={cartItemCount > 0 && !!draftCtx.reservation_draft_id}
          onContinueToExtras={goToExtras}
          bookingCart={bookingCart}
        />

        {/*18.01.2026 - leer textos de settings - 19.01.2026 - leer imagen background*/}
        <div
          className={`bookingFlowRoot ${hasBookingSession ? "bookingFlowRoot--withStay" : "bookingFlowRoot--search"} ${step === "extras" || step === "guest_details" ? "bookingFlowRoot--guest" : ""} ${step === "payment" ? "stripeSafe" : ""}`}
          data-booking-theme={bookingTheme}
        >
          <MainLayout
            hotelName={hotelContext.hotel_name}
            showHero={showBookingHeroOpeningMessage}
            variant={
              hasBookingSession && step === "categories"
                ? "rooms"
                : hasBookingSession && (step === "extras" || step === "guest_details")
                  ? "guest"
                  : "default"
            }
            heroClaim={getBookingEngineText("hero_claim")}
            heroSubtitle={getBookingEngineText("hero_subtitle")}
            heroBackgroundUrl={activeHeroBackgroundUrl}
          >
          
         {!hasBookingSession && step === "search" && (

          <>
          <WizardHeader title={t("select_your_stay")} />

            <div
              className="searchGrid"
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1.2fr 0.8fr 0.8fr",
                gap: "28px",
                marginBottom: "44px",
              }}
            >

              {isMobile ? (
                <NativeDateInput
                  id="be-checkin"
                  label={t("check_in")}
                  value={checkIn}
                  min={todayYMD}
                  onChange={(v) => {
                    if (!v) {
                      setCheckIn(null);
                      setCheckOut(null);
                      return;
                    }

                    setCheckIn(v);

                    const minNextCheckout = addDaysYMD(v, 1);
                    if (!checkOut || checkOut < minNextCheckout) {
                      setCheckOut(minNextCheckout || null);
                    }
                  }}
                />
              ) : (
                <FakeDateInput label={t("check_in")} value={checkIn} onClick={() => setCalendarOpen(true)} t={t} />
              )}

              {isMobile ? (
                <NativeDateInput
                  id="be-checkout"
                  label={t("check_out")}
                  value={checkOut}
                  min={checkIn ? addDaysYMD(checkIn, 1) : addDaysYMD(todayYMD, 1)}
                  disabled={!checkIn}
                  onChange={(v) => setCheckOut(v || null)}
                />
              ) : (
                <FakeDateInput label={t("check_out")} value={checkOut} onClick={() => setCalendarOpen(true)} t={t} />
              )}


              {isMobile ? (
                <NativeSelectInput
                  id="be-adults"
                  label={t("adults_label")}
                  value={adults}
                  options={Array.from({ length: Number(paxLimits?.max_adults || 1) }, (_, i) => {
                    const n = i + 1;
                    return {
                      value: n,
                      label: `${n} ${n > 1 ? t("adult_plural") : t("adult_singular")}`,
                    };
                  })}
                  onChange={(n) => setAdults(Number(n))}
                />
              ) : (
                <FakePaxInput
                  label={t("adults_label")}
                  value={`${adults} ${adults > 1 ? t("adult_plural") : t("adult_singular")}`}
                  onClick={() => setAdultsOpen(true)}
                />
              )}

              {isMobile ? (
                <NativeSelectInput
                  id="be-children"
                  label={t("children_label")}
                  value={children}
                  options={Array.from({ length: Number(paxLimits?.max_children || 0) + 1 }, (_, i) => {
                    const n = i;
                    return {
                      value: n,
                      label:
                        n === 0
                          ? t("no_children")
                          : `${n} ${n > 1 ? t("child_plural") : t("child_singular")}`,
                    };
                  })}
                  onChange={(n) => setChildren(Number(n))}
                />
              ) : (
                <FakePaxInput
                  label={t("children_label")}
                  value={children === 0 ? t("no_children")
                  : `${children} ${children > 1 ? t("child_plural") : t("child_singular")}`}
                  onClick={() => setChildrenOpen(true)}
                />
              )}
            </div>

            <CTAButton onClick={handleCheckAvailability} text={t("check_availability")} />
          </>
        )}

        {hasBookingSession && step === "categories" && (
          <>
                <WizardHeader title={t("choose_room")} />

                {categoriesLoading && (
                  <div style={{ color: "#bbb", textAlign: "center", padding: "40px 0" }}>
                     {t("loading_available_rooms")}
                  </div>
                )}

                {!categoriesLoading && shoppingError && (
                  <div className="bookingError" role="alert" style={{ textAlign: "center", padding: "28px 0" }}>
                    <div>{t(shoppingError)}</div>
                    <button
                      className="bookingSecondaryAction"
                      type="button"
                      onClick={() => setCategoriesReloadKey((value) => value + 1)}
                      style={{ marginTop: "14px", padding: "10px 16px" }}
                    >
                      {t("inline_rates_retry")}
                    </button>
                  </div>
                )}

                {!categoriesLoading && cartError && (
                  <div className="bookingError" role="alert" style={{ textAlign: "center", padding: "12px 0" }}>
                    <div>{t(cartError)}</div>
                    <button
                      className="bookingSecondaryAction"
                      type="button"
                      onClick={() => setCartReloadKey((value) => value + 1)}
                      style={{ marginTop: "14px", padding: "10px 16px" }}
                    >
                      {t("inline_rates_retry")}
                    </button>
                  </div>
                )}

                {!categoriesLoading && selectionError && !pricedCategories.some(
                  (category) => Number(category?.id) === Number(selectionError.categoryId)
                ) && (
                  <div className="bookingError" role="alert" style={{ textAlign: "center", padding: "18px 0" }}>
                    {t(selectionError.messageKey)}
                  </div>
                )}

                 {!categoriesLoading && !shoppingError && categories && pricedCategories.length === 0 && (
                   <div
                     style={{
                       maxWidth: "980px",
                       margin: "10px auto 0",
                       border: "1px solid rgba(230,195,106,0.30)",
                       background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
                       padding: isMobile ? "16px" : "22px",
                     }}
                   >
                     <div
                       style={{
                         color: "#e6c36a",
                         letterSpacing: "0.08em",
                         fontWeight: 900,
                         lineHeight: 1.4,
                       }}
                     >
                       {String(t("no_rooms_for_dates_combination"))
                         .replace("{{adults}}", String(adults))
                         .replace("{{children}}", String(children))}
                     </div>

                     <div style={{ marginTop: "10px", color: "#cfcfcf", lineHeight: 1.55 }}>
                       {t("no_rooms_for_dates_help")}
                     </div>

                     {showNoAvailabilityCapacityReason && (
                       <div
                         style={{
                           marginTop: "14px",
                           border: "1px solid rgba(255,179,179,0.35)",
                           background: "rgba(255,179,179,0.08)",
                           padding: "12px 12px",
                           color: "#ffb3b3",
                           lineHeight: 1.45,
                           fontWeight: 700,
                         }}
                       >
                         <div>{t("no_rooms_for_dates_reason_capacity")}</div>
                         {Number.isFinite(noAvailabilityHotelMaxOccupancy) && noAvailabilityHotelMaxOccupancy > 0 && (
                           <div style={{ marginTop: "6px" }}>
                             {String(t("hotel_max_occupancy_hint")).replace("{{max}}", String(noAvailabilityHotelMaxOccupancy))}
                           </div>
                         )}
                       </div>
                     )}

                     <div style={{ marginTop: "16px", height: "1px", background: "rgba(255,255,255,0.10)" }} />

                     <div
                       style={{
                         marginTop: "14px",
                         color: "#e6c36a",
                         letterSpacing: "0.10em",
                         fontWeight: 900,
                       }}
                     >
                       {t("no_rooms_for_dates_suggestions_title")}
                     </div>

                     {noAvailabilitySuggestionsLoading ? (
                       <div style={{ marginTop: "10px", color: "#bbb" }}>
                         {t("no_rooms_for_dates_suggestions_loading")}
                       </div>
                     ) : noAvailabilitySuggestions.length > 0 ? (
                       <div
                         style={{
                           marginTop: "12px",
                           display: "grid",
                           gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                           gap: "10px",
                         }}
                       >
                         {noAvailabilitySuggestions.map((s) => {
                           const key = String(s?.key || "");
                           const disabled = !!applyingSuggestedDatesKey;

                           return (
                             <button
                               key={key}
                               type="button"
                               disabled={disabled}
                               onClick={() => handleApplySuggestedDates(s.checkIn, s.checkOut)}
                               style={{
                                 textAlign: "left",
                                 padding: "14px 14px",
                                 border: "1px solid rgba(255,255,255,0.18)",
                                 background:
                                   disabled && applyingSuggestedDatesKey === key
                                     ? "rgba(255,255,255,0.10)"
                                     : "rgba(255,255,255,0.04)",
                                 color: "#fff",
                                 cursor: disabled ? "not-allowed" : "pointer",
                                 opacity: disabled ? 0.85 : 1,
                               }}
                             >
                               <div style={{ fontWeight: 900, letterSpacing: "0.04em" }}>
                                 {formatDateForLang(s.checkIn)} {"→"} {formatDateForLang(s.checkOut)}
                               </div>
                               <div style={{ marginTop: "8px", color: "#e6c36a", fontWeight: 800, letterSpacing: "0.08em" }}>
                                 {disabled && applyingSuggestedDatesKey === key
                                   ? t("loading_available_rooms")
                                   : t("no_rooms_for_dates_apply")}
                               </div>
                             </button>
                           );
                         })}
                       </div>
                     ) : (
                       <div style={{ marginTop: "10px", color: "#9b9b9b" }}>
                         {noAvailabilityEndpointUnsupported
                           ? t("no_rooms_for_dates_help")
                           : t("no_rooms_for_dates_no_suggestions")}
                       </div>
                     )}
                   </div>
                 )}

                 {!categoriesLoading && categories && pricedCategories.length > 0 && (
                   <div className="roomGrid">
                     {pricedCategories.map((c) => (
                     <RoomCategoryCard
                       key={c.id}
                       c={c}
                       t={t}
                       lang={lang}
                      beCopy={beRoomCategoryCopyMap?.[String(c.id)] || null}
                      amenities={roomCategoryAmenitiesMap?.[String(c.id)] || []}
                      coverUrl={roomCategoryAssetsMap?.[String(c.id)]?.cover?.public_url || null}
                      galleryUrls={(roomCategoryAssetsMap?.[String(c.id)]?.gallery || [])
                        .map((x) => (typeof x === "string" ? x : x?.public_url))
                        .filter(Boolean)}
                      rates={c.rates}
                      currencyCatalog={bookingCurrencyCatalog}
                      ratesError={Number(selectionError?.categoryId) === Number(c.id) ? t(selectionError.messageKey) : null}
                      selectingRateId={selectingRateId}
                      selectionDisabled={selectionPending || extrasNavigationPending}
                      onSelectRate={(rate) => handleSelectInlineRate(c, rate)}
                      onSetSelectionQuantity={(rate, quantity) => handleSetSelectionQuantity(c, rate, quantity)}
                      onViewRateConditions={(rate) => setRateConditionsDialog({ category: c, rate })}
                    />
                  ))}

                  <span className="bookingSrOnly" aria-live="polite" aria-atomic="true">
                    {selectionPending ? t("inline_rate_selecting") : ""}
                  </span>



                  </div>
                )}
          </>
        )}

        {hasDraft && step === "extras" && (
          <div className="bookingExtrasLayout">
            <main className="bookingExtrasLayout__main">
              <BookingExtrasStep
                catalog={extrasCatalog}
                loading={extrasLoading}
                error={extrasError}
                mutatingExtraKey={mutatingExtraKey}
                currency={bookingCurrency}
                currencyCatalog={bookingCurrencyCatalog}
                lang={lang}
                getRoomCategoryDisplayName={getRoomCategoryDisplayName}
                t={t}
                onToggle={handleSetExtraSelected}
                onRetry={() => setExtrasReloadKey((value) => value + 1)}
                onBack={handleBackFromExtras}
                onContinue={handleContinueFromExtras}
              />
            </main>

            <BookingSummaryPanel
              id="booking-summary-panel"
              className="bookingExtrasLayout__summary bookingGuestDetailsLayout__summary"
              variant="embedded"
              canEdit={false}
              hotelName={hotelContext.hotel_name}
              hotelContext={hotelContext}
              lang={lang}
              getRoomCategoryDisplayName={getRoomCategoryDisplayName}
              roomDraftIds={roomDraftIds}
              roomStateByDraftId={roomStateByDraftId}
              activeDraftId={Number(draftCtx.reservation_draft_id)}
              currency={bookingCurrency}
              currencyCatalog={bookingCurrencyCatalog}
              t={t}
              onRemoveRoom={handleRemoveRoomDraft}
              deletingDraftId={deletingDraftId}
              setTermsOpen={setTermsOpen}
              bookingCart={bookingCart}
            />
          </div>
        )}

        {hasDraft && step === "guest_details" && (
          <div className="bookingGuestDetailsLayout">
            <div className="bookingGuestDetailsLayout__main">
              <WizardHeader title={t("guest_details_title")} subtitle={t("guest_details_subtitle")} />

              <div
                className="bookingGuestPanel"
              >
              <div className="bookingGuestForm">
                <p className="bookingGuestForm__requiredNote">{t("required_fields_note")}</p>

                  <div
                    className="guestFormGrid bookingGuestForm__grid"
                  >
                  {/* row 1 */}
                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 2" }}>
                    <GuestFieldLabel htmlFor="booking-guest-gender">
                      {t("gender")}
                    </GuestFieldLabel>
                    <select
                      id="booking-guest-gender"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      value={guestForm.gender}
                      onChange={(e) => setGuestForm((p) => ({ ...p, gender: e.target.value }))}
                    >
                      <option value="male">{t("gender_male")}</option>
                      <option value="female">{t("gender_female")}</option>
                      <option value="other">{t("gender_other")}</option>
                      <option value="undisclosed">{t("gender_undisclosed")}</option>
                    </select>
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 5" }}>
                    <GuestFieldLabel
                      htmlFor="booking-guest-first-name"
                      required
                      requiredLabel={t("required_fields_note").replace(/^\*\s*/, "")}
                    >
                        {t("first_name")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-first-name"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      required
                      aria-required="true"
                      autoComplete="given-name"
                      value={guestForm.first_name}
                      onChange={(e) => setGuestForm((p) => ({ ...p, first_name: e.target.value }))}
                    />
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 5" }}>
                    <GuestFieldLabel
                      htmlFor="booking-guest-last-name"
                      required
                      requiredLabel={t("required_fields_note").replace(/^\*\s*/, "")}
                    >
                        {t("last_name")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-last-name"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      required
                      aria-required="true"
                      autoComplete="family-name"
                      value={guestForm.last_name}
                      onChange={(e) => setGuestForm((p) => ({ ...p, last_name: e.target.value }))}
                    />
                  </div>

                  {/* row 2 (Email + Phone) */}
                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 7" }}>
                    <GuestFieldLabel
                      htmlFor="booking-guest-email"
                      required
                      requiredLabel={t("required_fields_note").replace(/^\*\s*/, "")}
                    >
                        {t("email")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-email"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      type="email"
                      inputMode="email"
                      required
                      aria-required="true"
                      autoComplete="email"
                      value={guestForm.email}
                      onChange={(e) => setGuestForm((p) => ({ ...p, email: e.target.value }))}
                    />
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 5" }}>
                    <GuestFieldLabel
                      htmlFor="booking-guest-phone"
                      required
                      requiredLabel={t("required_fields_note").replace(/^\*\s*/, "")}
                    >
                        {t("phone")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-phone"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      type="tel"
                      inputMode="tel"
                      required
                      aria-required="true"
                      autoComplete="tel"
                      value={guestForm.phone}
                      onChange={(e) => setGuestForm((p) => ({ ...p, phone: e.target.value }))}
                    />
                  </div>

                  {/* row 3 (Address + City + Postal + Country in one line) */}
                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 5" }}>
                    <GuestFieldLabel htmlFor="booking-guest-address">
                      {t("address")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-address"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      autoComplete="street-address"
                      value={guestForm.address}
                      onChange={(e) => setGuestForm((p) => ({ ...p, address: e.target.value }))}
                    />
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 3" }}>
                    <GuestFieldLabel htmlFor="booking-guest-city">
                      {t("city")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-city"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      autoComplete="address-level2"
                      value={guestForm.city}
                      onChange={(e) => setGuestForm((p) => ({ ...p, city: e.target.value }))}
                    />
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 2" }}>
                    <GuestFieldLabel htmlFor="booking-guest-postal-code">
                      {t("postal_code")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-postal-code"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      autoComplete="postal-code"
                      value={guestForm.postal_code}
                      onChange={(e) => setGuestForm((p) => ({ ...p, postal_code: e.target.value }))}
                    />
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 2" }}>
                    <GuestFieldLabel htmlFor="booking-guest-country">
                      {t("country")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-country"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      autoComplete="country-name"
                      value={guestForm.country}
                      onChange={(e) => setGuestForm((p) => ({ ...p, country: e.target.value }))}
                    />
                  </div>

                  {/* row 4 (Birth date + ID type + ID number in one line) */}
                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 4" }}>
                    <GuestFieldLabel htmlFor="booking-guest-birth-date">
                      {t("birth_date")}
                    </GuestFieldLabel>
                    <span id="booking-guest-birth-date-format" className="bookingSrOnly">
                      {t("date_format_hint")}
                    </span>
                    <div className="bookingGuestForm__dateControlWrap">
                      <input
                        id="booking-guest-birth-date"
                        className="bookingGuestForm__control bookingGuestForm__dateControl nativeDateInput"
                        type="date"
                        lang={lang === "es" ? "es-ES" : "en-US"}
                        placeholder={t("date_format_hint")}
                        aria-describedby="booking-guest-birth-date-format"
                        style={inputStyle}
                        autoComplete="bday"
                        value={guestForm.birth_date}
                        onChange={(e) => setGuestForm((p) => ({ ...p, birth_date: e.target.value }))}
                      />
                      {!guestForm.birth_date ? (
                        <span className="bookingGuestForm__datePlaceholder" aria-hidden="true">
                          {t("date_format_hint")}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 3" }}>
                    <GuestFieldLabel htmlFor="booking-guest-id-type">
                      {t("id_type")}
                    </GuestFieldLabel>
                    <select
                      id="booking-guest-id-type"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      value={guestForm.id_type}
                      onChange={(e) => setGuestForm((p) => ({ ...p, id_type: e.target.value }))}
                    >
                      <option value="">—</option>
                      <option value="passport">{t("id_type_passport")}</option>
                      <option value="id">{t("id_type_national_id")}</option>
                      <option value="driving_license">{t("id_type_driving_license")}</option>
                    </select>
                  </div>

                  <div className="bookingGuestForm__field" style={{ gridColumn: "span 5" }}>
                    <GuestFieldLabel htmlFor="booking-guest-id-number">
                      {t("id_number")}
                    </GuestFieldLabel>
                    <input
                      id="booking-guest-id-number"
                      className="bookingGuestForm__control"
                      style={inputStyle}
                      value={guestForm.id_number}
                      onChange={(e) => setGuestForm((p) => ({ ...p, id_number: e.target.value }))}
                    />
                  </div>
                  

                  {/* ✅ Special requests per room */}
                  <div className="bookingGuestForm__specialRequests">
                    
                    {(() => {
                      const ids = Array.isArray(roomDraftIds) ? roomDraftIds : [];
                      const n = ids.length;

                      return (
                        <div
                          className="specialRequestsGrid bookingGuestForm__specialRequestsGrid"
                          style={{ gridTemplateColumns: n >= 2 ? undefined : "1fr" }}
                        >
                          {ids.map((draftId, idx) => {
                            const isLastOdd = n >= 3 && n % 2 === 1 && idx === n - 1;

                            return (
                              <div className="bookingGuestForm__field" key={draftId} style={isLastOdd ? { gridColumn: "1 / -1" } : undefined}>
                                <GuestFieldLabel htmlFor={`booking-guest-special-request-${draftId}`}>
                                  {t("special_request_for_room").replace("{{n}}", String(idx + 1))}
                                </GuestFieldLabel>

                                <textarea
                                  id={`booking-guest-special-request-${draftId}`}
                                  className="bookingGuestForm__control"
                                  rows={1}
                                  style={{
                                    ...inputStyle,
                                    WebkitAppearance: "none",
                                    appearance: "none",
                                  }}
                                  value={guestForm?.special_requests_by_room?.[Number(draftId)] || ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setGuestForm((p) => ({
                                      ...p,
                                      special_requests_by_room: {
                                        ...(p?.special_requests_by_room || {}),
                                        [Number(draftId)]: v,
                                      },
                                    }));
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                                </div>


                </div>

                {/* ✅ Terms acceptance (required) */}
                <div className="bookingGuestForm__terms">
                  <input
                    id="booking-guest-accept-terms"
                    className="bookingGuestForm__termsCheckbox"
                    type="checkbox"
                    checked={!!acceptTerms}
                    disabled={guestSaving}
                    onChange={handleTermsAcceptanceChange}
                    aria-labelledby="booking-guest-terms-copy"
                  />

                  <div id="booking-guest-terms-copy" className="bookingGuestForm__termsCopy">
                    {lang === "es" ? (
                      <>
                        Al completar esta reserva, estoy de acuerdo con los{" "}
                        <button
                          className="bookingGuestForm__termsButton"
                          type="button"
                          onClick={() => {
                            try {
                              // abre el popup de terms en la sidebar (si existe)
                              setTermsOpen(true);
                              const el = document.querySelector('[data-be-terms-anchor="1"]');
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                            } catch (e) {}
                          }}
                        >
                          términos y condiciones
                        </button>{" "}
                        de la misma.
                      </>
                    ) : (
                      <>
                        By completing this booking, I agree to the{" "}
                        <button
                          className="bookingGuestForm__termsButton"
                          type="button"
                          onClick={() => {
                            try {
                              setTermsOpen(true);
                              const el = document.querySelector('[data-be-terms-anchor="1"]');
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                            } catch (e) {}
                          }}
                        >
                          terms &amp; conditions
                        </button>
                        .
                      </>
                    )}

                  </div>
                </div>

                {!cardRequired ? (
                  <p data-booking-no-card-notice="true" style={{ color: "var(--be-text-muted)", margin: "16px 0 0" }}>
                    {t("booking_without_card_hint")}
                  </p>
                ) : null}
                {guestError ? (
                  <p role="alert" style={{ color: "var(--be-error)", margin: "16px 0 0" }}>{guestError}</p>
                ) : null}

                {/* Actions */}
                <div className="bookingGuestForm__actions">

                  <button
                    className="bookingSecondaryAction"
                    type="button"
                    onClick={handleBackFromGuestDetails}
                    disabled={guestSaving || extrasLoading}
                  >
                    {t("back")}
                  </button>

                  <button
                    className="bookingPrimaryAction"
                    type="button"
                    onClick={submitGuestDetails}
                    disabled={guestSaving || !acceptTerms}
                  >
                    {guestSaving
                      ? t(cardRequired ? "saving" : "booking_confirming")
                      : t(cardRequired ? "continue" : "booking_confirm")}
                  </button>

                </div>
              </div>

              {/* GDPR notice */}
                <div className="bookingGuestForm__gdpr">
                  {t("gdpr_notice")}
                </div>
              </div>
            </div>

            <BookingSummaryPanel
              id="booking-summary-panel"
              className="bookingGuestDetailsLayout__summary"
              variant="embedded"
                canEdit={false}
                hotelName={hotelContext.hotel_name}
                hotelContext={hotelContext}
                lang={lang}
                getRoomCategoryDisplayName={getRoomCategoryDisplayName}
                roomDraftIds={roomDraftIds}
                roomStateByDraftId={roomStateByDraftId}
                activeDraftId={Number(draftCtx.reservation_draft_id)}
                currency={bookingCurrency}
                currencyCatalog={bookingCurrencyCatalog}
                t={t}
                onRemoveRoom={handleRemoveRoomDraft}
                deletingDraftId={deletingDraftId}
                setTermsOpen={setTermsOpen}
                bookingCart={bookingCart}
              />
          </div>
        )}

        
        {hasDraft && step === "payment" && (
          <>
            <WizardHeader
              title={t("payment_details_title")}
              subtitle={t("payment_details_subtitle")}
            />

            <div
              className="bookingPaymentPanel"
              style={{
                maxWidth: "860px",
                margin: "0 auto",
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(255,255,255,0.03)",
                padding: "14px",
              }}
            >
              {!stripePromise || !paymentSetup ? (
                paymentSetupErr ? (
                  <div style={{ color: "#ffb3b3", padding: "10px" }}>
                    {/* SECURITY: don't expose raw backend/Stripe error details to the public booking page */}
                    {t("payment_error_generic")}
                  </div>

                ) : null
              ) : (
                <Elements stripe={stripePromise}>
                  <PaymentSetupForm
                    apiUrl={process.env.REACT_APP_API_URL}
                    reservation_group_id={Number(draftCtx.reservation_group_id)}
                    business_unit_id={hotelContext?.business_unit_id ?? businessUnitId ?? null}
                    public_session_token={String(draftCtx?.public_session_token || "").trim()}
                    client_secret={paymentSetup?.client_secret || null}               // ✅ NEW
                    stripe_account_id={paymentSetup?.stripe_account_id || null}       // ✅ NEW (for logs/validation)
                    billingDetails={{
                      name: `${guestForm.first_name || ""} ${guestForm.last_name || ""}`.trim() || null,
                      email: (guestForm.email || "").trim() || null,
                      phone: (guestForm.phone || "").trim() || null,
                    }}
                    booking_engine_language={lang}
                    currency_code={bookingCurrency}
                    fx_rate_set_id={currencyConfiguration?.current_rate_set_id || null}
                    bookingTheme={bookingTheme}
                    onBack={() => setStep("guest_details")}
                    onDone={(payload) => {
                      //console.log("[BOOKING][PAYMENT][DONE]", payload);
                      setPaymentResult(payload || null);
                      setStep("confirmation");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    t={t}
                  />
                </Elements>
              )}



            </div>
          </>
        )}


        {hasDraft && step === "confirmation" && (() => {
        // ✅ intenta sacar el perfil público del hotel (según tu estructura actual)
        const p =
          hotelContext?.booking_engine_profile ||
          hotelContext?.booking_engine_profile_public ||
          hotelContext?.booking_engine_profile_raw ||
          null;

        const hotelPublicName =
          String(p?.public_hotel_name || hotelContext?.hotel_name || "").trim() || "—";

        const addressLine = String(p?.address_line || "").trim();
        const city = String(p?.city || "").trim();
        const postal = String(p?.postal_code || "").trim();
        const country = String(p?.country || "").trim();

        const hotelAddress = [addressLine, [postal, city].filter(Boolean).join(" "), country]
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .join(", ");

        const pickFirstEmail = (arr) => {
          if (!Array.isArray(arr) || !arr.length) return null;
          const first = arr[0];
          const v = (first && typeof first === "object") ? (first.email || first.value || first.address) : first;
          const s = String(v || "").trim();
          return s || null;
        };

        const pickFirstPhone = (arr) => {
          if (!Array.isArray(arr) || !arr.length) return null;
          const first = arr[0];
          const v = (first && typeof first === "object") ? (first.phone || first.value || first.number) : first;
          const s = String(v || "").trim();
          return s || null;
        };

        const hotelEmail = pickFirstEmail(p?.emails);
        const hotelPhone = pickFirstPhone(p?.phones);

        // The commit response is the final monetary authority. The cart shown
        // before payment can legitimately be older than the rows persisted by
        // commit (for example when a required extra is reconciled at the last
        // moment), so the confirmation must prefer commit totals and lines.
        const committedBooking =
          paymentResult?.commit && typeof paymentResult.commit === "object"
            ? paymentResult.commit
            : null;
        const confirmationCurrency =
          committedBooking?.totals?.currency ||
          paymentResult?.totals?.currency ||
          paymentResult?.currency ||
          bookingCart?.currency ||
          bookingCurrency;
        const confirmationGrandTotal =
          committedBooking?.totals?.grand_total ??
          paymentResult?.totals?.grand_total ??
          paymentResult?.grand_total ??
          paymentResult?.total ??
          bookingCart?.grand_total ??
          bookingCart?.total;
        const hasCommittedExtras = Array.isArray(committedBooking?.extras);
        const committedExtraRows = hasCommittedExtras ? committedBooking.extras : [];
        const confirmationExtras = hasCommittedExtras
          ? Array.from(committedExtraRows.reduce((byExtraId, row) => {
              const extraId = Number(row?.extra_id);
              if (!Number.isFinite(extraId) || extraId <= 0) return byExtraId;
              const current = byExtraId.get(extraId) || {
                extra_id: extraId,
                name: String(row?.name || "").trim(),
                total: 0,
                currency: confirmationCurrency,
              };
              const amount = Number(row?.calculated_amount ?? row?.total ?? 0);
              current.total += Number.isFinite(amount) ? amount : 0;
              byExtraId.set(extraId, current);
              return byExtraId;
            }, new Map()).values())
          : (Array.isArray(bookingCart?.extras) ? bookingCart.extras : []);

        const handleCloseTab = () => {
          // ✅ Cerrar pestaña solo funciona si la pestaña fue abierta por JS (window.open)
          try {
            if (window.opener) {
              window.close();
              return;
            }
          } catch (e) {}

          // ✅ Fallback: vuelve al inicio del booking engine (ajusta si tu ruta es distinta)
          try {
            if (window.history.length > 1) {
              window.history.back();
              return;
            }
          } catch (e) {}

          // ✅ último fallback: recarga la home del motor para este slug
          try {
            window.location.href = `${window.location.origin}/booking/${slug}`;
          } catch (e) {}
        };

        return (
          <>
            {/* ✅ PRINT CSS */}
            <style>{`
              @media print {
                @page { size: A4; margin: 12mm; }

                html, body {
                  background: #fff !important;
                  color: #000 !important;
                }

                /* ✅ mata el look "dark screenshot" */
                * {
                  background: transparent !important;
                  color: #000 !important;
                  box-shadow: none !important;
                  text-shadow: none !important;
                  filter: none !important;
                  -webkit-filter: none !important;
                  outline: none !important;
                }

                /* ✅ no imprimir cosas UI */
                .be-no-print { display: none !important; }
                .bookingTopBar { display: none !important; }
                .bookingFlowRoot { padding-top: 0 !important; }

                /* ✅ el área imprimible debe ser blanco, limpio y ancho */
                #be-print-area {
                  background: #fff !important;
                  color: #000 !important;
                  max-width: none !important;
                  width: 100% !important;
                  border: 1px solid #000 !important;
                  border-radius: 0 !important;
                  padding: 10mm !important;
                  margin: 0 !important;
                }

                /* ✅ separadores negros */
                .be-print-sep {
                  background: #000 !important;
                  height: 1px !important;
                }

                /* ✅ links en negro */
                a, a:visited {
                  color: #000 !important;
                  text-decoration: none !important;
                }

                /* ✅ tipografía */
                b, strong { font-weight: 700 !important; }
              }
            `}</style>


            {/* ✅ header no se imprime */}
            <div className="be-no-print">
              <WizardHeader
                title={t("reservation_confirmed_title")}
                subtitle={t("reservation_confirmed_subtitle")}
              />
            </div>

            <div
              id="be-print-area"
              className="be-print-card"
              style={{
                maxWidth: "860px",
                margin: "0 auto",
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(255,255,255,0.03)",
                padding: "18px",
                borderRadius: "12px",
              }}
            >
              {/* ✅ top row: title left + hotel contact right */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "16px",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ minWidth: "240px" }}>
                  <div
                    className="be-print-title"
                    style={{ color: "#e6c36a", letterSpacing: "0.14em", fontWeight: 900 }}
                  >
                    {t("booking_summary")}
                  </div>
                  <div className="be-print-muted" style={{ marginTop: "6px", color: "#aaa", fontSize: "0.92rem" }}>
                    {t("reservation_confirmed_subtitle")}
                  </div>
                </div>

                <div
                  style={{
                    textAlign: "right",
                    lineHeight: 1.5,
                    maxWidth: "380px",
                  }}
                >
                  <div style={{ fontWeight: 900, letterSpacing: "0.10em" }}>
                    {hotelPublicName}
                  </div>

                  {hotelAddress ? (
                    <div className="be-print-muted" style={{ color: "#bbb", fontSize: "0.92rem" }}>
                      {hotelAddress}
                    </div>
                  ) : null}

                  {hotelPhone ? (
                    <div className="be-print-muted" style={{ color: "#bbb", fontSize: "0.92rem" }}>
                      {hotelPhone}
                    </div>
                  ) : null}

                  {hotelEmail ? (
                    <div className="be-print-muted" style={{ color: "#bbb", fontSize: "0.92rem" }}>
                      {hotelEmail}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="be-print-sep" style={{ marginTop: "14px", height: "1px", background: "rgba(255,255,255,0.10)" }} />

              {/* ✅ voucher body */}
              <div style={{ marginTop: "14px", lineHeight: 1.7, color: "var(--be-text)" }}>
                {committedBooking?.booking_engine_card_policy === "not_required" ? (
                  <p data-booking-no-card-confirmation="true" style={{ color: "var(--be-text-muted)" }}>
                    {t("booking_without_card_confirmed")}
                  </p>
                ) : null}
                <div>
                  <span className="be-print-muted" style={{ color: "#999" }}>{t("label_locator")}:</span>{" "}
                  <b>{paymentResult?.reservation_group_locator || "—"}</b>
                </div>

                <div style={{ marginTop: "10px" }}>
                  <span className="be-print-muted" style={{ color: "#999" }}>{t("label_booker")}:</span>{" "}
                  <b>{`${guestForm.first_name || ""} ${guestForm.last_name || ""}`.trim() || "—"}</b>
                </div>

                <div>
                  <span className="be-print-muted" style={{ color: "#999" }}>{t("label_email")}:</span>{" "}
                  <b>{guestForm.email || "—"}</b>
                </div>

                <div>
                  <span className="be-print-muted" style={{ color: "#999" }}>{t("label_phone")}:</span>{" "}
                  <b>{guestForm.phone || "—"}</b>
                </div>

                <div className="be-print-sep" style={{ marginTop: "14px", height: "1px", background: "rgba(255,255,255,0.08)" }} />

                {(Array.isArray(roomDraftIds) ? roomDraftIds : []).map((draftId, idx) => {
                  const st = roomStateByDraftId?.[Number(draftId)] || {};
                  const canonicalCartRoom = (Array.isArray(bookingCart?.items) ? bookingCart.items : [])
                    .find((item) => Number(item?.reservation_draft_id) === Number(draftId));
                  const committedTotalsAreAuthoritative =
                    Number.isFinite(Number(committedBooking?.totals?.grand_total));
                  const committedRoomTotal = Number(
                    st?.selectedRate?.base_min_total ?? canonicalCartRoom?.base_room_total
                  );
                  const displayRoomTotal = Number(st?.selectedRate?.min_total);
                  const usesCommittedCurrency =
                    committedTotalsAreAuthoritative && Number.isFinite(committedRoomTotal);
                  const total = usesCommittedCurrency ? committedRoomTotal : displayRoomTotal;
                  const roomCurrency = usesCommittedCurrency
                    ? confirmationCurrency
                    : (st?.selectedRate?.currency || bookingCart?.currency || bookingCurrency);

                  return (
                    <div key={draftId} style={{ marginTop: "14px" }}>
                      <div style={{ fontWeight: 900, letterSpacing: "0.12em" }}>
                        {t("label_room_n").replace("{{n}}", String(idx + 1))}
                      </div>

                      <div>
                        <span className="be-print-muted" style={{ color: "#999" }}>{t("label_dates")}:</span>{" "}
                        <b>{st?.checkIn || "—"} → {st?.checkOut || "—"}</b>
                      </div>

                      <div>
                        <span className="be-print-muted" style={{ color: "#999" }}>{t("label_guests")}:</span>{" "}
                        <b>{st?.adults ?? "—"}</b> {t("adult_plural")},{" "}
                        <b>{st?.children ?? "—"}</b> {t("child_plural")}
                      </div>

                      <div>
                        <span className="be-print-muted" style={{ color: "#999" }}>{t("label_category")}:</span>{" "}
                        <b>{getRoomCategoryDisplayName(st?.selectedCategory) || "—"}</b>
                      </div>

                      <div>
                        <span className="be-print-muted" style={{ color: "#999" }}>{t("label_rate")}:</span>{" "}
                        <b>{st?.selectedRate?.name || "—"}</b>
                      </div>

                      <div style={{ marginTop: "8px" }}>
                        <span className="be-print-muted" style={{ color: "#999" }}>{t("label_total")}:</span>{" "}
                        <b>{Number.isFinite(total)
                          ? formatBookingMoney(total, roomCurrency, lang, bookingCurrencyCatalog)
                          : "—"}</b>
                      </div>
                    </div>
                  );
                })}

                {confirmationExtras.length > 0 ? (
                  <div style={{ marginTop: "16px" }}>
                    <div style={{ fontWeight: 900, letterSpacing: "0.12em" }}>
                      {t("extras_selected_title")}
                    </div>
                    {confirmationExtras.map((extra) => (
                      <div
                        key={extra?.extra_id || extra?.name}
                        style={{ display: "flex", justifyContent: "space-between", gap: "16px", marginTop: "5px" }}
                      >
                        <span>{extra?.name || "—"}</span>
                        <b>{Number(extra?.total) === 0
                          ? t("extras_included")
                          : formatBookingMoney(
                              extra?.total,
                              extra?.currency || confirmationCurrency,
                              lang,
                              bookingCurrencyCatalog
                            )}</b>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="be-print-sep" style={{ marginTop: "16px", height: "1px", background: "rgba(255,255,255,0.08)" }} />

                <div style={{ marginTop: "14px", fontSize: "1.4rem", fontWeight: 900 }}>
                  {t("label_total_all")}: {formatBookingMoney(
                    confirmationGrandTotal,
                    confirmationCurrency,
                    lang,
                    bookingCurrencyCatalog
                  )}
                </div>
              </div>

              {/* ✅ buttons never printed */}
              <div className="be-no-print" style={{ marginTop: "18px", display: "flex", gap: "12px" }}>
                <button
                  className="bookingPrimaryAction"
                  type="button"
                  onClick={() => window.print()}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "linear-gradient(90deg, #caa24b, #e6c36a)",
                    border: "none",
                    color: "#111",
                    fontWeight: 900,
                    letterSpacing: "0.14em",
                    cursor: "pointer",
                  }}
                >
                  {t("print_save_pdf")}
                </button>

                <button
                  className="bookingSecondaryAction"
                  type="button"
                  onClick={handleCloseTab}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                    fontWeight: 900,
                    letterSpacing: "0.14em",
                    cursor: "pointer",
                  }}
                >
                  {t("close_tab")}
                </button>
              </div>
            </div>
          </>
        );
      })()}





      </MainLayout>
      </div>

      {calendarOpen && (
        <CalendarModal
          lang={lang}
          minDate={new Date()}
          onClose={() => setCalendarOpen(false)}
          onSelect={async (ci, co) => {
            setCalendarOpen(false);

            //console.log("[BOOKING][PUBLIC][CHANGE_DATES]", { hasDraft, ci, co, adults, children });

            if (!hasDraft) {
              setCheckIn(ci);
              setCheckOut(co);
              return;
            }

            await createOrUpdateDraftAndGo({
              nextCheckIn: ci,
              nextCheckOut: co,
              nextAdults: paxBeforeDatesChange.adults,
              nextChildren: paxBeforeDatesChange.children,
            });
          }}
          t={t}
        />
      )}



      {adultsOpen && (
        <AdultsModal
          value={adults}
          max={paxLimits.max_adults}
          onClose={() => setAdultsOpen(false)}
          onConfirm={async (v) => {
            setAdultsOpen(false);

            //console.log("[BOOKING][PUBLIC][CHANGE_ADULTS]", { hasDraft, nextAdults: v, checkIn, checkOut, children });

            // ✅ Pantalla 1: solo UI (eliges adultos ANTES del botón)
            if (!hasDraft) {
              setAdults(v);
              return;
            }


            // ✅ Pantalla 2: PATCH manteniendo IDs
            await createOrUpdateDraftAndGo({
              nextCheckIn: checkIn,
              nextCheckOut: checkOut,
              nextAdults: v,
              nextChildren: children,
            });
          }}

        />
      )}

      {childrenOpen && (
        <ChildrenModal
          value={children}
          max={paxLimits.max_children}
          onClose={() => setChildrenOpen(false)}
          onConfirm={async (v) => {
            setChildrenOpen(false);

            //console.log("[BOOKING][PUBLIC][CHANGE_CHILDREN]", { hasDraft, nextChildren: v, checkIn, checkOut, adults });

            if (!hasDraft) {
              setChildren(v);
              return;
            }


            await createOrUpdateDraftAndGo({
              nextCheckIn: checkIn,
              nextCheckOut: checkOut,
              nextAdults: adults,
              nextChildren: v,
            });
          }}

        />
      )}

      {guestsOpen && (
        <GuestsModal
          adults={adults}
          children={children}
          maxAdults={paxLimits.max_adults}
          maxChildren={paxLimits.max_children}
          onClose={() => setGuestsOpen(false)}
          onConfirm={async (nextGuests) => {
            setGuestsOpen(false);
            await createOrUpdateDraftAndGo({
              nextCheckIn: checkIn,
              nextCheckOut: checkOut,
              nextAdults: nextGuests.adults,
              nextChildren: nextGuests.children,
            });
          }}
          t={t}
        />
      )}
      <RateConditionsModal
        value={rateConditionsDialog}
        categoryName={getRoomCategoryDisplayName(rateConditionsDialog?.category)}
        lang={lang}
        t={t}
        onClose={() => setRateConditionsDialog(null)}
      />




    </>
  );

}

/* ================== UI COMPONENTS ================== */

function MainLayout({
  hotelName,
  children,
  showHero = true,
  variant = "default",
  heroClaim = "",
  heroSubtitle = "",
  heroBackgroundUrl = null,
}) {
  const isRoomsVariant = variant === "rooms";
  const isGuestVariant = variant === "guest";
  const baseTop = showHero ? 40 : 80;
  const baseBottom = showHero ? 50 : 80;

  // Let the viewport fill the available width up to a sane reading cap.
  const maxContentWidth = "1600px";

  const hasBg = !!heroBackgroundUrl;

  return (
    <div
      className={`bookingPage bookingShell ${showHero ? "" : "bookingShellNoHero"} ${isRoomsVariant ? "bookingShell--rooms" : ""} ${isGuestVariant ? "bookingShell--guest" : ""}`}
      style={{
        minHeight: "100dvh",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",

        background: hasBg
          ? "#000"
          : "radial-gradient(ellipse at center top, #1a1a1a 0%, #000 65%)",

        color: "#fff",
        display: "flex",
        alignItems: showHero ? "center" : "flex-start",
        justifyContent: "center",
        paddingTop: `${showHero ? 24 : baseTop}px`,
        paddingBottom: `${showHero ? 30 : baseBottom}px`,
        paddingRight: "20px",
        paddingLeft: "20px",
        transform: showHero ? "translateY(-60px)" : "none",
      }}
    >
      {hasBg && (
        <div
          className="bookingHeroImage"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,

            backgroundImage: `url(${heroBackgroundUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",

            transform: "scale(1.06)",
            filter: "blur(6px) brightness(0.62) contrast(1.08)",
            opacity: 0.95,
          }}
        />
      )}

      {hasBg && (
        <div
          className="bookingHeroScrim"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            background:
              "linear-gradient(rgba(0,0,0,0.18), rgba(0,0,0,0.42))",
          }}
        />
      )}

      <div
        className={`bookingViewport ${showHero ? "heroWrap" : ""} ${isRoomsVariant ? "bookingViewport--rooms" : ""} ${isGuestVariant ? "bookingViewport--guest" : ""}`}
        style={{
          position: "relative",
          zIndex: 1,

          width: "100%",
          maxWidth: maxContentWidth,
          margin: "0 auto",
          boxSizing: "border-box",
          minWidth: 0,
        }}
      >
        {showHero && (
          <>
            <div className="heroTitleWrap" style={{ textAlign: "center", marginBottom: "30px" }}>
              <div
                className="heroTitle"
                style={{
                  fontSize: "4.2rem",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  color: "#e6c36a",
                  textTransform: "uppercase",
                }}
              >
                {hotelName}
              </div>
            </div>

            <div className="heroSubtitleWrap" style={{ textAlign: "center", marginBottom: "55px" }}>
              <div className="heroSubtitle" style={{ fontSize: "2.6rem", fontWeight: 300, marginBottom: "14px" }}>
                {heroClaim}
              </div>
              <div className="bookingMutedText" style={{ color: "#bbb" }}>
                {heroSubtitle}
              </div>
            </div>
          </>
        )}

        <div
          className={`bookingCard ${isRoomsVariant ? "bookingCard--rooms" : ""} ${isGuestVariant ? "bookingCard--guest" : ""}`}
          style={{
            background:
              "linear-gradient(180deg, rgba(20,20,20,0.96), rgba(5,5,5,0.96))",
            border: "1px solid #2a2a2a",
            padding: "44px 38px 48px",
            boxShadow: "0 40px 120px rgba(0,0,0,0.75)",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}


export function resolveBookingCurrency(configuration, selectedCurrency = "") {
  return normalizeCurrencyCode(selectedCurrency) ||
    normalizeCurrencyCode(configuration?.base_currency_code) ||
    "EUR";
}

export function formatBookingMoney(value, currency, lang, currencyCatalog = []) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;

  const locale = lang === "es" ? "es-ES" : "en-GB";
  const currencyCode = normalizeCurrencyCode(currency);
  const minorUnits = getCurrencyMinorUnits(currencyCode, currencyCatalog);

  try {
    if (currencyCode) {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: minorUnits,
        maximumFractionDigits: minorUnits,
      }).format(amount);
    }
  } catch {
    // Fall through to the symbol-based representation below.
  }

  return `${currency || "€"}${new Intl.NumberFormat(locale, {
    minimumFractionDigits: minorUnits,
    maximumFractionDigits: minorUnits,
  }).format(amount)}`;
}

function formatBookingTopBarDate(value, lang) {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || "—";

  const [year, month, day] = raw.split("-").map(Number);
  try {
    return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "en-GB", {
      day: "numeric",
      month: "short",
    }).format(new Date(year, month - 1, day));
  } catch {
    return raw;
  }
}

export function RateConditionsModal({
  value,
  categoryName,
  lang = "en",
  t = (key, fallback) => (fallback ?? key),
  onClose,
}) {
  const rate = value?.rate || null;
  const rateName = String(rate?.name || "").trim() || t("rate", "Rate");
  const refundableCopy = rate?.is_refundable === true
    ? t("inline_rate_refundable")
    : t("inline_rate_non_refundable");
  const cancellationPolicy = String(rate?.cancellation_policy || "").trim()
    || t("inline_rate_conditions_no_policy");

  const penaltyCopy = (() => {
    const explicit = String(rate?.penalty_description || "").trim();
    if (explicit) return explicit;

    const penaltyType = String(rate?.penalty_type || "").trim().toLowerCase();
    const penaltyAmount = Number(rate?.penalty_amount);
    if (!penaltyType || !Number.isFinite(penaltyAmount)) return "";

    if (penaltyType.includes("percent")) {
      return String(t("inline_rate_penalty_percent"))
        .replace("{{amount}}", String(penaltyAmount));
    }

    const penaltyCurrency = rate?.penalty_currency || rate?.base_currency || rate?.currency || "EUR";
    const formattedAmount = formatBookingMoney(penaltyAmount, penaltyCurrency, lang);
    return String(t("inline_rate_penalty_fixed"))
      .replace("{{amount}}", formattedAmount || String(penaltyAmount));
  })();

  return (
    <Modal
      id="booking-rate-conditions-dialog"
      show={!!rate}
      onHide={onClose}
      centered
      scrollable
      restoreFocus
      className="bookingTermsModalShell bookingRateConditionsModalShell"
      backdropClassName="bookingTermsBackdrop"
      contentClassName="bookingTermsModal bookingRateConditionsModal"
      aria-labelledby="booking-rate-conditions-title"
    >
      <Modal.Header>
        <div className="bookingRateConditionsModal__heading">
          <span className="bookingRateConditionsModal__eyebrow">
            {t("inline_rate_conditions_title")}
          </span>
          <Modal.Title
            id="booking-rate-conditions-title"
            className="bookingRateConditionsModal__title"
          >
            {rateName}
          </Modal.Title>
          {categoryName ? (
            <div className="bookingRateConditionsModal__category">{categoryName}</div>
          ) : null}
        </div>

        <button
          className="roomCard__rateConditionsClose"
          type="button"
          aria-label={t("inline_rate_conditions_close")}
          onClick={onClose}
        >
          ×
        </button>
      </Modal.Header>

      <Modal.Body>
        <dl className="bookingRateConditionsModal__body">
          <div className="bookingRateConditionsModal__item">
            <dt>{t("inline_rate_conditions_refundability")}</dt>
            <dd>{refundableCopy}</dd>
          </div>
          <div className="bookingRateConditionsModal__item">
            <dt>{t("inline_rate_conditions_cancellation")}</dt>
            <dd>{cancellationPolicy}</dd>
          </div>
          {penaltyCopy ? (
            <div className="bookingRateConditionsModal__item">
              <dt>{t("inline_rate_conditions_penalty")}</dt>
              <dd>{penaltyCopy}</dd>
            </div>
          ) : null}
        </dl>
      </Modal.Body>
    </Modal>
  );
}

function BookingTermsModal({
  open,
  onClose,
  hotelContext,
  lang = "en",
  t = (key, fallback) => (fallback ?? key),
}) {
  const rows = Array.isArray(hotelContext?.booking_engine_terms)
    ? hotelContext.booking_engine_terms
    : [];
  const visibleTerms = rows
    .slice()
    .sort((a, b) => (Number(a?.sort_order) || 0) - (Number(b?.sort_order) || 0))
    .map((item, index) => ({
      id: item?.id || index,
      title: String(item?.title?.[lang] || "").trim(),
      text: String(item?.text?.[lang] || "").trim(),
    }))
    .filter((item) => item.title || item.text);

  return (
    <Modal
      show={!!open}
      onHide={onClose}
      centered
      scrollable
      size="lg"
      className="bookingTermsModalShell"
      backdropClassName="bookingTermsBackdrop"
      contentClassName="bookingTermsModal"
      aria-labelledby="booking-terms-title"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title id="booking-terms-title" className="bookingTermsModal__title">
          {t("booking_terms_link", "Booking terms & conditions")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {visibleTerms.length > 0 ? visibleTerms.map((item) => (
          <section key={item.id} className="bookingTermsModal__section">
            {item.title ? <h3>{item.title}</h3> : null}
            {item.text ? <div>{item.text}</div> : null}
          </section>
        )) : (
          <div className="bookingTermsModal__empty">
            {t("booking_terms_empty", "No terms configured yet.")}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

function useBookingMediaQuery(query) {
  const readMatch = () => (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(query).matches
  );
  const [matches, setMatches] = useState(readMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener?.("change", handleChange);
    return () => media.removeEventListener?.("change", handleChange);
  }, [query]);

  return matches;
}

export function BookingExtrasStep({
  catalog,
  loading = false,
  error = null,
  mutatingExtraKey = null,
  currency = "EUR",
  currencyCatalog = [],
  lang = "en",
  getRoomCategoryDisplayName = (category) => String(category?.name || "").trim(),
  t = (key, fallback) => fallback ?? key,
  onToggle,
  onRetry,
  onBack,
  onContinue,
}) {
  const roomSections = (Array.isArray(catalog?.rooms) ? catalog.rooms : [])
    .map((room, index) => ({
      ...room,
      display_index: index + 1,
      items: (Array.isArray(room?.items) ? room.items : []).filter(isVisibleBookingExtra),
    }));
  const reservationItems = (
    Array.isArray(catalog?.reservation?.items) ? catalog.reservation.items : []
  ).filter(isVisibleBookingExtra);
  const hasScopedContract =
    Number(catalog?.contract_version || 0) >= 2 ||
    roomSections.length > 0 ||
    reservationItems.length > 0;
  const legacyItems = !hasScopedContract
    ? (Array.isArray(catalog?.items) ? catalog.items : []).filter(isVisibleBookingExtra)
    : [];
  const selectedKeys = new Set(
    (
      (Array.isArray(catalog?.selection?.entries) && catalog.selection.entries) ||
      (Array.isArray(catalog?.selections) && catalog.selections) ||
      []
    ).map((entry) => bookingExtraSelectionKey(entry?.reservation_draft_id, entry?.extra_id))
      .filter(Boolean)
  );
  const isMutating = mutatingExtraKey != null;
  const hasItems =
    reservationItems.length > 0 ||
    legacyItems.length > 0 ||
    roomSections.some((room) => room.items.length > 0);

  const replaceCopyTokens = (template, replacements) => Object.entries(replacements)
    .reduce(
      (copy, [key, value]) => copy.replaceAll(`{{${key}}}`, String(value)),
      String(template || "")
    );

  const nightsBetween = (checkIn, checkOut) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(checkIn) || !/^\d{4}-\d{2}-\d{2}$/.test(checkOut)) return 0;
    const [startYear, startMonth, startDay] = String(checkIn).split("-").map(Number);
    const [endYear, endMonth, endDay] = String(checkOut).split("-").map(Number);
    return Math.max(0, Math.round(
      (Date.UTC(endYear, endMonth - 1, endDay) - Date.UTC(startYear, startMonth - 1, startDay)) /
      (24 * 60 * 60 * 1000)
    ));
  };

  const formatStayDate = (value) => {
    const raw = String(value || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || "—";
    const [year, month, day] = raw.split("-").map(Number);
    try {
      return new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(year, month - 1, day));
    } catch {
      return raw;
    }
  };

  const countCopy = (type, count) => {
    const safeCount = Math.max(0, Math.trunc(Number(count) || 0));
    const plural = safeCount === 1
      ? type
      : type === "child"
        ? "children"
        : type === "baby"
          ? "babies"
          : `${type}s`;
    return replaceCopyTokens(t(`extras_count_${plural}`), { count: safeCount });
  };

  const pricingCopy = (item, room = null) => {
    const scope = String(item?.scope || "per_room").toLowerCase();
    const calculation = item?.calculation && typeof item.calculation === "object"
      ? item.calculation
      : {};
    const chargePeriod = String(
      calculation.period || item?.charge_period || "per_stay"
    ).replace(/^per_/, "").toLowerCase();
    const chargeUnit = scope === "per_reservation"
      ? "booking"
      : String(calculation.unit || item?.charge_unit || "per_room")
          .replace(/^per_/, "")
          .toLowerCase();
    const normalizedUnit = ["pax", "person", "people"].includes(chargeUnit)
      ? "guest"
      : chargeUnit;
    const unitLabel = t(`extras_unit_${normalizedUnit}`);
    const amountType = String(item?.amount_type || "absolute").toLowerCase();
    const itemCurrency = item?.currency || currency;
    const criteria = room?.criteria && typeof room.criteria === "object" ? room.criteria : {};
    const fallbackUnitCount = normalizedUnit === "adult"
      ? criteria.adults
      : normalizedUnit === "child"
        ? criteria.children
        : normalizedUnit === "guest"
          ? Number(criteria.adults || 0) + Number(criteria.children || 0)
          : 1;
    const unitCount = Number.isFinite(Number(calculation.unit_count))
      ? Math.max(0, Number(calculation.unit_count))
      : Math.max(0, Number(fallbackUnitCount || 0));
    const fallbackNights = room
      ? nightsBetween(criteria.check_in_date, criteria.check_out_date)
      : 1;
    const nightCount = Number.isFinite(Number(calculation.night_count))
      ? Math.max(0, Number(calculation.night_count))
      : Math.max(0, fallbackNights);

    let price;
    if (amountType === "relative") {
      const percentage = new Intl.NumberFormat(lang === "es" ? "es-ES" : "en-GB", {
        maximumFractionDigits: 2,
      }).format(Number(item?.configured_amount ?? item?.amount ?? 0));
      price = replaceCopyTokens(t("extras_percentage_price"), {
        amount: percentage,
        base: t(scope === "per_reservation" ? "extras_base_booking" : "extras_base_room"),
      });
      if (scope !== "per_reservation" && normalizedUnit !== "room") {
        price += replaceCopyTokens(t("extras_percentage_unit"), { unit: unitLabel });
      }
    } else {
      const itemCurrencyCatalog = [item, ...currencyCatalog];
      const unitAmount = formatBookingMoney(
        item?.unit_amount,
        itemCurrency,
        lang,
        itemCurrencyCatalog
      );
      price = replaceCopyTokens(t("extras_unit_price"), {
        amount: unitAmount || formatBookingMoney(0, itemCurrency, lang, itemCurrencyCatalog),
        unit: unitLabel,
        period: t(chargePeriod === "night" ? "extras_period_night" : "extras_period_stay"),
      });
    }

    const units = countCopy(normalizedUnit, unitCount);
    const application = chargePeriod === "night" && nightCount > 0
      ? replaceCopyTokens(t("extras_application_nights"), {
          units,
          nights: countCopy("night", nightCount),
        })
      : scope === "per_reservation" && unitCount === 1
        ? t("extras_application_once")
        : replaceCopyTokens(t("extras_application_stay"), { units });
    // `basis_amount` follows the selected display currency; the base amount is
    // only a fallback for legacy/non-converted responses.
    const rawBasisAmount = calculation.basis_amount ?? calculation.base_basis_amount;
    const basisAmount = Number(rawBasisAmount);
    const basis = amountType === "relative" && Number.isFinite(basisAmount)
      ? replaceCopyTokens(t("extras_calculation_basis"), {
          amount: formatBookingMoney(
            basisAmount,
            itemCurrency,
            lang,
            [item, ...currencyCatalog]
          ),
        })
      : "";

    return { price, application, basis };
  };

  const renderExtraCard = (item, reservationDraftId, room = null) => {
    const extraId = Number(item.extra_id);
    const selectionKey = bookingExtraSelectionKey(reservationDraftId, extraId);
    const selected =
      item.is_selected === true ||
      item.is_required === true ||
      item.is_automatic === true ||
      selectedKeys.has(selectionKey);
    const updating = mutatingExtraKey === selectionKey;
    const required = item.is_required === true;
    const automatic = item.is_automatic === true;
    const selectable =
      item.is_selectable !== false &&
      !required &&
      !automatic &&
      (!selected || item.can_remove !== false);
    const formattedTotal = formatBookingMoney(
      item.total,
      item.currency || currency,
      lang,
      [item, ...currencyCatalog]
    );
    const pricing = pricingCopy(item, room);
    const statusLabels = [];
    if (required) statusLabels.push({ key: "required", copy: t("extras_required") });
    if (automatic) statusLabels.push({ key: "automatic", copy: t("extras_automatic") });
    if (selected && !required && !automatic) {
      statusLabels.push({ key: "selected", copy: t("extras_selected") });
    }
    const fallbackName = replaceCopyTokens(t("extras_fallback_name"), { id: extraId });

    return (
      <article
        key={selectionKey}
        className={`bookingExtraCard ${selected ? "bookingExtraCard--selected" : ""}`.trim()}
        data-extra-id={extraId}
        data-reservation-draft-id={reservationDraftId == null ? "reservation" : reservationDraftId}
      >
        <div className="bookingExtraCard__ornament" aria-hidden="true">✦</div>
        <div className="bookingExtraCard__content">
          <div className="bookingExtraCard__heading">
            <h3>{item.name || fallbackName}</h3>
            {statusLabels.length > 0 ? (
              <div className="bookingExtraCard__badges">
                {statusLabels.map((status) => (
                  <span
                    key={status.key}
                    className={`bookingExtraCard__status bookingExtraCard__status--${status.key}`}
                  >
                    {status.copy}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          {item.description ? <p className="bookingExtraCard__description">{item.description}</p> : null}
          <dl className="bookingExtraCard__pricingBreakdown">
            <div>
              <dt>{t("extras_price_label")}</dt>
              <dd>{pricing.price}</dd>
            </div>
            <div>
              <dt>{t("extras_applied_label")}</dt>
              <dd>
                {pricing.application}
                {pricing.basis ? <small>{pricing.basis}</small> : null}
              </dd>
            </div>
          </dl>
        </div>

        <div className="bookingExtraCard__purchase">
          <div className="bookingExtraCard__price">
            <small>{t("extras_total_label")}</small>
            <strong>{formattedTotal}</strong>
          </div>
          <button
            type="button"
            className="bookingExtraCard__action"
            aria-pressed={selected}
            aria-busy={updating || undefined}
            disabled={!selectable || isMutating}
            onClick={() => onToggle?.(item, !selected, reservationDraftId)}
          >
            {updating
              ? t("extras_updating")
              : required
                ? t("extras_required")
                : automatic
                  ? t("extras_automatic")
                  : selected
                    ? t("extras_remove")
                    : t("extras_add")}
          </button>
        </div>
      </article>
    );
  };

  return (
    <section className="bookingExtrasStep" aria-labelledby="booking-extras-title">
      <header className="bookingExtrasStep__hero">
        <span className="bookingExtrasStep__eyebrow" aria-hidden="true">✦</span>
        <h1 id="booking-extras-title">{t("extras_title")}</h1>
        <p>{t("extras_subtitle")}</p>
      </header>

      {hasItems && loading ? (
        <div className="bookingExtrasStep__refreshStatus" role="status" aria-live="polite">
          <span className="bookingExtrasStep__spinner" aria-hidden="true" />
          {t("extras_loading")}
        </div>
      ) : null}

      {hasItems && error ? (
        <div className="bookingExtrasStep__refreshError" role="alert">
          <span>{t(error)}</span>
          <button type="button" onClick={onRetry}>{t("extras_retry")}</button>
        </div>
      ) : null}

      {loading && !hasItems ? (
        <div className="bookingExtrasStep__status" role="status" aria-live="polite">
          <span className="bookingExtrasStep__spinner" aria-hidden="true" />
          {t("extras_loading")}
        </div>
      ) : error && !hasItems ? (
        <div className="bookingExtrasStep__error" role="alert">
          <p>{t(error)}</p>
          <button type="button" onClick={onRetry}>{t("extras_retry")}</button>
        </div>
      ) : !hasItems ? (
        <div className="bookingExtrasStep__empty">
          <span aria-hidden="true">✓</span>
          <h2>{t("extras_empty_title")}</h2>
          <p>{t("extras_empty_body")}</p>
        </div>
      ) : (
        <div className="bookingExtrasStep__sections">
          {reservationItems.length > 0 ? (
            <section className="bookingExtrasSection bookingExtrasSection--reservation">
              <header className="bookingExtrasSection__header">
                <div>
                  <span className="bookingExtrasSection__kicker">✦</span>
                  <h2>{t("extras_reservation_section_title")}</h2>
                  <p>{t("extras_reservation_section_subtitle")}</p>
                </div>
              </header>
              <div className="bookingExtrasStep__grid">
                {reservationItems.map((item) => renderExtraCard(item, null, null))}
              </div>
            </section>
          ) : null}

          {roomSections.map((room) => {
            const criteria = room?.criteria || {};
            const categoryName = getRoomCategoryDisplayName(room?.room_category) || "—";
            const rateName = String(room?.room_rate?.name || "").trim() || "—";
            const roomTitle = replaceCopyTokens(t("extras_room_section_title"), {
              n: room.display_index,
            });
            const occupancyParts = [
              countCopy("adult", criteria.adults),
              countCopy("child", criteria.children),
            ];
            if (Number(criteria.babies || 0) > 0) {
              occupancyParts.push(countCopy("baby", criteria.babies));
            }
            const occupancy = occupancyParts.join(" · ");
            return (
              <section
                key={room.reservation_draft_id}
                className="bookingExtrasSection bookingExtrasSection--room"
                data-room-extra-section={room.reservation_draft_id}
              >
                <header className="bookingExtrasSection__header bookingExtrasSection__header--room">
                  <div>
                    <span className="bookingExtrasSection__kicker">{roomTitle}</span>
                    <h2>{categoryName}</h2>
                    <p>{rateName}</p>
                  </div>
                  <dl className="bookingExtrasSection__metadata">
                    <div>
                      <dt>{t("extras_stay_dates")}</dt>
                      <dd>
                        {formatStayDate(criteria.check_in_date)} → {formatStayDate(criteria.check_out_date)}
                      </dd>
                    </div>
                    <div>
                      <dt>{t("extras_occupancy")}</dt>
                      <dd>{occupancy}</dd>
                    </div>
                  </dl>
                </header>
                {room.items.length > 0 ? (
                  <div className="bookingExtrasStep__grid">
                    {room.items.map((item) => (
                      renderExtraCard(item, room.reservation_draft_id, room)
                    ))}
                  </div>
                ) : (
                  <p className="bookingExtrasSection__empty">{t("extras_room_empty")}</p>
                )}
              </section>
            );
          })}

          {legacyItems.length > 0 ? (
            <section className="bookingExtrasSection bookingExtrasSection--legacy">
              <div className="bookingExtrasStep__grid">
                {legacyItems.map((item) => renderExtraCard(item, null, null))}
              </div>
            </section>
          ) : null}
        </div>
      )}

      <div className="bookingExtrasStep__actions">
        <button
          className="bookingSecondaryAction"
          type="button"
          onClick={onBack}
          disabled={isMutating}
        >
          {t("back")}
        </button>
        <button
          className="bookingPrimaryAction"
          type="button"
          onClick={onContinue}
          disabled={loading || !!error || isMutating}
        >
          {t("extras_continue")}
        </button>
      </div>

      <span className="bookingSrOnly" aria-live="polite" aria-atomic="true">
        {isMutating ? t("extras_updating") : ""}
      </span>
    </section>
  );
}

export function BookingTopBar({
  hotelName,
  hotelContext,
  hasDraft,
  step,
  lang,
  setLang,
  bookingCurrencies,
  bookingCurrency,
  currencyCatalog = [],
  onCurrencyChange,
  getRoomCategoryDisplayName,
  roomDraftIds,
  roomStateByDraftId,
  activeDraftId,
  shoppingCriteria = null,
  t,
  onChangeDates,
  onChangeAdults,
  onChangeChildren,
  onChangeGuests,
  onRemoveRoom,
  deletingDraftId,
  termsOpen,
  setTermsOpen,
  isMobile,
  interactionDisabled = false,
  canContinue = false,
  onContinueToExtras,
  onContinueToGuestDetails,
  bookingCart = null,
}) {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const summaryTitleRef = useRef(null);
  const pendingDrawerActionRef = useRef(null);
  const wideBookingLayout = useBookingMediaQuery("(min-width: 1201px)");
  const persistentSummaryVisible = (step === "extras" || step === "guest_details") && wideBookingLayout;
  const hotelProfile = hotelContext?.booking_engine_profile || {};
  const primaryPhone = normalizeBookingContactValues(
    hotelProfile?.phones,
    ["value", "phone", "number"]
  )[0] || "";
  const primaryPhoneHref = primaryPhone.replace(/[^\d+]/g, "");
  const showCompactContact = hasDraft && isMobile && !!primaryPhoneHref;

  const ids = Array.isArray(roomDraftIds)
    ? roomDraftIds.map(Number).filter((id) => Number.isFinite(id) && id > 0)
    : [];
  const rooms = ids.map((id) => ({
    id,
    ...(roomStateByDraftId?.[id] || {}),
  }));
  const selectedRoomCount = rooms.filter((room) => !!room?.selectedRate?.id).length;
  const selectedRoomsSubtotal = rooms.reduce((sum, room) => {
    const amount = Number(room?.selectedRate?.min_total);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const canonicalGrandTotal = Number(bookingCart?.grand_total ?? bookingCart?.total);
  const displayGrandTotal = Number.isFinite(canonicalGrandTotal)
    ? canonicalGrandTotal
    : selectedRoomsSubtotal;
  const summaryCurrency = bookingCart?.currency || bookingCurrency;
  const formattedGrandTotal = selectedRoomCount > 0
    ? formatBookingMoney(displayGrandTotal, summaryCurrency, lang, currencyCatalog)
    : null;

  const firstRoom = rooms[0] || null;
  const canEditActiveStay =
    step === "categories" &&
    !interactionDisabled;
  const roomLabel = `${rooms.length} ${t(rooms.length === 1 ? "room_singular" : "room_plural")}`;
  const hasShoppingCriteria = !!shoppingCriteria;
  const firstRoomHasStay = !!firstRoom?.checkIn && !!firstRoom?.checkOut;
  const roomsShareStay = firstRoomHasStay && rooms.every(
    (room) => room.checkIn === firstRoom.checkIn && room.checkOut === firstRoom.checkOut
  );
  const roomsShareGuests = !!firstRoom && rooms.every(
    (room) => Number(room.adults || 0) === Number(firstRoom.adults || 0) &&
      Number(room.children || 0) === Number(firstRoom.children || 0)
  );
  const criteriaCheckIn = hasShoppingCriteria ? shoppingCriteria?.checkIn : firstRoom?.checkIn;
  const criteriaCheckOut = hasShoppingCriteria ? shoppingCriteria?.checkOut : firstRoom?.checkOut;
  const guestTotal = hasShoppingCriteria
    ? Number(shoppingCriteria?.adults || 0) + Number(shoppingCriteria?.children || 0)
    : roomsShareGuests
      ? Number(firstRoom?.adults || 0) + Number(firstRoom?.children || 0)
      : null;
  const guestLabel = guestTotal != null && guestTotal > 0
    ? `${guestTotal} ${t(guestTotal === 1 ? "guest_singular" : "guest_plural")}`
    : t("view_details");
  const stayLabel = hasShoppingCriteria
    ? (
      criteriaCheckIn && criteriaCheckOut
        ? `${formatBookingTopBarDate(criteriaCheckIn, lang)} — ${formatBookingTopBarDate(criteriaCheckOut, lang)}`
        : t("view_details")
    )
    : roomsShareStay
      ? `${formatBookingTopBarDate(criteriaCheckIn, lang)} — ${formatBookingTopBarDate(criteriaCheckOut, lang)}`
      : rooms.length > 1
        ? t("different_stays")
        : t("view_details");

  useEffect(() => {
    pendingDrawerActionRef.current = null;
    setSummaryOpen(false);
  }, [activeDraftId, persistentSummaryVisible, step]);

  function openBookingSummary() {
    if (persistentSummaryVisible) {
      setSummaryOpen(false);
      const focusPersistentSummary = () => {
        document.getElementById("booking-summary-panel")?.focus({ preventScroll: true });
      };
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(focusPersistentSummary);
      } else {
        focusPersistentSummary();
      }
      return;
    }
    setSummaryOpen(true);
  }

  function runAfterDrawerClose(action) {
    if (typeof action !== "function") return;
    if (!summaryOpen) {
      action();
      return;
    }
    pendingDrawerActionRef.current = action;
    setSummaryOpen(false);
  }

  function handleDrawerExited() {
    const action = pendingDrawerActionRef.current;
    pendingDrawerActionRef.current = null;
    action?.();
  }

  function handleDrawerHide() {
    pendingDrawerActionRef.current = null;
    setSummaryOpen(false);
  }

  const selectedRoomsCopy = t("selected_rooms_count")
    .replace("{{selected}}", String(selectedRoomCount))
    .replace("{{total}}", String(rooms.length));

  return (
    <>
      <header className={`bookingTopBar ${hasDraft ? "bookingTopBar--withStay" : "bookingTopBar--search"}`}>
        <div className="bookingTopBar__brand" title={hotelName || ""}>
          {hotelName || "—"}
        </div>

        {hasDraft ? (
          <nav className="bookingTopBar__stay" aria-label={t("stay_details")}>
            {showCompactContact ? (
              <a
                className="bookingTopBar__stayControl bookingTopBar__contact"
                href={`tel:${primaryPhoneHref}`}
                aria-label={`${t("need_help")} ${primaryPhone}`}
              >
                <span className="bookingTopBar__controlLabel">{t("need_help")}</span>
                <span className="bookingTopBar__controlValue bookingTopBar__contactPhone">
                  {t("call_hotel")}
                </span>
              </a>
            ) : null}
            <button
              type="button"
              className="bookingTopBar__stayControl"
              onClick={() => canEditActiveStay ? onChangeDates?.() : openBookingSummary()}
              aria-label={canEditActiveStay ? t("edit_dates") : t("open_booking_summary")}
            >
              <span className="bookingTopBar__controlLabel">{t("label_dates")}</span>
              <span className="bookingTopBar__controlValue">{stayLabel}</span>
            </button>

            <button
              type="button"
              className="bookingTopBar__stayControl"
              onClick={() => canEditActiveStay ? onChangeGuests?.() : openBookingSummary()}
              aria-label={canEditActiveStay ? t("edit_guests") : t("open_booking_summary")}
            >
              <span className="bookingTopBar__controlLabel">{t("label_guests")}</span>
              <span className="bookingTopBar__controlValue">{guestLabel}</span>
            </button>

            <button
              type="button"
              className="bookingTopBar__stayControl bookingTopBar__roomsControl"
              onClick={openBookingSummary}
              aria-haspopup={persistentSummaryVisible ? undefined : "dialog"}
              aria-controls={persistentSummaryVisible ? "booking-summary-panel" : "booking-summary-drawer"}
              aria-expanded={persistentSummaryVisible ? true : summaryOpen}
              aria-label={`${t("open_booking_summary")}: ${roomLabel}${formattedGrandTotal ? `, ${formattedGrandTotal}` : ""}`}
            >
              <span className="bookingTopBar__controlLabel">{t("booking_summary")}</span>
              <span className="bookingTopBar__controlValue bookingTopBar__roomsValue">
                <span className="bookingTopBar__roomsCount">{roomLabel}</span>
                {formattedGrandTotal ? (
                  <strong className="bookingTopBar__roomsTotal">{formattedGrandTotal}</strong>
                ) : null}
              </span>
            </button>
          </nav>
        ) : <div className="bookingTopBar__stay" />}

        <div className="bookingTopBar__actions">
          <label className="bookingSrOnly" htmlFor="booking-topbar-language">
            {t("language")}
          </label>
          <select
            id="booking-topbar-language"
            className="bookingTopBar__select"
            value={lang}
            onChange={(event) => setLang(event.target.value)}
          >
            <option value="en">EN</option>
            <option value="es">ES</option>
          </select>

          {bookingCurrencies.length > 1 ? (
            <>
              <label className="bookingSrOnly" htmlFor="booking-topbar-currency">
                {t("currency_label")}
              </label>
              <select
                id="booking-topbar-currency"
                className="bookingTopBar__select bookingTopBar__currency"
                value={bookingCurrency}
                disabled={interactionDisabled}
                onChange={(event) => onCurrencyChange(event.target.value)}
              >
                {bookingCurrencies.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </>
          ) : null}

          {hasDraft ? (
            <button
              type="button"
              className="bookingTopBar__summaryButton"
              onClick={openBookingSummary}
              aria-haspopup={persistentSummaryVisible ? undefined : "dialog"}
              aria-controls={persistentSummaryVisible ? "booking-summary-panel" : "booking-summary-drawer"}
              aria-expanded={persistentSummaryVisible ? true : summaryOpen}
              aria-label={`${t("open_booking_summary")}: ${roomLabel}${formattedGrandTotal ? `, ${formattedGrandTotal}` : ""}`}
            >
              <span className="bookingTopBar__summaryText">
                <span>{t("booking_summary")}</span>
                <small>{roomLabel}</small>
              </span>
              {formattedGrandTotal ? (
                <strong>{formattedGrandTotal}</strong>
              ) : (
                <span className="bookingTopBar__summaryArrow" aria-hidden="true">›</span>
              )}
            </button>
          ) : null}

          {hasDraft && step === "categories" ? (
            <button
              type="button"
              className="bookingTopBar__continueButton"
              disabled={!canContinue || interactionDisabled}
              onClick={onContinueToExtras || onContinueToGuestDetails}
            >
              {t("continue")}
            </button>
          ) : null}
        </div>
      </header>

      <p className="bookingSrOnly" aria-live="polite" aria-atomic="true">
        {selectedRoomCount > 0 ? `${selectedRoomsCopy}. ${formattedGrandTotal}` : ""}
      </p>

      <Offcanvas
        id="booking-summary-drawer"
        show={summaryOpen && !persistentSummaryVisible}
        onHide={handleDrawerHide}
        onExited={handleDrawerExited}
        onEntered={() => summaryTitleRef.current?.focus()}
        placement={isMobile ? "bottom" : "end"}
        backdropClassName="bookingSummaryBackdrop"
        className={`bookingSummaryOffcanvas ${isMobile ? "bookingSummaryOffcanvas--mobile" : ""}`}
        scroll={false}
        backdrop
        restoreFocus
      >
        <Offcanvas.Header
          className="bookingSummaryPanel__header"
          closeButton
          closeVariant="white"
          closeLabel={t("close_booking_summary")}
        >
          <Offcanvas.Title
            id="booking-summary-title"
            className="bookingSummaryPanel__title"
            ref={summaryTitleRef}
            tabIndex={-1}
          >
            {t("booking_summary")}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="bookingSummaryPanel__body bookingSummaryPanel__body--drawer">
          <BookingSummaryPanel
            variant="drawer"
            canEdit={step === "categories" && !interactionDisabled}
            hotelName={hotelName}
            hotelContext={hotelContext}
            lang={lang}
            getRoomCategoryDisplayName={getRoomCategoryDisplayName}
            roomDraftIds={roomDraftIds}
            roomStateByDraftId={roomStateByDraftId}
            activeDraftId={activeDraftId}
            currency={bookingCurrency}
            currencyCatalog={currencyCatalog}
            t={t}
            onChangeDates={() => runAfterDrawerClose(onChangeDates)}
            onChangeAdults={() => runAfterDrawerClose(onChangeAdults)}
            onChangeChildren={() => runAfterDrawerClose(onChangeChildren)}
            onRemoveRoom={onRemoveRoom}
            deletingDraftId={deletingDraftId}
            setTermsOpen={(nextOpen) => {
              if (!nextOpen) {
                setTermsOpen(false);
                return;
              }
              runAfterDrawerClose(() => setTermsOpen(true));
            }}
            bookingCart={bookingCart}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <BookingTermsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        hotelContext={hotelContext}
        lang={lang}
        t={t}
      />
    </>
  );
}

function BookingSummaryPanel({
  variant = "drawer",
  id,
  className = "",
  t = (key, fallback) => (fallback ?? key),
  ...summaryProps
}) {
  const isEmbedded = variant === "embedded";
  const variantClass = isEmbedded
    ? "bookingSummaryPanel--embedded"
    : "bookingSummaryPanel--drawer";
  const titleId = id ? `${id}-title` : undefined;
  const content = <BookingSummaryContent {...summaryProps} t={t} />;

  if (!isEmbedded) {
    return (
      <div className={`bookingSummaryPanel ${variantClass} ${className}`.trim()}>
        {content}
      </div>
    );
  }

  return (
    <section
      id={id}
      className={`bookingSummaryPanel ${variantClass} ${className}`.trim()}
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <header className="bookingSummaryPanel__header">
        <h2 id={titleId} className="bookingSummaryPanel__title">
          {t("booking_summary")}
        </h2>
      </header>
      <div className="bookingSummaryPanel__body">
        {content}
      </div>
    </section>
  );
}

export function BookingSummaryContent({
  hotelName,
  hotelContext, // ✅
  lang = "en",
  getRoomCategoryDisplayName = (c) => (String(c?.name || "").trim() || null),
  roomDraftIds,
  roomStateByDraftId,
  activeDraftId,
  currency = "€",
  currencyCatalog = [],
  t = (key, fallback) => (fallback ?? key),
  onChangeDates,
  onChangeAdults,
  onChangeChildren,
  onRemoveRoom,
  deletingDraftId,
  setTermsOpen,
  canEdit = true,
  bookingCart = null,
}) {

  const ids = Array.isArray(roomDraftIds) ? roomDraftIds : [];
  const summaryCurrency = bookingCart?.currency || currency;

  const selectedRoomCount = ids.filter((draftId) => (
    !!roomStateByDraftId?.[Number(draftId)]?.selectedRate?.id
  )).length;
  const allRoomsSelected = ids.length > 0 && selectedRoomCount === ids.length;

  const roomsFallbackTotal = ids.reduce((sum, draftId) => {
    const st = roomStateByDraftId?.[Number(draftId)];
    const minTotal = st?.selectedRate?.min_total;
    const n = minTotal != null ? Number(minTotal) : 0;
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const cartItems = Array.isArray(bookingCart?.items) ? bookingCart.items : [];
  const cartItemByDraftId = new Map(
    cartItems.map((item) => [Number(item?.reservation_draft_id), item])
  );
  const selectedExtras = Array.isArray(bookingCart?.extras) ? bookingCart.extras : [];
  const reservationExtras = selectedExtras.filter((extra) => (
    String(extra?.scope || "").toLowerCase() === "per_reservation"
  ));
  const reservationExtrasFallbackTotal = reservationExtras.reduce((sum, extra) => {
    const amount = Number(extra?.total ?? extra?.calculated_amount ?? 0);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const canonicalReservationExtrasTotal = Number(bookingCart?.reservation_extras_total);
  const reservationExtrasTotal = Number.isFinite(canonicalReservationExtrasTotal)
    ? canonicalReservationExtrasTotal
    : reservationExtrasFallbackTotal;
  const roomExtras = cartItems.flatMap((item) => (
    (Array.isArray(item?.extras) ? item.extras : []).filter((extra) => (
      String(extra?.scope || "per_room").toLowerCase() !== "per_reservation"
    ))
  ));
  const hasSelectedExtras = reservationExtras.length > 0 || roomExtras.length > 0;
  const aggregateExtrasFallbackTotal = selectedExtras.reduce((sum, extra) => {
    const value = Number(extra?.total);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const scopedExtrasFallbackTotal = reservationExtrasTotal + roomExtras.reduce((sum, extra) => {
    const value = Number(extra?.total);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const extrasFallbackTotal = hasSelectedExtras
    ? scopedExtrasFallbackTotal
    : aggregateExtrasFallbackTotal;
  const canonicalRoomsTotal = Number(bookingCart?.rooms_total);
  const canonicalExtrasTotal = Number(bookingCart?.extras_total);
  const canonicalGrandTotal = Number(bookingCart?.grand_total ?? bookingCart?.total);
  const roomsTotal = Number.isFinite(canonicalRoomsTotal) ? canonicalRoomsTotal : roomsFallbackTotal;
  const extrasTotal = Number.isFinite(canonicalExtrasTotal) ? canonicalExtrasTotal : extrasFallbackTotal;
  const grandTotal = Number.isFinite(canonicalGrandTotal)
    ? canonicalGrandTotal
    : roomsTotal + extrasTotal;
  const formattedGrandTotal = selectedRoomCount > 0
    ? formatBookingMoney(grandTotal, summaryCurrency, lang, currencyCatalog)
    : null;
  const selectedRoomsCopy = t("selected_rooms_count")
    .replace("{{selected}}", String(selectedRoomCount))
    .replace("{{total}}", String(ids.length));
  const hotelProfile = hotelContext?.booking_engine_profile || {};
  const addressLines = [
    String(hotelProfile?.address_line || "").trim(),
    [hotelProfile?.postal_code, hotelProfile?.city]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" "),
    String(hotelProfile?.country || "").trim(),
  ].filter(Boolean);
  const hotelPhones = normalizeBookingContactValues(
    hotelProfile?.phones,
    ["value", "phone", "number"]
  );
  const hotelEmails = normalizeBookingContactValues(
    hotelProfile?.emails,
    ["value", "email"]
  );
  const hasHotelContact = addressLines.length > 0 || hotelPhones.length > 0 || hotelEmails.length > 0;


  const miniBtnStyle = {
    padding: "6px 8px",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.14)",
    color: "rgba(255,255,255,0.85)",
    cursor: "pointer",
    letterSpacing: "0.08em",
    fontWeight: 700,
    fontSize: "0.78rem",
    borderRadius: "6px",
  };

  const removeBtnStyle = {
    padding: "6px 8px",
    cursor: "pointer",
    letterSpacing: "0.10em",
    fontWeight: 900,
    fontSize: "0.78rem",
    borderRadius: "6px",
  };


  return (
    <div
      className="bookingSummaryContent"
      style={{
        boxSizing: "border-box",
        padding: "18px 16px 16px",
        borderRight: "1px solid rgba(255,255,255,0.10)",
        background: "rgba(0,0,0,0.60)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        zIndex: 1200,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="bookingSummaryContent__inner">
        {/* Hotel name */}
        <div
          className="bookingSummaryContent__hotelName"
          style={{
            fontWeight: 900,
            letterSpacing: "0.10em",
            color: "#e6c36a",
            textTransform: "uppercase",
            lineHeight: 1.15,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {hotelName || "—"}
        </div>

        {/* ✅ Grand total under hotel name */}
        <div className="bookingSummaryContent__grandTotal" style={{ marginTop: "10px", border: "1px solid rgba(230,195,106,0.22)", background: "rgba(230,195,106,0.06)", padding: "12px" }}>
          <div className="bookingSummaryContent__grandTotalLabel" style={{ color: "#e6c36a", letterSpacing: "0.16em", fontSize: "0.78rem", fontWeight: 900 }}>
            {hasSelectedExtras
              ? t("total_booking")
              : allRoomsSelected
                ? t("total_all_rooms")
                : t("selected_subtotal")}
          </div>
          {formattedGrandTotal ? (
            <>
              <div className="bookingSummaryContent__grandTotalValue" style={{ marginTop: "6px", fontSize: "2.0rem", fontWeight: 900 }}>
                {formattedGrandTotal}
              </div>
              {!allRoomsSelected ? (
                <div style={{ marginTop: "4px", color: "rgba(255,255,255,0.62)", fontSize: "0.8rem" }}>
                  {selectedRoomsCopy}
                </div>
              ) : null}
              {hasSelectedExtras ? (
                <div className="bookingSummaryContent__breakdown">
                  <span>{t("rooms_subtotal")}</span>
                  <strong>{formatBookingMoney(roomsTotal, summaryCurrency, lang, currencyCatalog)}</strong>
                  <span>{t("extras_subtotal")}</span>
                  <strong>{formatBookingMoney(extrasTotal, summaryCurrency, lang, currencyCatalog)}</strong>
                </div>
              ) : null}
            </>
          ) : (
            <div style={{ marginTop: "7px", color: "rgba(255,255,255,0.72)", fontSize: "0.9rem", lineHeight: 1.4 }}>
              {t("select_rate_to_see_total")}
            </div>
          )}

          {(() => {
            const flags = {
              vat: !!hotelContext?.booking_engine_price_includes_vat,
              other: !!hotelContext?.booking_engine_price_includes_other_taxes,
              fees: !!hotelContext?.booking_engine_price_includes_fees,
              city: !!hotelContext?.booking_engine_price_includes_city_tax,
            };

            const allIncluded = flags.vat && flags.other && flags.fees && flags.city;

            const items = [];
            if (flags.vat) items.push(t("tax_label_vat", "VAT"));
            if (flags.other) items.push(t("tax_label_other_taxes", "other taxes"));
            if (flags.fees) items.push(t("tax_label_fees", "fees"));
            if (flags.city) items.push(t("tax_label_city_tax", "city tax"));

            const joinNatural = (arr) => {
              const safe = Array.isArray(arr) ? arr.filter(Boolean) : [];
              if (safe.length <= 1) return safe[0] || "";
              const conj = t("list_conjunction_and", "and");
              if (safe.length === 2) return `${safe[0]} ${conj} ${safe[1]}`;
              const head = safe.slice(0, -1).join(", ");
              const last = safe[safe.length - 1];
              return `${head} ${conj} ${last}`;
            };

            if (items.length === 0) {
              return (
                <div style={{ marginTop: "6px", color: "rgba(255,255,255,0.70)", fontSize: "0.86rem", lineHeight: 1.35 }}>
                  {t("taxes_not_including_any", "Not including any taxes or fees")}
                </div>
              );
            }

            if (allIncluded) {
              return (
                <div style={{ marginTop: "6px", color: "rgba(255,255,255,0.70)", fontSize: "0.86rem", lineHeight: 1.35 }}>
                  {t("taxes_all_included", "All taxes and fees included")}
                </div>
              );
            }

            const joined = joinNatural(items);

            return (
              <div style={{ marginTop: "6px", color: "rgba(255,255,255,0.70)", fontSize: "0.86rem", lineHeight: 1.35 }}>
                {t("taxes_including_prefix", "Including")} {joined}
              </div>
            );

          })()}

          <div style={{ marginTop: "10px" }}>
            <button
              className="bookingSummaryContent__termsButton"
              type="button"
              onClick={() => setTermsOpen(true)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: "rgba(255,255,255,0.85)",
                textDecoration: "underline",
                cursor: "pointer",
                letterSpacing: "0.08em",
                fontWeight: 800,
                fontSize: "0.85rem",
              }}
            >
              {t("booking_terms_link", "Booking terms & conditions")}
            </button>
          </div>

        </div>

        

        {/* Scrollable content */}
        <div
          className="bookingSummaryContent__scroll"
          style={{ marginTop: "14px", overflowY: "auto", minHeight: 0, flex: 1, paddingRight: "4px" }}
        >
          {ids.map((draftId, idx) => {
            const st = roomStateByDraftId?.[Number(draftId)] || {};
            const cartItem = cartItemByDraftId.get(Number(draftId)) || null;
            const isActive = Number(draftId) === Number(activeDraftId);

            // ✅ Lock editing when rate is selected
            const hasRateSelected = !!st?.selectedRate?.id;

            const isDeletingThis = Number(deletingDraftId) === Number(draftId);
            const cannotRemove = false;
            const disableRemove = isDeletingThis;

            const selectedRateTotal = st?.selectedRate?.min_total != null
              ? Number(st.selectedRate.min_total)
              : null;
            const avg = st?.selectedRate?.min_avg_night != null ? Number(st.selectedRate.min_avg_night) : null;
            const itemRoomExtras = (Array.isArray(cartItem?.extras) ? cartItem.extras : [])
              .filter((extra) => (
                String(extra?.scope || "per_room").toLowerCase() !== "per_reservation"
              ));
            const itemHasReservationExtras = (Array.isArray(cartItem?.extras) ? cartItem.extras : [])
              .some((extra) => (
                String(extra?.scope || "").toLowerCase() === "per_reservation"
              ));
            const roomExtrasFallbackTotal = itemRoomExtras.reduce((sum, extra) => {
              const amount = Number(extra?.total ?? extra?.calculated_amount ?? 0);
              return sum + (Number.isFinite(amount) ? amount : 0);
            }, 0);
            const cartRoomTotal = Number(
              cartItem?.room_total ?? cartItem?.rooms_total ?? cartItem?.accommodation_total
            );
            // A per-reservation extra is materialized on one anchor draft, but
            // belongs to the booking section below—not that room's subtotal.
            // Prefer the scoped v2 fields so the anchor room never absorbs it.
            const cartRoomExtrasTotal = Number(
              cartItem?.room_extras_total ?? (
                itemHasReservationExtras
                  ? undefined
                  : cartItem?.extras_total ?? cartItem?.extra_total
              )
            );
            const cartRoomGrandTotal = Number(
              cartItem?.room_subtotal ?? cartItem?.room_grand_total ??
              (itemHasReservationExtras
                ? undefined
                : cartItem?.grand_total ?? cartItem?.total_amount ?? cartItem?.total)
            );
            const roomTotal = Number.isFinite(cartRoomTotal) ? cartRoomTotal : selectedRateTotal;
            const roomExtrasTotal = Number.isFinite(cartRoomExtrasTotal)
              ? cartRoomExtrasTotal
              : roomExtrasFallbackTotal;
            const roomGrandTotal = Number.isFinite(cartRoomGrandTotal)
              ? cartRoomGrandTotal
              : (Number(roomTotal || 0) + roomExtrasTotal);

            return (
              <div
                key={draftId}
                className="bookingSummaryContent__room"
                style={{
                  marginTop: idx === 0 ? 0 : "12px",
                  border: isActive ? "1px solid rgba(230,195,106,0.35)" : "1px solid rgba(255,255,255,0.10)",
                  background: isActive ? "rgba(230,195,106,0.05)" : "rgba(255,255,255,0.03)",
                  padding: "12px",
                }}
              >
                <div className="bookingSummaryContent__roomHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "10px" }}>
                  <div style={{ color: "#e6c36a", letterSpacing: "0.16em", fontSize: "0.78rem", fontWeight: 900 }}>
                    {t("stay_details_room").replace("{{n}}", String(idx + 1))} 
                  </div>

                  {/* ✅ Replace Change buttons with a single Remove button once a rate is selected */}
                  {canEdit && hasRateSelected ? (
                    <button
                      className="bookingSummaryContent__removeButton"
                      onClick={() => {
                        //console.log("[BOOKING][SIDEBAR][REMOVE_CLICK]", { reservation_draft_id: draftId });
                        onRemoveRoom?.(Number(draftId));
                      }}
                      disabled={disableRemove || cannotRemove}
                      title={cannotRemove ? t("cannot_delete_last_room") : t("confirm_remove_room")}
                      style={{
                        ...removeBtnStyle,
                        opacity: (disableRemove || cannotRemove) ? 0.55 : 1,
                        cursor: (disableRemove || cannotRemove) ? "not-allowed" : "pointer",
                      }}
                    >
                      {t("room_details_remove")}
                    </button>
                  ) : canEdit && isActive ? (
                    <div style={{ color: "#e6c36a", letterSpacing: "0.14em", fontSize: "0.70rem", fontWeight: 900 }}>
                      {t("active")}
                    </div>
                  ) : null}
                  </div>

                  {/* Dates */}
                  <div
                    className="bookingSummaryContent__datesRow"
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                    }}
                  >
                    <div className="bookingSummaryContent__dates" style={{ color: "#ddd", letterSpacing: "0.06em" }}>
                      {st?.checkIn || "—"} → {st?.checkOut || "—"}
                    </div>
                    {canEdit && isActive && !hasRateSelected && (
                      <button onClick={onChangeDates} style={miniBtnStyle}>
                        {t("room_details_change")}
                      </button>
                    )}
                  </div>

                  <div className="bookingSummaryContent__divider" style={{ marginTop: "12px", height: "1px", background: "rgba(255,255,255,0.06)" }} />

                  {/* Pax */}
                  <div className="bookingSummaryContent__pax" style={{ marginTop: "12px", display: "grid", gap: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                      <div className="bookingSummaryContent__roomText" style={{ color: "#ddd" }}>
                        {t("adults_label")}: <b>{st?.adults ?? "—"}</b>
                      </div>
                      {canEdit && isActive && !hasRateSelected && (
                        <button onClick={onChangeAdults} style={miniBtnStyle}>
                          {t("room_details_change")}
                        </button>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                      <div className="bookingSummaryContent__roomText" style={{ color: "#ddd" }}>
                        {t("children_label")}: <b>{st?.children ?? "—"}</b>
                      </div>
                      {canEdit && isActive && !hasRateSelected && (
                        <button onClick={onChangeChildren} style={miniBtnStyle}>
                          {t("room_details_change")}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Selection (same card) */}
                  <div className="bookingSummaryContent__divider" style={{ marginTop: "12px", height: "1px", background: "rgba(255,255,255,0.06)" }} />

                  <div className="bookingSummaryContent__roomText bookingSummaryContent__selection" style={{ marginTop: "12px", color: "#ddd", lineHeight: 1.45 }}>
                    <div>
                      <span className="bookingSummaryContent__fieldLabel" style={{ color: "#999" }}>{t("category")}</span>{" "}
                      <b>{getRoomCategoryDisplayName(st?.selectedCategory) || "—"}</b>
                    </div>
                    <div style={{ marginTop: "6px" }}>
                      <span className="bookingSummaryContent__fieldLabel" style={{ color: "#999" }}>{t("rate")}</span>{" "}
                      <b>{st?.selectedRate?.name || "—"}</b>
                    </div>

                    <div className="bookingSummaryContent__roomPrice" style={{ marginTop: "12px" }}>
                      <div className="bookingSummaryContent__totalLabel" style={{ color: "#999", letterSpacing: "0.14em", fontSize: "0.72rem", fontWeight: 900 }}>
                        {itemRoomExtras.length > 0 ? t("rooms_subtotal") : t("total")}
                      </div>
                      <div className="bookingSummaryContent__totalValue" style={{ marginTop: "4px", fontSize: "1.8rem", fontWeight: 900 }}>
                        {roomTotal == null ? "—" : formatBookingMoney(roomTotal, summaryCurrency, lang, currencyCatalog)}
                      </div>
                      <div className="bookingSummaryContent__nightly" style={{ marginTop: "4px", color: "#888", fontSize: "0.92rem" }}>
                        {avg == null ? "" : `${formatBookingMoney(avg, summaryCurrency, lang, currencyCatalog)} / ${t("night")}`}
                      </div>
                    </div>

                    {itemRoomExtras.length > 0 ? (
                      <section
                        className="bookingSummaryContent__extras bookingSummaryContent__extras--room"
                        aria-label={`${t("extras_selected_title")} ${idx + 1}`}
                      >
                        <div className="bookingSummaryContent__extrasTitle">
                          {t("extras_selected_title")}
                        </div>
                        <div className="bookingSummaryContent__extrasList">
                          {itemRoomExtras.map((extra) => {
                            const extraId = Number(extra?.extra_id ?? extra?.id);
                            const amount = Number(extra?.total ?? extra?.calculated_amount ?? 0);
                            return (
                              <div
                                key={`${draftId}:${extraId || extra?.name}`}
                                className="bookingSummaryContent__extraLine"
                              >
                                <strong>
                                  {extra?.name || t("extras_fallback_name").replace("{{id}}", String(extraId))}
                                </strong>
                                <strong>
                                  {Number.isFinite(amount) && amount === 0
                                    ? t("extras_included")
                                    : formatBookingMoney(
                                        amount,
                                        extra?.currency || summaryCurrency,
                                        lang,
                                        [extra, ...currencyCatalog]
                                      )}
                                </strong>
                              </div>
                            );
                          })}
                        </div>
                        <div className="bookingSummaryContent__extraSubtotal">
                          <span>{t("extras_subtotal")}</span>
                          <strong>{formatBookingMoney(roomExtrasTotal, summaryCurrency, lang, currencyCatalog)}</strong>
                        </div>
                      </section>
                    ) : null}

                    {itemRoomExtras.length > 0 ? (
                      <div className="bookingSummaryContent__roomSubtotal">
                        <span>{t("extras_room_subtotal")}</span>
                        <strong>{formatBookingMoney(roomGrandTotal, summaryCurrency, lang, currencyCatalog)}</strong>
                      </div>
                    ) : null}
                  </div>

              </div>
            );
          })}

          {reservationExtras.length > 0 ? (
            <section
              className="bookingSummaryContent__extras bookingSummaryContent__extras--reservation"
              aria-labelledby="booking-summary-reservation-extras-title"
            >
              <div
                id="booking-summary-reservation-extras-title"
                className="bookingSummaryContent__extrasTitle"
              >
                {t("extras_reservation_section_title")}
              </div>
              <div className="bookingSummaryContent__extrasList">
                {reservationExtras.map((extra) => {
                  const extraId = Number(extra?.extra_id ?? extra?.id);
                  const amount = Number(extra?.total ?? extra?.calculated_amount ?? 0);
                  return (
                    <div
                      key={`reservation:${extraId || extra?.name}`}
                      className="bookingSummaryContent__extraLine"
                    >
                      <div>
                        <strong>
                          {extra?.name || t("extras_fallback_name").replace("{{id}}", String(extraId))}
                        </strong>
                        <small>{t("extras_once_per_booking")}</small>
                      </div>
                      <strong>
                        {Number.isFinite(amount) && amount === 0
                          ? t("extras_included")
                          : formatBookingMoney(
                              amount,
                              extra?.currency || summaryCurrency,
                              lang,
                              [extra, ...currencyCatalog]
                            )}
                      </strong>
                    </div>
                  );
                })}
              </div>
              <div className="bookingSummaryContent__extraSubtotal">
                <span>{t("extras_reservation_subtotal")}</span>
                <strong>{formatBookingMoney(reservationExtrasTotal, summaryCurrency, lang, currencyCatalog)}</strong>
              </div>
            </section>
          ) : null}

          {hasHotelContact ? (
            <address
              style={{
                margin: "16px 0 0",
                padding: "14px 12px",
                borderTop: "1px solid rgba(255,255,255,0.10)",
                color: "rgba(255,255,255,0.68)",
                fontSize: "0.84rem",
                fontStyle: "normal",
                lineHeight: 1.5,
              }}
            >
              <div style={{ marginBottom: "7px", color: "#e6c36a", fontSize: "0.7rem", fontWeight: 900, letterSpacing: "0.13em" }}>
                {t("hotel_contact")}
              </div>
              {addressLines.map((line, index) => <div key={`${index}-${line}`}>{line}</div>)}
              {hotelPhones.map((phone, index) => (
                <div key={`${index}-${phone}`}>
                  <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} style={{ color: "inherit" }}>{phone}</a>
                </div>
              ))}
              {hotelEmails.map((email, index) => (
                <div key={`${index}-${email}`}>
                  <a href={`mailto:${email}`} style={{ color: "inherit" }}>{email}</a>
                </div>
              ))}
            </address>
          ) : null}

          <div style={{ height: "14px" }} />
        </div>
      </div>
    </div>
  );
}





function TopBarButton({ onClick, children }) {
  return (
    <button
      className="topbarBtn"
      onClick={onClick}
      style={{
        padding: "10px 12px",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#fff",
        cursor: "pointer",
        letterSpacing: "0.08em",
        fontWeight: 600,
      }}
    >
      {children}
    </button>
  );
}




function FakeDateInput({ label, value, onClick, t }) {
  return (
    <div style={{ cursor: "pointer" }}>
      <label
        style={labelStyle}
        onClick={() => {
          //console.log("[BOOKING][CLICK] Date label", label);
          onClick();
        }}
      >
        {label}
      </label>

      <div
        className="fakeInput"
        style={fakeInputStyle}
        onClick={() => {
          //console.log("[BOOKING][CLICK] Date field", label);
          onClick();
        }}
      >
        {value || t("select_date", "Select date")}
      </div>
    </div>
  );
}



function NativeDateInput({ id, label, value, min, onChange, disabled = false, compact = false }) {
  const inputRef = React.useRef(null);

  const openPicker = () => {
    if (disabled) return;
    const el = inputRef.current;
    if (!el) return;

    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
        return;
      }
    } catch {}

    try {
      el.focus?.({ preventScroll: true });
    } catch {}
  };

  return (
    <div>
      <label
        style={labelStyle}
        htmlFor={id}
        onClick={(e) => {
          e.preventDefault();
          openPicker();
        }}
      >
        {label}
      </label>

      <input
        id={id}
        ref={inputRef}
        type="date"
        className="fakeInput nativeDateInput"
        style={{
          ...inputStyle,
          boxSizing: "border-box",
          minWidth: 0,
          maxWidth: "100%",
          padding: compact ? "12px 12px" : inputStyle.padding,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
        value={value || ""}
        min={min || undefined}
        onClick={() => openPicker()}
        onChange={(e) => onChange?.(e.target.value || null)}
        disabled={disabled}
      />
    </div>
  );
}

function NativeSelectInput({ id, label, value, options, onChange, disabled = false }) {
  const opts = Array.isArray(options) ? options : [];
  const v = value == null ? "" : String(value);

  return (
    <div>
      <label style={labelStyle} htmlFor={id}>
        {label}
      </label>

      <select
        id={id}
        className="fakeInput nativeSelectInput"
        style={{
          ...inputStyle,
          boxSizing: "border-box",
          minWidth: 0,
          maxWidth: "100%",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
        value={v}
        onChange={(e) => {
          const next = Number(e.target.value);
          onChange?.(Number.isFinite(next) ? next : null);
        }}
        disabled={disabled}
      >
        {opts.map((o) => (
          <option key={String(o?.value)} value={String(o?.value)}>
            {o?.label ?? String(o?.value ?? "")}
          </option>
        ))}
      </select>
    </div>
  );
}

function FakePaxInput({ label, value, onClick }) {
  return (
    <div style={{ cursor: "pointer" }} onClick={onClick}>
      <label style={labelStyle}>{label}</label>
      <div className="fakeInput" style={fakeInputStyle}>{value}</div>
    </div>
  );
}

function AdultsModal({ value, max, onClose, onConfirm, t }) {
  const tt = typeof t === "function" ? t : (key, fallback) => (fallback ?? key);
  const [v, setV] = useState(value);
  const label = tt("adults_label", "Adults");

  return (
    <PaxModalBase title={tt("adults", "Adults")} onClose={onClose}>
      <Counter
        value={v}
        min={1}
        max={max}
        label={label}
        decreaseLabel={`${tt("counter_decrease", "Decrease")} ${label}`}
        increaseLabel={`${tt("counter_increase", "Increase")} ${label}`}
        onChange={setV}
      />
      <ConfirmButton onClick={() => onConfirm(v)} t={tt} />
    </PaxModalBase>
  );
}

function ChildrenModal({ value, max, onClose, onConfirm, t }) {
  const tt = typeof t === "function" ? t : (key, fallback) => (fallback ?? key);
  const [v, setV] = useState(value);
  const label = tt("children_label", "Children");

  return (
    <PaxModalBase title={tt("children", "Children")} onClose={onClose}>
      <Counter
        value={v}
        min={0}
        max={max}
        label={label}
        decreaseLabel={`${tt("counter_decrease", "Decrease")} ${label}`}
        increaseLabel={`${tt("counter_increase", "Increase")} ${label}`}
        onChange={setV}
      />
      <ConfirmButton onClick={() => onConfirm(v)} t={tt} />
    </PaxModalBase>
  );
}

export function GuestsModal({ adults, children, maxAdults, maxChildren, onClose, onConfirm, t }) {
  const tt = typeof t === "function" ? t : (key, fallback) => (fallback ?? key);
  const [nextAdults, setNextAdults] = useState(adults);
  const [nextChildren, setNextChildren] = useState(children);
  const adultsLabel = tt("adults_label", "Adults");
  const childrenLabel = tt("children_label", "Children");

  return (
    <PaxModalBase title={tt("label_guests", "Guests")} onClose={onClose}>
      <div style={{ display: "grid", gap: "24px" }}>
        <div>
          <div style={{ marginBottom: "10px", color: "#bbb", fontWeight: 800 }}>
            {adultsLabel}
          </div>
          <Counter
            value={nextAdults}
            min={1}
            max={maxAdults}
            label={adultsLabel}
            decreaseLabel={`${tt("counter_decrease", "Decrease")} ${adultsLabel}`}
            increaseLabel={`${tt("counter_increase", "Increase")} ${adultsLabel}`}
            onChange={setNextAdults}
          />
        </div>
        <div>
          <div style={{ marginBottom: "10px", color: "#bbb", fontWeight: 800 }}>
            {childrenLabel}
          </div>
          <Counter
            value={nextChildren}
            min={0}
            max={maxChildren}
            label={childrenLabel}
            decreaseLabel={`${tt("counter_decrease", "Decrease")} ${childrenLabel}`}
            increaseLabel={`${tt("counter_increase", "Increase")} ${childrenLabel}`}
            onChange={setNextChildren}
          />
        </div>
      </div>
      <ConfirmButton
        onClick={() => onConfirm({ adults: nextAdults, children: nextChildren })}
        t={tt}
      />
    </PaxModalBase>
  );
}



function PaxModalBase({ title, children, onClose }) {
  const titleId = React.useId();
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const panel = panelRef.current;
    panel?.focus();

    function handleEscape(event) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onCloseRef.current?.();
    }

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus?.();
      }
    };
  }, []);

  return (
    <div
      className="bookingPortal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        ref={panelRef}
        className="bookingPortal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        autoFocus
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0d0d0d",
          padding: "36px",
          border: "1px solid #333",
          width: "420px",
          textAlign: "center",
        }}
      >
        <h3 id={titleId} className="bookingPortal__title" style={{ marginBottom: "26px", letterSpacing: "0.18em" }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
function Counter({ value, min, max, label, decreaseLabel, increaseLabel, onChange }) {
  const numericValue = Number(value);
  const atMinimum = numericValue <= Number(min);
  const atMaximum = numericValue >= Number(max);

  return (
    <div role="group" aria-label={label} style={{ display: "flex", justifyContent: "center", gap: "22px" }}>
      <button
        type="button"
        aria-label={decreaseLabel}
        disabled={atMinimum}
        onClick={() => onChange(Math.max(min, numericValue - 1))}
      >
        −
      </button>
      <div aria-live="polite" aria-atomic="true" style={{ fontSize: "1.6rem", minWidth: "40px" }}>
        {value}
      </div>
      <button
        type="button"
        aria-label={increaseLabel}
        disabled={atMaximum}
        onClick={() => onChange(Math.min(max, numericValue + 1))}
      >
        +
      </button>
    </div>
  );
}

function ConfirmButton({ onClick, t }) {
  return (
    <button
      className="bookingPrimaryAction"
      type="button"
      onClick={onClick}
      style={{
        marginTop: "28px",
        padding: "14px 30px",
        background: "#e6c36a",
        border: "none",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {t("confirm")}
    </button>
  );
}



function CTAButton({ onClick, text }) {
  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-150%); }
            40% { transform: translateX(250%); }
            100% { transform: translateX(250%); }
          }
        `}
      </style>

      <button
        className="ctaBtn"
        onClick={onClick}
        style={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          padding: "26px",
          background: "linear-gradient(90deg, #ffffff 0%, #f1f1f1 100%)",
          color: "#000",
          border: "none",
          fontSize: "1.3rem",
          fontWeight: 600,
          letterSpacing: "0.22em",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 0,
            left: "-40%",
            width: "40%",
            height: "100%",
            background:
              "linear-gradient(120deg, transparent, rgba(230,195,106,0.6), transparent)",
            animation: "shimmer 3s infinite",
            pointerEvents: "none",
          }}
        />
        <span style={{ position: "relative" }}>
          {text}
        </span>
      </button>
      
    </>
  );
}


function formatLocalYMD(d) {
  try {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
}

function addDaysYMD(ymd, daysToAdd) {
  const s = String(ymd || "").trim();
  if (!s) return "";

  const parts = s.split("-");
  if (parts.length !== 3) return "";

  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !m || !d) return "";

  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + Number(daysToAdd || 0));
  return formatLocalYMD(dt);
}

/* ================== CALENDAR MODAL ================== */

function CalendarModal({ onClose, onSelect, t, lang = "en", minDate = null }) {
  const isMobile =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(max-width: 768px)").matches);

  const [cursor, setCursor] = useState(new Date());
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [nativeStart, setNativeStart] = useState("");
  const [nativeEnd, setNativeEnd] = useState("");

  function isSameDay(a, b) {
    return a && b && a.toDateString() === b.toDateString();
  }

  function inRange(d) {
    return start && end && d > start && d < end;
  }

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function isDisabledDay(d) {
    if (!minDate) return false;
    return startOfDay(d) < startOfDay(minDate);
  }

  function handleSelect(d) {
    if (isDisabledDay(d)) {
      //console.warn("[BOOKING][CALENDAR][DISABLED_DAY_CLICK]", { date: d.toISOString() });
      return;
    }

    if (!start || (start && end)) {
      setStart(d);
      setEnd(null);
    } else if (d < start) {
      setStart(d);
    } else {
      setEnd(d);
    }
  }

  function fmt(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const nativeMin = minDate ? fmt(minDate) : undefined;
  const canConfirmNative = !!nativeStart && !!nativeEnd && nativeEnd >= nativeStart;
  const canConfirm = isMobile ? canConfirmNative : !!start && !!end;

  function renderMonth(baseDate) {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startWeekday = firstDay.getDay() || 7;

    const dows = [
      t("calendar_dow_mon"),
      t("calendar_dow_tue"),
      t("calendar_dow_wed"),
      t("calendar_dow_thu"),
      t("calendar_dow_fri"),
      t("calendar_dow_sat"),
      t("calendar_dow_sun"),
    ];

    const monthLabel = baseDate.toLocaleString(lang === "es" ? "es-ES" : "en-GB", { month: "long" });

    return (
      <div style={{ flex: 1 }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: "12px",
            letterSpacing: "0.08em",
          }}
        >
          {monthLabel} {year}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "6px",
          }}
        >
          {dows.map((d) => (
            <div key={d} style={{ textAlign: "center", color: "#777" }}>
              {d}
            </div>
          ))}

          {Array.from({ length: startWeekday - 1 }).map((_, i) => (
            <div key={`e-${i}`} />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const d = new Date(year, month, i + 1);
            const selected = isSameDay(d, start) || isSameDay(d, end);
            const range = inRange(d);
            const disabled = isDisabledDay(d);

            return (
              <div
                key={i}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                aria-pressed={selected}
                onClick={() => {
                  if (disabled) {
                    //console.warn("[BOOKING][CALENDAR][CLICK_BLOCKED_PAST_DATE]", { date: fmt(d) });
                    return;
                  }
                  handleSelect(d);
                }}
                onKeyDown={(event) => {
                  if (disabled || (event.key !== "Enter" && event.key !== " ")) return;
                  event.preventDefault();
                  handleSelect(d);
                }}
                style={{
                  padding: "10px 0",
                  textAlign: "center",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.35 : 1,
                  background: selected ? "#e6c36a" : range ? "#3a3320" : "#111",
                  color: selected ? "#000" : "#fff",
                }}
              >
                {i + 1}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className="bookingPortal bookingCalendarModal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        className="bookingPortal__panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0d0d0d",
          padding: isMobile ? "18px" : "36px",
          border: "1px solid #333",
          width: isMobile ? "92vw" : "880px",
          maxWidth: isMobile ? "520px" : undefined,
        }}
      >
        {/* Guidance */}
        <div
          className="bookingAccentText"
          style={{
            textAlign: "center",
            marginBottom: "22px",
            fontSize: "1.1rem",
            letterSpacing: "0.12em",
            color: "#e6c36a",
          }}
        >
          {!(isMobile ? nativeStart : start)
            ? t("calendar_select_checkin")
            : !(isMobile ? nativeEnd : end)
            ? t("calendar_select_checkout")
            : t("calendar_confirm_stay")}
        </div>

        {/* Months */}
        {isMobile ? (
          <div style={{ display: "grid", gap: "14px" }}>
            <label style={{ display: "grid", gap: "8px", color: "#ddd", letterSpacing: "0.06em" }}>
              <span>{t("calendar_select_checkin")}</span>
              <input
                type="date"
                value={nativeStart}
                min={nativeMin}
                onChange={(e) => {
                  const v = e.target.value;
                  setNativeStart(v);
                  if (nativeEnd && v && nativeEnd < v) setNativeEnd("");
                }}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  background: "#111",
                  color: "#fff",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  fontSize: "1.05rem",
                }}
              />
            </label>

            <label style={{ display: "grid", gap: "8px", color: "#ddd", letterSpacing: "0.06em" }}>
              <span>{t("calendar_select_checkout")}</span>
              <input
                type="date"
                value={nativeEnd}
                min={nativeStart || nativeMin}
                onChange={(e) => setNativeEnd(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  background: "#111",
                  color: "#fff",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  fontSize: "1.05rem",
                }}
              />
            </label>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              gap: "28px",
            }}
          >
            {renderMonth(cursor)}
            {renderMonth(new Date(cursor.getFullYear(), cursor.getMonth() + 1))}
          </div>
        )}

        {/* Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "center" : "space-between",
            marginTop: "26px",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1))}
            style={{ display: isMobile ? "none" : "inline-block" }}
          >
            ‹ {t("calendar_prev")}
          </button>

          <button
            className="bookingPrimaryAction"
            type="button"
            disabled={!canConfirm}
            onClick={() => {
              if (isMobile) onSelect(nativeStart, nativeEnd);
              else onSelect(fmt(start), fmt(end));
            }}
            style={{
              padding: "14px 30px",
              background: "#e6c36a",
              border: "none",
              fontWeight: 600,
              cursor: canConfirm ? "pointer" : "not-allowed",
              width: isMobile ? "100%" : undefined,
            }}
          >
            {t("calendar_confirm_dates")}
          </button>

          <button
            type="button"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1))}
            style={{ display: isMobile ? "none" : "inline-block" }}
          >
            {t("calendar_next")} ›
          </button>
        </div>
      </div>
    </div>
  );
}


function WizardHeader({ title, subtitle }) {
  const isMobile =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(max-width: 768px)").matches);

  return (
    <div className="wizardHeader" style={{ textAlign: "center", marginBottom: "18px" }}>
      <div
        style={{
          fontSize: isMobile ? "1.25rem" : "1.65rem",
          fontWeight: 600,
          letterSpacing: "0.10em",
          lineHeight: 1.15,
          whiteSpace: isMobile ? "normal" : "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: isMobile ? "-webkit-box" : "block",
          WebkitBoxOrient: isMobile ? "vertical" : undefined,
          WebkitLineClamp: isMobile ? 2 : undefined,
          wordBreak: isMobile ? "break-word" : undefined,
        }}
      >
        {title}
      </div>

      {!!subtitle && (
        <div style={{ marginTop: "6px", color: "rgba(255,255,255,0.65)", fontSize: "0.92rem", lineHeight: 1.25 }}>
          {subtitle}
        </div>
      )}

      <div style={{ margin: "12px auto 0", height: "1px", width: "180px", background: "linear-gradient(90deg, transparent, rgba(230,195,106,0.65), transparent)" }} />
    </div>
  );
}

function GuestFieldLabel({ children, htmlFor, required = false, requiredLabel = "" }) {
  return (
    <label className="bookingGuestForm__label" htmlFor={htmlFor}>
      {children}
      {required ? (
        <>
          <span className="bookingGuestForm__requiredMark" aria-hidden="true">*</span>
          <span className="bookingSrOnly"> {requiredLabel}</span>
        </>
      ) : null}
    </label>
  );
}


function safeImgUrl(u) {
  const s = String(u || "").trim();
  if (!s) return null;

  try {
    const url = new URL(s, window.location.origin);
    const host = url.hostname.toLowerCase();

    const isDev = String(process.env.NODE_ENV || "").toLowerCase() === "development";
    const isLocalHost = host === "localhost" || host === "127.0.0.1";

    const protocolOk =
      url.protocol === "https:" || (isDev && isLocalHost && url.protocol === "http:");

    if (!protocolOk) return null;

    const allowedHosts = [
      "d1o2vq6zua44w3.cloudfront.net",
      window.location.hostname.toLowerCase(),
      ...(isDev ? ["localhost", "127.0.0.1"] : []),
    ];

    if (!allowedHosts.includes(host)) return null;

    return url.toString();
  } catch {
    return null;
  }
}

export function RoomCategoryCard({
    c,
    beCopy = null,
    rates = [],
    currencyCatalog = [],
    ratesLoading = false,
    ratesError = null,
    selectingRateId = null,
    selectionDisabled = false,
    onSelectRate,
    onSetSelectionQuantity,
    onViewRateConditions,
    coverUrl = null,
    galleryUrls = [],
    amenities = [],          // ✅ recibe amenities por props
    lang = "en",             // ✅ recibe lang por props
  t = (key) => key,
}) {

    const safeCoverUrl = safeImgUrl(coverUrl);
    const safeGalleryUrls = (Array.isArray(galleryUrls) ? galleryUrls : [])
      .map((u) => safeImgUrl(u))
      .filter(Boolean);

    const primaryImg = safeCoverUrl || safeGalleryUrls[0] || null;
    const hasPhotos = !!primaryImg;

  const [isOpen, setIsOpen] = React.useState(false);
  const [contentReady, setContentReady] = React.useState(false);
  const [idx, setIdx] = React.useState(0);
  const [cardImageIndex, setCardImageIndex] = React.useState(0);
  const cardSwipeStartRef = React.useRef(null);
  const suppressGalleryClickRef = React.useRef(false);
  const isMobile =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(pointer: coarse)").matches ||
      window.matchMedia("(max-width: 768px)").matches);

  const images = React.useMemo(() => {
    const out = [];
    if (safeCoverUrl) out.push(safeCoverUrl);

    const seen = new Set(out);
    for (const u of safeGalleryUrls) {
      if (!u) continue;
      if (seen.has(u)) continue;
      seen.add(u);
      out.push(u);
    }
    return out;
  }, [safeCoverUrl, safeGalleryUrls]);

  React.useEffect(() => {
    setCardImageIndex((current) => {
      if (images.length <= 0) return 0;
      return Math.min(Math.max(0, Number(current) || 0), images.length - 1);
    });
  }, [images.length]);

  const cardImage = images[cardImageIndex] || primaryImg;
  const visibleCardDots = React.useMemo(() => {
    const total = images.length;
    if (total <= 1) return [];
    if (total <= 5) return Array.from({ length: total }, (_, index) => index);
    const start = Math.min(Math.max(cardImageIndex - 2, 0), total - 5);
    return Array.from({ length: 5 }, (_, offset) => start + offset);
  }, [cardImageIndex, images.length]);

  function stepCardImage(delta) {
    if (images.length <= 1) return;
    setCardImageIndex((current) => {
      const total = images.length;
      return ((Number(current) || 0) + delta + total) % total;
    });
  }

  function handleCardPointerDown(event) {
    if (event.pointerType !== "touch" && event.pointerType !== "pen") return;
    cardSwipeStartRef.current = { x: event.clientX, y: event.clientY };
  }

  function handleCardPointerUp(event) {
    const start = cardSwipeStartRef.current;
    cardSwipeStartRef.current = null;
    if (!start || (event.pointerType !== "touch" && event.pointerType !== "pen")) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.25) return;

    suppressGalleryClickRef.current = true;
    stepCardImage(dx < 0 ? 1 : -1);
  }

  function handleCardPhotoClick() {
    if (suppressGalleryClickRef.current) {
      suppressGalleryClickRef.current = false;
      return;
    }
    openAt(cardImageIndex);
  }

  function handleCardPhotoKeyDown(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    stepCardImage(event.key === "ArrowLeft" ? -1 : 1);
  }


  React.useEffect(() => {
    if (!isOpen) return;
    setContentReady(false);
    setContentReady(true);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;

    function onKeyDown(e) {
      if (e.key === "Escape") setIsOpen(false);
      if (e.key === "ArrowLeft") setIdx((v) => Math.max(0, (Number(v) || 0) - 1));
      if (e.key === "ArrowRight") setIdx((v) => Math.min(images.length - 1, (Number(v) || 0) + 1));
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, images.length]);

  function openAt(startIndex) {
    const safe = Math.max(0, Math.min(Number(startIndex) || 0, images.length - 1));
    setIdx(safe);
    setIsOpen(true);
  }

    const be = beCopy && typeof beCopy === "object" ? beCopy : null;

    const displayName =
      (lang === "es" ? String(be?.name_es || "").trim() : String(be?.name_en || "").trim()) ||
      String(c?.name || "").trim() ||
      "Room category";

    const formatPhotoCopy = (key, imageIndex = cardImageIndex) =>
      String(t(key) || key)
        .replace("{{room}}", displayName)
        .replace("{{current}}", String(imageIndex + 1))
        .replace("{{total}}", String(images.length));

    const shortDescription =
      (lang === "es" ? String(be?.short_description_es || "").trim() : String(be?.short_description_en || "").trim()) ||
      String(c?.description || "").trim() ||
      "";

    const bedType =
      (lang === "es" ? String(be?.bed_type_es || "").trim() : String(be?.bed_type_en || "").trim()) ||
      "";

    const specsParts = [];
    const m2n = Number(be?.size_m2);
    if (Number.isFinite(m2n) && m2n > 0) specsParts.push(`${Math.trunc(m2n)} m²`);

    const bedsN = Number(be?.beds_count);
    if (Number.isFinite(bedsN) && bedsN > 0) {
      specsParts.push(`${Math.trunc(bedsN)} ${lang === "es" ? "camas" : "beds"}`);
    }

    if (bedType) specsParts.push(bedType);

    const paxN = Number(be?.max_pax);
    if (Number.isFinite(paxN) && paxN > 0) specsParts.push(`${Math.trunc(paxN)} pax`);

    const safeRates = Array.isArray(rates) ? rates : [];
    const categoryUnavailable = c?.is_available === false || Number(c?.available_to_add) === 0;
    const categoryHasSelection = safeRates.some((rate) => Number(rate?.selected_quantity || 0) > 0);
    const unavailableReason = String(
      c?.unavailable_reason || safeRates.find((rate) => rate?.unavailable_reason)?.unavailable_reason || ""
    ).trim().toUpperCase();
    const availabilityMessageKey =
      unavailableReason === "CART_LIMIT_REACHED"
        ? "inline_rate_cart_limit"
        : unavailableReason === "AVAILABILITY_MISSING"
          ? "inline_rate_availability_missing"
          : categoryHasSelection && Number(c?.available_to_add) === 0
            ? "inline_rate_last_room_added"
            : "inline_rate_sold_out";

    return (
      <>
        <article
          className={`roomCard${categoryUnavailable ? " roomCard--unavailable" : ""}`}
          aria-labelledby={`room-category-title-${c?.id ?? "unknown"}`}
        >
          <div className="roomCard__mediaPane">
            <div
              className="roomCard__photoStage"
              role={hasPhotos ? "group" : undefined}
              aria-roledescription={hasPhotos ? "carousel" : undefined}
              aria-label={hasPhotos ? displayName : undefined}
            >
              {hasPhotos ? (
                <>
                  <button
                    className="roomCover roomCard__media roomCard__photoButton"
                    type="button"
                    aria-label={formatPhotoCopy("room_photo_open_gallery")}
                    onClick={handleCardPhotoClick}
                    onKeyDown={handleCardPhotoKeyDown}
                    onPointerDown={handleCardPointerDown}
                    onPointerUp={handleCardPointerUp}
                    onPointerCancel={() => {
                      cardSwipeStartRef.current = null;
                    }}
                  >
                    <img
                      className="roomCard__image"
                      src={cardImage}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>

                  {images.length > 1 ? (
                    <>
                      <button
                        className="roomCard__carouselArrow roomCard__carouselArrow--prev"
                        type="button"
                        aria-label={t("room_photo_previous")}
                        onClick={() => stepCardImage(-1)}
                      >
                        ‹
                      </button>
                      <button
                        className="roomCard__carouselArrow roomCard__carouselArrow--next"
                        type="button"
                        aria-label={t("room_photo_next")}
                        onClick={() => stepCardImage(1)}
                      >
                        ›
                      </button>

                      <div className="roomCard__carouselDots" aria-label={formatPhotoCopy("room_photo_go_to")}>
                        {visibleCardDots.map((imageIndex) => (
                          <button
                            className={`roomCard__carouselDot${imageIndex === cardImageIndex ? " roomCard__carouselDot--active" : ""}`}
                            type="button"
                            key={imageIndex}
                            aria-label={formatPhotoCopy("room_photo_go_to", imageIndex)}
                            aria-current={imageIndex === cardImageIndex ? "true" : undefined}
                            onClick={() => setCardImageIndex(imageIndex)}
                          />
                        ))}
                      </div>
                      <span className="bookingSrOnly" aria-live="polite" aria-atomic="true">
                        {formatPhotoCopy("room_photo_go_to")}
                      </span>
                    </>
                  ) : null}
                </>
              ) : (
                <div className="roomCover roomCard__media roomCard__media--empty">
                  <span className="roomCard__mediaPlaceholder">{t("room_details")}</span>
                </div>
              )}
            </div>

            <RoomCategoryDetails beCopy={beCopy} amenities={amenities} lang={lang} roomName={displayName} t={t} />
          </div>

          <div className="roomCard__body">
            <header>
              <h2
                id={`room-category-title-${c?.id ?? "unknown"}`}
                className="roomCard__title"
              >
                {displayName}
              </h2>

              {specsParts.length > 0 ? (
                <div className="roomCard__facts" aria-label={specsParts.join(", ")}>
                  {specsParts.map((spec, specIndex) => (
                    <span className="roomCard__fact" key={`${spec}-${specIndex}`}>
                      {spec}
                    </span>
                  ))}
                </div>
              ) : null}
            </header>

            {shortDescription ? (
              <p className="roomCard__description">{shortDescription}</p>
            ) : null}

            <section
              className="roomCard__rates"
              aria-labelledby={`room-category-rates-${c?.id ?? "unknown"}`}
              aria-busy={selectingRateId != null}
            >
              <div className="roomCard__ratesHeader">
                <h3 id={`room-category-rates-${c?.id ?? "unknown"}`}>
                  {t("inline_rates_title")}
                </h3>
              </div>

              {ratesLoading ? (
                <div className="roomCard__ratesLoading">{t("rate_select_loading")}</div>
              ) : safeRates.length > 0 ? (
                <>
                  <div className="roomCard__rateList" role="list">
                    {safeRates.map((rate) => {
                      const rateId = Number(rate?.id);
                      const rateName = String(rate?.name || `Rate ${rateId}`);
                      const selectionKey = String(rate?.selection_key || "");
                      const isSelecting = selectionKey
                        ? String(selectingRateId || "") === selectionKey
                        : Number(selectingRateId) === rateId;
                      const quantity = Math.max(0, Math.trunc(Number(rate?.selected_quantity) || 0));
                      const isSelected = quantity > 0;
                      const canIncrement = rate?.can_increment !== false && rate?.is_available !== false && !categoryUnavailable;
                      const currency = rate?.currency || c?.currency || "EUR";
                      const total = formatBookingMoney(
                        rate?.min_total,
                        currency,
                        lang,
                        [rate, c, ...currencyCatalog]
                      ) || "—";
                      const selectLabel = `${t("inline_rate_select")}: ${rateName}, ${displayName}, ${total}`;
                      const conditionsLabel = `${t("inline_rate_view_conditions")}: ${rateName}`;

                      return (
                        <div
                          className={`roomCard__rateOption${isSelected ? " roomCard__rateOption--selected" : ""}${!canIncrement ? " inlineRateRow--unavailable" : ""}`}
                          key={rateId}
                          role="listitem"
                        >
                          {quantity === 0 ? (
                            <button
                              className="roomCard__rateSelect"
                              type="button"
                              aria-label={selectLabel}
                              aria-busy={isSelecting}
                              disabled={selectionDisabled || !canIncrement}
                              onClick={() => onSelectRate?.(rate)}
                            >
                              <span className="roomCard__rateMarker" aria-hidden="true" />
                            </button>
                          ) : (
                            <div
                              className="bookingRateQuantity"
                              role="group"
                              aria-label={`${t("inline_rate_quantity")}: ${rateName}`}
                            >
                              <button
                                className="bookingRateQuantity__button"
                                type="button"
                                aria-label={`${t("inline_rate_remove")}: ${rateName}`}
                                disabled={selectionDisabled}
                                onClick={() => onSetSelectionQuantity?.(rate, quantity - 1)}
                              >
                                −
                              </button>
                              <span className="bookingRateQuantity__value" aria-live="polite">{quantity}</span>
                              <button
                                className="bookingRateQuantity__button"
                                type="button"
                                aria-label={`${t("inline_rate_add")}: ${rateName}`}
                                disabled={selectionDisabled || !canIncrement}
                                onClick={() => onSetSelectionQuantity?.(rate, quantity + 1)}
                              >
                                +
                              </button>
                            </div>
                          )}

                          <span className="roomCard__rateName" title={rateName}>{rateName}</span>

                          <strong
                            className="roomCard__ratePrice"
                            aria-label={`${t("inline_rate_total_stay")}: ${total}`}
                          >
                            {total}
                          </strong>

                          <button
                            className="roomCard__rateConditionsButton"
                            type="button"
                            aria-label={conditionsLabel}
                            aria-describedby={`room-category-rate-conditions-hint-${c?.id ?? "unknown"}`}
                            aria-haspopup="dialog"
                            aria-controls="booking-rate-conditions-dialog"
                            onClick={() => onViewRateConditions?.(rate)}
                          >
                            <span className="roomCard__rateConditionsIcon" aria-hidden="true">i</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <p
                    id={`room-category-rate-conditions-hint-${c?.id ?? "unknown"}`}
                    className="roomCard__rateConditionsHint"
                  >
                    <span className="roomCard__rateConditionsHintIcon" aria-hidden="true">i</span>
                    <span>{t("inline_rate_conditions_hint")}</span>
                  </p>
                </>
              ) : (
                <div className="roomCard__ratesLoading">{t("rate_select_no_rates")}</div>
              )}

              {categoryUnavailable ? (
                <div className="roomCard__availabilityMessage" role="status">
                  {t(availabilityMessageKey)}
                </div>
              ) : null}

              {ratesError ? (
                <div className="roomCard__ratesError" role="alert">{ratesError}</div>
              ) : null}
            </section>
          </div>
        </article>

      {/* Photos modal */}
      {isOpen && contentReady
        ? createPortal(
            <div
              className="inv-popup-overlay"
              onMouseDown={() => setIsOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 2147483647,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                className="inv-popup-container"
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  width: "min(980px, 94vw)",
                  height: "min(760px, 92vh)",
                  maxHeight: "92dvh",
                  padding: "18px",
                  paddingBottom: "calc(18px + env(safe-area-inset-bottom))",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ position: "relative", marginBottom: "12px" }}>
                  <div
                    style={{
                      fontWeight: 900,
                      letterSpacing: "0.12em",
                      color: "#e6c36a",
                      textTransform: "uppercase",
                      paddingRight: "44px",
                    }}
                  >
                    {displayName}
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close"
                    style={{
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      width: "36px",
                      height: "36px",
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: "pointer",
                      lineHeight: "1",
                    }}
                  >
                    ×
                  </button>
                </div>

                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    flex: 1,
                    minHeight: isMobile ? 0 : "360px",
                    borderRadius: "12px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "#000",
                    overflow: "hidden",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: images[idx] ? `url(${images[idx]})` : "none",
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  />

                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={() => {
                      setIdx((v) => {
                        const n = images.length || 0;
                        if (n <= 1) return 0;
                        const cur = Number(v) || 0;
                        return (cur - 1 + n) % n;
                      });
                    }}
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "44px",
                      height: "44px",
                      borderRadius: "999px",
                      background: "rgba(0,0,0,0.38)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      color: "#fff",
                      cursor: "pointer",
                      opacity: images.length > 1 ? 1 : 0.25,
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                    }}
                  >
                    ‹
                  </button>

                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={() => {
                      setIdx((v) => {
                        const n = images.length || 0;
                        if (n <= 1) return 0;
                        const cur = Number(v) || 0;
                        return (cur + 1) % n;
                      });
                    }}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "44px",
                      height: "44px",
                      borderRadius: "999px",
                      background: "rgba(0,0,0,0.38)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      color: "#fff",
                      cursor: "pointer",
                      opacity: images.length > 1 ? 1 : 0.25,
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                    }}
                  >
                    ›
                  </button>
                </div>

                {images.length > 0 ? (
                  <div
                    style={{
                      height: isMobile ? "74px" : "110px",
                      overflowX: isMobile ? "auto" : "hidden",
                      overflowY: isMobile ? "hidden" : "auto",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: isMobile ? "nowrap" : "wrap",
                        gap: "8px",
                        paddingBottom: "4px",
                      }}
                    >
                      {images.map((u, i) => (
                        <div
                          key={`${u}-${i}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setIdx(i)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") setIdx(i);
                          }}
                          style={{
                            width: isMobile ? "72px" : "84px",
                            height: isMobile ? "48px" : "56px",
                            borderRadius: "8px",
                            border:
                              i === idx
                                ? "2px solid rgba(230,195,106,0.9)"
                                : "1px solid rgba(255,255,255,0.14)",
                            backgroundImage: `url(${u})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            cursor: "pointer",
                            flex: "0 0 auto",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}


    </>
  );
}


/* ================== STYLES ================== */

const labelStyle = {
  display: "block",
  marginBottom: "12px",
  fontSize: "0.95rem",
  letterSpacing: "0.22em",
  color: "#ccc",
  fontWeight: 600,
  textTransform: "uppercase",
};

const fakeInputStyle = {
  padding: "20px 18px",
  backgroundColor: "#000",
  border: "1px solid #333",
  fontSize: "1.25rem",
};

const inputStyle = {
  width: "100%",
  padding: "20px 18px",
  backgroundColor: "#000",
  border: "1px solid #333",
  color: "#fff",
  fontSize: "1.25rem",
};

function CenteredMessage({ children }) {
  return (
    <div
      className="bookingPage bookingCenteredMessage"
      role="alert"
      style={{
        minHeight: "100vh",
        backgroundColor: "#000",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.2rem",
      }}
    >
      {children}
    </div>
  );
}

function CenteredLoadingMessage({ children }) {
  return (
    <div className="bookingPage bookingEntryLoading" role="status" aria-live="polite">
      <span className="bookingEntryLoading__spinner" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function PaymentSetupForm({
  apiUrl,
  reservation_group_id,
  business_unit_id,
  public_session_token,
  client_secret,       // ✅ NEW
  stripe_account_id,   // ✅ NEW
  billingDetails,
  booking_engine_language,
  currency_code,
  fx_rate_set_id,
  bookingTheme,
  t,
  onBack,
  onDone,
}) {


  const tSafe = typeof t === "function" ? t : ((k) => k);

  const stripe = useStripe();
  const elements = useElements();

  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const stripeThemeTokens = readBookingThemeCssTokens();

  // ✅ NEW: Cardholder name (mandatory)
    const [cardholderName] = useState(() => {
    const n = String(billingDetails?.name || "").trim();
    return n || "";
  });


  useEffect(() => {
    // no-op: cardholder name is locked to booker name
  }, [billingDetails?.name]);

  async function handleSaveCard() {
    setErrMsg(null);

    if (!reservation_group_id) {
      setErrMsg(t("payment_setup_missing_group_id"));
      return;
    }
    if (!stripe || !elements) {
      setErrMsg(t("payment_setup_stripe_not_ready"));
      return;
    }

    const name = String(cardholderName || "").trim();
    if (!name) {
      setErrMsg(t("payment_setup_enter_name"));
      return;
    }

    const cardEl = elements.getElement(CardElement);
    if (!cardEl) {
      setErrMsg(t("payment_setup_card_element_not_found"));
      return;
    }

    try {
      setSaving(true);

      const token = String(public_session_token || "").trim();
      if (!token) {
        //console.error("[BOOKING][PAYMENT][ABORT] Missing public_session_token");
        setErrMsg(t("payment_setup_commit_failed"));
        return;
      }

      //console.log("[BOOKING][PAYMENT][SETUP_INTENT][CREATE][START]", {
        //reservation_group_id,
        //business_unit_id,
        //has_token: true,
        //token_length: token.length,
      //});

      const cs = String(client_secret || "").trim() || null;
      const acct = String(stripe_account_id || "").trim() || null;

      if (!acct) {
        //console.error("[BOOKING][PAYMENT][BOOTSTRAP][ERROR] Missing stripe_account_id (prop)", {
          //reservation_group_id,
          //business_unit_id,
        //});
        setErrMsg("Missing stripe connected account id");
        return;
      }

      if (!cs) {
        //console.error("[BOOKING][PAYMENT][BOOTSTRAP][ERROR] Missing client_secret (prop)", {
          //reservation_group_id,
          //business_unit_id,
          //stripe_account_id: acct,
        //});
        setErrMsg(t("payment_setup_missing_client_secret"));
        return;
      }

      //console.log("[BOOKING][PAYMENT][CONFIRM][START]", { stripe_account_id: acct });


      //console.log("[BOOKING][PAYMENT][CONFIRM][START]");

      const result = await stripe.confirmCardSetup(cs, {
        payment_method: {
          card: cardEl,
          billing_details: {
            // ✅ use our mandatory field
            name: name,
            email: billingDetails?.email || undefined,
            phone: billingDetails?.phone || undefined,
          },
        },
      });

      if (result?.error) {
        //console.error("[BOOKING][PAYMENT][CONFIRM][ERROR]", result.error);
        setErrMsg(result.error.message || "Card confirmation failed");
        return;
      }

      const setupIntent = result?.setupIntent || null;

      const confirmPaymentUrl = `${apiUrl}/api/public/stripe/payment-intents/confirm`;
      const confirmPayload = {
        reservation_group_draft_id: Number(reservation_group_id),
        setup_intent_id: setupIntent?.id || null,
      };

      const postConfirmPayment = async () => {
        try {
          const resp = await axios.post(
            confirmPaymentUrl,
            confirmPayload,
            { headers: { "x-public-session-token": token } }
          );
          return resp?.data || null;
        } catch (err) {
          return err?.response?.data || null;
        }
      };

      let paymentConfirmData = await postConfirmPayment();

      if (
        paymentConfirmData?.code === "SCA_REQUIRED" &&
        paymentConfirmData?.client_secret
      ) {
        const scaResult = await stripe.confirmCardPayment(
          String(paymentConfirmData.client_secret)
        );
        if (scaResult?.error) {
          setErrMsg(scaResult.error.message || t("payment_setup_commit_failed"));
          return;
        }
        paymentConfirmData = await postConfirmPayment();
      }

      if (!paymentConfirmData || paymentConfirmData?.ok !== true) {
        setErrMsg(paymentConfirmData?.error || t("payment_setup_commit_failed"));
        return;
      }
      //console.log("[BOOKING][PAYMENT][CONFIRM][OK]", {
        //setup_intent_id: setupIntent?.id,
        //status: setupIntent?.status,
        //payment_method: setupIntent?.payment_method,
      //});

      const confirmation = await commitBookingDraftGroup({
        apiUrl,
        groupId: reservation_group_id,
        publicSessionToken: token,
        language: booking_engine_language,
        currencyCode: currency_code,
        fxRateSetId: fx_rate_set_id,
        updatedBy: "PublicBookingPaymentCommit",
      });

      onDone?.({
        setup_intent_id: setupIntent?.id,
        status: setupIntent?.status,
        payment_method: setupIntent?.payment_method,
        ...confirmation,
      });


    } catch (e) {
      //console.error("[BOOKING][PAYMENT][ERROR]", {
        //message: e?.message,
        //status: e?.response?.status,
        //data: e?.response?.data,
      //});
      setErrMsg(e?.response?.data?.error || t("payment_setup_commit_failed"));
    } finally {
      setSaving(false);
    }
  }

  const inputStyleLocal = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.25)",
    color: "#fff",
    outline: "none",
    fontSize: "16px",
  };

  const labelStyleLocal = {
    display: "block",
    marginTop: "12px",
    marginBottom: "8px",
    fontSize: "0.82rem",
    letterSpacing: "0.16em",
    color: "rgba(255,255,255,0.75)",
    fontWeight: 900,
  };

  return (
    <div className="bookingPaymentForm" data-booking-theme={bookingTheme} style={{ padding: "10px" }}>
      <div className="bookingAccentText" style={{ color: "#e6c36a", letterSpacing: "0.12em", fontWeight: 900 }}>
        {t("payment_details_title", "PAYMENT DETAILS")}
      </div>

      {/* ✅ NEW: Cardholder name */}
      <label style={labelStyleLocal}>
        {t("cardholder_name", "CARDHOLDER NAME")}{" "}
        <span style={{ color: "#e6c36a" }}>
          ({t("mandatory", "MANDATORY")})
        </span>
      </label>

      <input
        style={{
          ...inputStyleLocal,
          opacity: 0.85,
          cursor: "not-allowed",
        }}
        value={cardholderName}
        disabled={true}
        readOnly={true}
        placeholder={t("name_on_card_placeholder", "Name on card")}
        autoComplete="cc-name"
      />

      <label style={labelStyleLocal}>
        {t("card_details", "CARD DETAILS")}
      </label>

      <div
        className="bookingStripeElement"
        style={{
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(0,0,0,0.25)",
          padding: "12px",
        }}
      >
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                color: stripeThemeTokens.text,
                fontSize: "16px",
                fontSmoothing: "antialiased",
                fontFamily: stripeThemeTokens.font,
                "::placeholder": { color: stripeThemeTokens.muted },
              },
              invalid: { color: stripeThemeTokens.error },
            },
          }}
        />
      </div>

      {/* ✅ calming helper text (no scary link) */}
      <div
        style={{
          marginTop: "10px",
          color: "rgba(255,255,255,0.55)",
          fontSize: "0.85rem",
          lineHeight: 1.45,
        }}
      >
        {t(
          "card_secure_helper",
          "Your card will be stored securely. Charges or pre-authorizations may be applied according to the booking policies."
        )}
      </div>

      {!!errMsg && (
        <div className="bookingError" role="alert" style={{ marginTop: "12px", color: "#ffb3b3" }}>
          {errMsg}
        </div>
      )}

      <div style={{ display: "flex", gap: "12px", marginTop: "14px" }}>
        <button
          className="bookingSecondaryAction"
          type="button"
          onClick={onBack}
          disabled={saving}
          style={{
            flex: 1,
            padding: "12px",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#fff",
            fontWeight: 900,
            letterSpacing: "0.14em",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
          }}
        >
          {t("back", "BACK")}
        </button>

        <button
          className="bookingPrimaryAction"
          type="button"
          onClick={handleSaveCard}
          disabled={saving || !stripe || !elements}
          style={{
            flex: 1,
            padding: "12px",
            background: saving
              ? "rgba(255,255,255,0.10)"
              : "linear-gradient(90deg, #caa24b, #e6c36a)",
            border: "none",
            color: saving ? "#bbb" : "#111",
            fontWeight: 900,
            letterSpacing: "0.14em",
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving
            ? t("processing", "PROCESSING...")
            : t("pay_and_confirm_reservation", "CONFIRM WITH CARD GUARANTEE")}
        </button>
      </div>
    </div>
  );

}
